import _ from 'lodash';
import { v4 as uuidv4 } from 'uuid';
import { makeAutoObservable, observable, runInAction, when } from 'mobx';
import { FirstMenu, MenuDish, NewMenuPrice, Restaurant, SecondMenu } from '../../models';
import type { MenuStoreListResponse, MenuStoreMenuListItem } from '../../models/menu-store.entity';
import { setEmptyNewPriceValue } from '../../constants/utils';
import apiManage, { apiManage_rbp } from '../../request';
import type { PatchDishesParams } from '../../request/api/rbp/menu';
import cloudDishLibraryApi, {
    buildCloudDishLibraryBatchNotLimitBody,
    buildCloudDishLibraryDishesSortBody,
    buildCloudMenuItemSluIndexUpdateBody,
    mapCloudMenuListToPickerSkeleton,
    mergeCloudMenuDetailIntoPickerSkeleton,
    pickCloudDishIdFromCreateResult,
    pickCloudImageUploadRef,
    pickFirstAddedCloudSluId,
    type CloudDishLibraryBatchUpdateDishBody,
    type CloudDishLibraryCreateDishBody,
    type CloudDishLibraryDishesSortBody,
    type CloudDishLibraryUpdateDishBody,
    type CloudDishLibraryUpdateMenuSluUpdateItem,
} from '../../request/api/rbp/cloud-dish-library';
import GlobalNotification from '../../component/common/snackbar';
import i18n from '../../i18n';
import setLoading from '../../component/linear-determinate';
import { accountState } from '../my-account/account-state';
import { parseMenuStoreListResponse } from '../menu-store/menu-store.helpers';
import {
    applyMainGroupNameInput,
    DEFAULT_MAIN_GROUP_NAME_I18N_JSON,
    getMainGroupNameForDisplay,
    normalizeMainGroupNameI18nJson,
    type MenuLibraryDisplayLang,
} from '../../utils/menu-main-group-name-i18n';
import moment from 'moment';
import {
    buildRestaurantStaticImageDisplayUrl,
    buildRestaurantStaticUploadQueryFileName,
    extractFileNameForRestaurantStaticDelete,
} from '../../utils/restaurant-static-image-url';
import {
    getRestaurantVersionCheckBlockI18n,
    mapErrorToI18nKey,
    type RestaurantVersionCheckBlockI18nPayload,
} from '../../constants/tool-tunnel-config';
import {
    formatCloudMenuCardSubtitle,
    normalizeCloudMenuListResponse,
    normalizeCloudUploadResult,
    normalizeCloudDeleteResult,
    pickAddDishesResultCounts,
    sumSelectedCloudDishCount,
    extractCloudDetailCategories,
    extractCloudDetailDishes,
    type CloudDishLibraryCreatedCourseDish,
    type CloudDishLibraryMenuSummary,
} from '../../utils/cloud-dish-library';

/** 菜单库展示语言（释义见 `utils/menu-main-group-name-i18n.ts` 与文档 `menu-library-management.md` 第 9 节） */
export type { MenuLibraryDisplayLang };

/** 菜品筛选 — 快速定位 chip（Figma 筛选弹窗） */
export type DishFilterQuickPreset =
    | 'all'
    | 'combo'
    | 'seasonal'
    | 'weight'
    | 'new'
    | 'signature'
    | 'no_discount';

/** 新建弹窗子分类详情菜品行 key（勾选、删除与列表索引一致） */
export function createMenuSubcategoryDishRowKey(d: MenuDish, index: number): string {
    return d.item_id != null ? `id:${d.item_id}` : `idx:${index}`;
}

/**
 * 门店设置 id=22 菜品排序默认值（与 RBP2 `getSortKey` / `item_name1 ASC` 一致）
 */
export const MENU_LIBRARY_DEFAULT_DISH_SORT = 'item_name1 ASC';

/**
 * 门店设置 id=22「按优先级手动排序」取值（与 RBP2 `slu_priority DESC` 拖拽门禁一致）
 */
export const MENU_LIBRARY_DISH_SORT_BY_PRIORITY = 'slu_priority DESC';

/**
 * 按当前展示顺序生成本地 `PATCH /v3/menus/first/index` 的 body。
 * 与 RBP2 `handleSortChange` 一致：`slu_priority = length - index`（展示首行 priority 最大，配合 `slu_priority DESC`）。
 * @param items - 当前子类 `menu_item`（展示顺序）
 * @returns `{ item_id, slu_priority }[]`；无已落库行时返回 null
 */
export function buildDetailDishSluPriorityIndexPayload(
    items: MenuDish[] | undefined,
): Array<{ item_id: number; slu_priority: number }> | null {
    if (!items?.length) {
        return null;
    }
    /** 展示列表长度，用于 `length - index` */
    const length = items.length;
    const out: Array<{ item_id: number; slu_priority: number }> = [];
    for (let index = 0; index < length; index += 1) {
        const id = items[index].item_id;
        if (id != null && Number.isFinite(Number(id))) {
            out.push({ item_id: id as number, slu_priority: length - index });
        }
    }
    return out.length ? out : null;
}

/**
 * 解析 setting 22 的菜品排序字符串。
 * @param res - `getSettingById(22)` 返回体
 * @returns 非空 `value`；否则默认 `item_name1 ASC`
 */
export function parseDishSortSetting22(res: unknown): string {
    const v: unknown = _.get(res as object, '[0].value');
    if (typeof v === 'string' && v.trim() !== '') {
        return v.trim();
    }
    return MENU_LIBRARY_DEFAULT_DISH_SORT;
}

/**
 * 解析 setting 103 的子类别优先级排序开关。
 * @param res - `getSettingById(103)` 返回体
 * @returns 非零有限数字、字符串 `1` 或布尔 `true` 时返回开启
 */
export function parseMenuPrioritySortSetting103(res: unknown): boolean {
    const value: unknown = _.get(res as object, '[0].value');
    return (typeof value === 'number' && Number.isFinite(value) && value !== 0)
        || value === '1'
        || value === true;
}

/**
 * 菜单库详情菜品表行 key：本地用 `item_id`；云端用云端菜品 `id`（`item_id` 可能均为 0 导致重复 key）。
 * @param dish - 菜品行
 * @param index - 列表下标（兜底 key）
 * @param isCloud - 是否云端菜单详情
 */
export function getMenuLibraryDetailDishRowKey(dish: MenuDish, index: number, isCloud: boolean): string | number {
    if (isCloud) {
        const cloudId = getCloudDishIdFromMenuDish(dish);
        if (cloudId != null) {
            return cloudId;
        }
        return `cloud-dish-idx:${index}`;
    }
    return dish.item_id != null ? dish.item_id : `dish-${index}`;
}

/**
 * 从菜品行读取云端菜品 string id（详情扩展字段 `id`）。
 * @param dish - 菜品行
 * @returns 非空 id；否则 null
 */
export function getCloudDishIdFromMenuDish(dish: MenuDish | null | undefined): string | null {
    if (dish == null) {
        return null;
    }
    const cloudId = (dish as { id?: string | number }).id;
    if (cloudId == null || String(cloudId).trim() === '') {
        return null;
    }
    return String(cloudId);
}

/** 筛选预览「菜品 ID」列展示结果 */
export type MenuLibraryFilterDishIdDisplay = {
    /** 单元格可见文案（含本地 `#n` 或云端 id / 截断） */
    text: string;
    /** 悬停全文；云端 string id 时提供，本地 `#n` 时可省略 */
    title?: string;
};

/** 云端 string id 超过此长度则截断展示（前 8 + … + 后 4） */
const MENU_LIBRARY_FILTER_CLOUD_ID_DISPLAY_MAX = 12;

/**
 * 筛选弹窗预览表「菜品 ID」列文案。
 * 本地：有 `item_id` 则 `#n`，否则 `—`。
 * 云端：`item_id > 0` 优先 `#n`（本地同步菜）；否则展示云端 string `id`（过长截断）。
 * @param dish - 菜品行
 * @param isCloud - 是否云端菜单详情
 */
export function getMenuLibraryFilterDishIdDisplay(
    dish: MenuDish,
    isCloud: boolean,
): MenuLibraryFilterDishIdDisplay {
    const rawItemId = dish.item_id != null ? Number(dish.item_id) : null;
    /** 有效本地数字 id（云端新建常为 0，不当作展示主键） */
    const hasPositiveItemId =
        rawItemId != null && Number.isFinite(rawItemId) && rawItemId > 0;

    if (isCloud) {
        if (hasPositiveItemId) {
            return { text: `#${rawItemId}` };
        }
        const cloudId = getCloudDishIdFromMenuDish(dish);
        if (cloudId == null) {
            return { text: '—' };
        }
        if (cloudId.length > MENU_LIBRARY_FILTER_CLOUD_ID_DISPLAY_MAX) {
            return {
                text: `${cloudId.slice(0, 8)}…${cloudId.slice(-4)}`,
                title: cloudId,
            };
        }
        return { text: cloudId, title: cloudId };
    }

    if (rawItemId != null && Number.isFinite(rawItemId)) {
        return { text: `#${rawItemId}` };
    }
    return { text: '—' };
}

/**
 * 判断一级菜单是否为空菜单。
 * @param menu - 当前一级菜单详情
 * @returns 没有任何小类时返回 true
 */
export function canDeleteEmptyMenu(menu: FirstMenu | null | undefined): boolean {
    return menu != null && Array.isArray(menu.menu_item_slu) && menu.menu_item_slu.length === 0;
}

/**
 * 判断小类是否为空小类。
 * @param subcategory - 当前小类详情
 * @returns 没有任何菜品时返回 true
 */
export function canDeleteEmptySubcategory(subcategory: SecondMenu | null | undefined): boolean {
    return subcategory != null && Array.isArray(subcategory.menu_item) && subcategory.menu_item.length === 0;
}

/**
 * 本地一级/二级菜单删除 OkPacket 是否成功。
 * 后端成功时可能返回 `affectedRows: 0`，故不以 affectedRows 为准，仅看是否带 `error`。
 * @param result - `delete_first_menu` / `delete_second_menu` 返回体
 * @returns 无 `error` 字段（或为假值）时 true
 */
export function isLocalMenuDeleteSuccess(result: unknown): boolean {
    return !_.get(result, 'error');
}

/**
 * 菜单库详情菜品表行是否可勾选：云端要求有云端 `id`；本地要求已落库 `item_id` 与 `slu_id`。
 * @param dish - 菜品行
 * @param isCloud - 是否云端菜单详情
 */
export function isMenuLibraryDetailDishRowSelectable(dish: MenuDish, isCloud: boolean): boolean {
    if (isCloud) {
        return getCloudDishIdFromMenuDish(dish) != null;
    }
    return dish.item_id != null && dish.slu_id != null;
}

/**
 * 从二级菜单对象读取云端小类 `_id`（详情扩展字段）。
 * @param sm - 小类行
 * @returns 非空 `_id`；否则 null
 */
export function getCloudSluIdFromSecondMenuEntity(sm: SecondMenu | null | undefined): string | null {
    if (sm == null) {
        return null;
    }
    const cloudSluId = (sm as { _id?: string })._id;
    return typeof cloudSluId === 'string' && cloudSluId.trim() ? cloudSluId.trim() : null;
}

/**
 * 菜单库详情左侧小类 key：本地用 `dmi_slu_id`；云端用 `_id`（`dmi_slu_id` 可能均为 0 导致重复 key / 选中错乱）。
 * @param slu - 小类行
 * @param index - 列表下标（兜底 key）
 * @param isCloud - 是否云端菜单详情
 */
export function getMenuLibraryDetailSecondSluKey(
    slu: SecondMenu,
    index: number,
    isCloud: boolean,
): string | number {
    if (isCloud) {
        const cloudId = getCloudSluIdFromSecondMenuEntity(slu);
        if (cloudId != null) {
            return cloudId;
        }
        return `cloud-slu-idx:${index}`;
    }
    return slu.dmi_slu_id != null ? slu.dmi_slu_id : `slu-${index}`;
}

/** 新建菜单弹窗内「子类别」草稿行（提交前仅前端持有） */
export interface CreateMenuSubcategoryDraft {
    /** 临时行 id，用于列表 key 与删除 */
    tempId: string;
    /** 已落库后的二级菜单 id；未保存前为空，失焦 PATCH 依赖此字段 */
    dmi_slu_id?: number;
    /** 云端已落库小类 `_id`；云端更新与删除请求使用此字段 */
    cloud_slu_id?: string;
    /** 子类别主名称 `dmi_slu_name`（`displayLang === 'unspecified'` 时编辑） */
    name: string;
    /** 子类别 `dmi_slu_name_i18n` JSON 字符串草稿（与一级菜单 `main_group_name_i18n` 结构同，`parseMainGroupNameI18n`） */
    nameI18nJson: string;
    /**
     * 本子类别行的「当前编辑/展示语言」选择（独立于详情 `menuDisplayLang`、新建弹窗一级菜单 `createDialogFirstMenuDisplayLang`）。
     * 对应 RBP2 `second-menu-detail` 的 `selectedI18nName`；名称输入框展示/写入 `dmi_slu_name` 与各语言槽 `name1`。
     */
    displayLang: MenuLibraryDisplayLang;
    /** 每人小类点餐数量限制（草稿字符串） */
    perPersonLimit: string;
    /** 桌台小类点餐数量限制（草稿字符串） */
    perTableLimit: string;
}

/** 菜单库列表卡片展示项（由一级菜单数据映射） */
export interface MenuLibraryItem {
    /** 一级菜单 id */
    main_group_id: number;
    /** 卡片标题（一级菜单名称） */
    title: string;
    /** 卡片副文案 */
    subtitle: string;
    /** 一级菜单排序下标（创建新菜单时计算下一个 index） */
    main_group_index?: number;
}

/** 跳转制作器时携带的路由 state（仅上传/分享打开基础菜品库） */
export type MenuLibraryCreatorLocationState = {
    openClassifyMenuLibrary?: boolean;
};

/** 菜单菜品制作器路由路径 */
export const MENU_DISH_CREATOR_PATH = '/main/rbp-cloud/dish-menu/creator';

/**
 * 节假日在 `NewMenuPrice.week` 中的取值（与制作器 `setEmptyNewPriceValue` 之外的扩展行一致，供菜单库星期/绑定费用使用）
 */
export const MENU_LIBRARY_WEEK_HOLIDAY_NUM = 7;

/**
 * RBP 门店设置 id：新建菜单价格方案（`value` 与一级菜单 `set_item_main` 语义一致，`1` 按周新方案，`0` 经典单套）
 */
export const MENU_LIBRARY_SETTING_ID_CREATE_MENU_PRICE_SCHEME = 78;

/**
 * 星期选项值：1–6 周一–周六，0 周日，7 节假日
 */
export const MENU_LIBRARY_WEEK_VALUES = [1, 2, 3, 4, 5, 6, 0, MENU_LIBRARY_WEEK_HOLIDAY_NUM] as const;

/**
 * 菜单库管理 MobX：列表、详情、新建一级菜单
 */
export class MenuLibraryManagementState {
    constructor() {
        makeAutoObservable(this, {
            /** `File` 非纯对象，避免 MobX 深度转换 */
            menuLibraryAddDishPendingDishImageFile: observable.ref,
            createDialogPendingMenuImageFile: observable.ref,
            /** 防抖定时器句柄，不参与响应式追踪 */
            detailSecondMenuReorderDebounceTimer: false,
            /** 与 PATCH 之间的中间态，避免无意义渲染 */
            detailSecondMenuReorderDirtySinceLastPersist: false,
            detailDishReorderDebounceTimer: false,
            detailDishReorderDirtySinceLastPersist: false,
        });
    }

    /** 一级菜单卡片列表 */
    menuLibraryList = observable<MenuLibraryItem>([]);

    /**
     * 本模块列表/详情/保存所使用的餐厅（与框架顶栏全局 currentRestaurant 独立；下拉仅改此字段）
     */
    menuLibraryRestaurant: Restaurant | null = null;

    /**
     * 模块内所选店展示用货币（隧道 `currency_name` > 云端 settings/currency > `€`）。
     * 与顶栏 `mainPageData.rbpCurrency` 独立，随菜单库顶栏门店下拉更新。
     */
    menuLibraryDisplayCurrency = '€';

    /** 模块内所选店 RBP 国家名（意大利为 `Italy`） */
    menuLibraryRbpCountryName = '';

    /** 列表加载中 */
    isLoading: boolean = false;

    /**
     * 模块内所选门店隧道/版本校验失败时的 i18n key；非空时**仅本地 Tab**列表区展示该文案（与「暂无更多数据」同一占位）。
     * 云端 Tab 不展示此错误，直接展示云端列表结果。
     */
    menuLibraryTunnelErrorI18nKey: string | null = null;

    /**
     * 与 `menuLibraryTunnelErrorI18nKey` 配套的插值（版本门禁：**`current_version` + `minimum_version`** 或仅 **`minimum_version`**）；无插值时为 `null`。
     */
    menuLibraryTunnelErrorParams: Record<string, string> | null = null;

    /**
     * **本地 Tab** 顶栏：当前所选门店隧道+版本校验已成功；为 false 时不展示节假日开关与「新建菜单」。
     * 云端 Tab 顶栏不依赖本字段。
     */
    menuLibraryListTunnelVerified: boolean = false;

    /**
     * 模块内所选门店隧道 `check-permission` 是否通过（**不含**版本门禁）。
     * - `null`：尚未出结果（校验中）
     * - `true`：隧道通，云端下拉 / `print_class` 可请求
     * - `false`：隧道不通，云端侧跳过 §5.5 与 `print_class` 请求
     */
    menuLibraryTunnelPermissionOk: boolean | null = null;

    /**
     * 列表顶区 Tab：本地菜单管理 / 云端菜单管理
     */
    menuLibraryListTab: 'local' | 'cloud' = 'local';

    /**
     * 是否处于「上传至云端菜品库」多选模式（本地列表）
     */
    isUploadToCloudSelectMode = false;

    /**
     * 多选待上传的本地一级菜单 `main_group_id` 列表
     */
    selectedUploadMainGroupIds: number[] = [];

    /**
     * 上传至云端提交中（展示「菜单上传中」浮层）
     */
    uploadToCloudSubmitting = false;

    /**
     * 上传请求代次：取消 / 切 Tab / init 时递增，用于丢弃进行中请求的成功回写。
     */
    private uploadToCloudSerial = 0;

    /**
     * 上传成功结果浮层数据（非空时展示；列表项取自本地选中卡片 title + subtitle）
     */
    uploadToCloudResult: {
        /** 成功上传份数 */
        menuCount: number;
        /** 紫条列表：菜单名 + 本地展示日期 */
        items: { primary: string; secondary?: string }[];
    } | null = null;

    /**
     * 云端菜单列表（摘要）
     */
    cloudMenuList: CloudDishLibraryMenuSummary[] = [];

    /**
     * 云端列表加载中
     */
    cloudMenuListLoading = false;

    /**
     * 是否处于「管理/删除云端菜单」多选模式
     */
    isCloudMenuDeleteSelectMode = false;

    /**
     * 多选待删除的云端菜单 id
     */
    selectedCloudMenuIds: string[] = [];

    /**
     * 删除云端菜单提交中（展示「菜单删除中」浮层）
     */
    cloudMenuDeleteSubmitting = false;

    /**
     * 删除请求代次：取消 / 切 Tab / init 时递增，用于丢弃进行中请求的成功回写。
     */
    private cloudMenuDeleteSerial = 0;

    /**
     * 删除二次确认弹窗是否打开（底部确认条「确定」后）
     */
    isCloudMenuDeleteConfirmOpen = false;

    /**
     * 删除成功结果浮层数据（非空时展示；列表项取自云端选中卡片名 + 日期副标题）
     */
    cloudMenuDeleteResult: {
        /** 成功删除份数 */
        menuCount: number;
        /** 紫条列表：菜单名 + 展示日期 */
        items: { primary: string; secondary?: string }[];
    } | null = null;

    /**
     * 是否显示加载云端菜品弹窗
     */
    isLoadCloudDishDialogOpen = false;

    /**
     * 加载弹窗：当前选中的云端菜单 id
     */
    loadCloudSelectedMenuId: string | null = null;

    /**
     * 加载弹窗：云端详情原始响应（结构联调收紧）
     */
    loadCloudMenuDetailRaw: unknown = null;

    /**
     * 加载弹窗：详情加载中
     */
    loadCloudMenuDetailLoading = false;

    /**
     * 加载弹窗：已勾选的云端菜品 id
     */
    loadCloudSelectedDishIds: string[] = [];

    /**
     * 加载弹窗：搜索关键字（名称/编码）
     */
    loadCloudDishSearchKeyword = '';

    /**
     * 加载弹窗：当前云端小类 id（仅切换列表；字段名联调可能变化）
     */
    loadCloudSelectedCategoryId: string | null = null;

    /**
     * add-dishes 提交中
     */
    loadCloudAddDishesSubmitting = false;

    /**
     * 加载弹窗：二次确认「菜品添加到本地菜单」是否打开（Figma `9805:50696`）
     */
    isLoadCloudConfirmOpen = false;

    /**
     * 加载成功弹窗：成功数 / 已存在数 / 名称 / 套餐下新建菜（非空时展示成功浮层 Figma `9782:49542`）
     */
    loadCloudAddResult: {
        successCount: number;
        existsCount: number;
        names: string[];
        createdCourseDishCount: number;
        createdCourseDishes: CloudDishLibraryCreatedCourseDish[];
    } | null = null;

    /**
     * 全店 type=0 菜单（含菜品），供套餐组「添加菜品」、转移/复制选二级与 MenuDishList；
     * 在页面 init 后后台预取；打开弹窗默认不重拉（有缓存则复用）。
     * 菜单树/菜品变更成功后经 `refreshLocalMenusAfterMutation` 强制重拉（对齐 RBP2）。
     * 亦用于上传确认条统计所选菜单菜品总数。
     */
    courseGroupPickerMenusAll: FirstMenu[] | null = null;

    courseGroupPickerMenusLoading = false;

    /**
     * 云端套餐选菜：菜单列表骨架（小类无菜，点击后再 merge 详情）。
     */
    cloudCourseGroupPickerMenus: FirstMenu[] | null = null;

    /** 云端套餐选菜列表 loading */
    cloudCourseGroupPickerMenusLoading = false;

    /**
     * 云端套餐选菜：正在懒加载详情的云端菜单 id；无请求时为 null。
     * 用于「添加菜品」弹窗一级菜单展开行内 loading。
     */
    cloudCoursePickerDetailLoadingId: string | null = null;

    /** 已懒加载过详情的云端菜单 id，避免重复请求 */
    private cloudCoursePickerLoadedMenuIds = new Set<string>();

    /**
     * 每次 `init()` 递增；`fetchFirstMenuList` / `ensureCourseGroupPickerMenusLoaded` 带该 epoch 返回后若不一致则丢弃结果，
     * 避免连续 init（如快速切换门店）时上一次请求晚到覆盖新数据或误关 loading。
     */
    private menuLibraryInitEpoch = 0;

    /** `ensureCourseGroupPickerMenusLoaded` 并发序号；仅当仍为「当前次」fetch 时才结束 loading，避免陈旧请求误清状态 */
    private coursePickerMenusFetchSerial = 0;

    /** `ensureCloudCourseGroupPickerMenusLoaded` 并发序号；force 重拉时抬升以丢弃进行中旧请求 */
    private cloudCoursePickerMenusFetchSerial = 0;

    /** 是否显示新建一级菜单弹窗 */
    isCreateMenuDialogOpen: boolean = false;

    /**
     * 新建弹窗来源：本地「新增本地菜单」或云端「新增云端菜单」（同壳；云端写操作走门禁 toast）
     */
    createSource: 'local' | 'cloud' = 'local';

    /** 新建一级菜单：主名称 `main_group_name`（未指定语言槽时编辑） */
    newMenuPrimaryName: string = '';
    /** 新建一级菜单：`main_group_name_i18n` JSON 草稿（与 RBP2 `parseI18n` 语义一致） */
    createDialogMainGroupNameI18nJson: string = DEFAULT_MAIN_GROUP_NAME_I18N_JSON;

    /** 新建菜单弹窗：子类别草稿列表（确认后按序 `post_create_second_menu`） */
    createMenuSubcategoryDrafts: CreateMenuSubcategoryDraft[] = [];

    /**
     * 新建菜单弹窗：非空表示正在查看某子类别的「子分类详情」（菜品列表）；与 `createMenuSubcategoryDrafts` 中 `tempId` 对应
     */
    createMenuSubcategoryDetailTempId: string | null = null;

    /** 新建菜单弹窗 — 子分类详情内「新增菜品」占位弹窗是否打开 */
    isCreateMenuSubcategoryAddDishDialogOpen: boolean = false;

    /**
     * 「新增/编辑/批量设置菜品」弹窗模式：批量时与 `batchDishSettingsRowKeys` 配合，共用 `MenuLibraryAddDishDialogContent`。
     */
    menuLibraryAddDishDialogMode: 'single' | 'batch' = 'single';

    /** 批量菜品设置：勾选的 `createMenuSubcategoryDishRowKey` 列表（仅已落库 `item_id` 会参与保存） */
    batchDishSettingsRowKeys: string[] = [];

    /**
     * 子分类详情内打开「新增/编辑菜品」弹窗时：若为非空，表示编辑 `createMenuSubcategoryDishRowKey` 对应的行；
     * 为 `null` 表示新增菜品。
     */
    createMenuSubcategoryAddDishEditRowKey: string | null = null;

    /**
     * 菜单库**详情页**打开「新增/编辑菜品」时：若非空，表示编辑该 `item_id` 的已落库菜品；
     * 与 `createMenuSubcategoryAddDishEditRowKey` 互斥；仅当未处于新建弹窗子分类详情（`createMenuSubcategoryDetailTempId == null`）时生效。
     * 云端详情：仅当本地数字 `item_id > 0` 时写入；云端主键见 `menuLibraryDetailAddDishEditCloudDishId`。
     */
    menuLibraryDetailAddDishEditItemId: number | null = null;

    /**
     * 云端菜单详情编辑菜品时：当前编辑行的云端菜品 string `id`（`item_id` 可能均为 0，不能作查找键）。
     */
    menuLibraryDetailAddDishEditCloudDishId: string | null = null;

    /**
     * 菜单库详情内首次 `post_dishes` 成功后、列表尚未含该行时，`getAddDishEditTarget` 回退用（与 `menuLibraryDetailAddDishEditItemId` 配对，列表命中后不再使用）。
     */
    menuLibraryDetailPostSavedDishFallback: MenuDish | null = null;

    /**
     * 新增/编辑菜品弹窗：裁剪后的菜品图文件，仅在点击「保存」时上传 restaurant-static；
     * 与 `RestaurantStaticImageUpload` 的 `deferStaticUpload` 配合。
     */
    menuLibraryAddDishPendingDishImageFile: File | null = null;

    /** 用户在弹窗内删除「服务端已有图」的意图，保存时再请求删除静态资源并清空 `dish_image` */
    menuLibraryAddDishPendingRemoveServerDishImage: boolean = false;

    /**
     * 本次打开弹窗时快照的 `dish_image`（供延迟删除时解析静态文件名，与表单当前值可能不同步）
     */
    menuLibraryAddDishDishImageRefAtDialogOpen: string = '';

    /**
     * 设置待保存的菜品图本地文件（选图会清除「待删服务端图」标记）
     */
    setMenuLibraryAddDishPendingDishImageFile(file: File | null) {
        if (file != null) {
            this.menuLibraryAddDishPendingRemoveServerDishImage = false;
        }
        this.menuLibraryAddDishPendingDishImageFile = file;
    }

    /** 标记保存时需删除服务端静态图并清空库字段 */
    markMenuLibraryAddDishPendingRemoveServerDishImage() {
        this.menuLibraryAddDishPendingRemoveServerDishImage = true;
        this.menuLibraryAddDishPendingDishImageFile = null;
    }

    /**
     * 重置菜品图暂存态；`dishImageRefFromServer` 为打开弹窗或保存成功后的基线 `dish_image`
     */
    resetMenuLibraryAddDishImageStaging(dishImageRefFromServer: string) {
        this.menuLibraryAddDishPendingDishImageFile = null;
        this.menuLibraryAddDishPendingRemoveServerDishImage = false;
        this.menuLibraryAddDishDishImageRefAtDialogOpen = String(dishImageRefFromServer ?? '').trim();
    }

    /**
     * 保存菜品时：按 `item_id` 调用 restaurant-static 上传/删除；返回供前端回填 `MenuDish.dish_image` 的字符串（不随保存菜品接口提交）
     */
    async resolveMenuLibraryAddDishImageForSave(stringId: string, itemId: number, fallbackDishImage: string): Promise<string> {
        const pending = this.menuLibraryAddDishPendingDishImageFile;
        const removeSrv = this.menuLibraryAddDishPendingRemoveServerDishImage;
        const refAtOpen = this.menuLibraryAddDishDishImageRefAtDialogOpen;
        if (pending) {
            const queryFileName = buildRestaurantStaticUploadQueryFileName(itemId, pending);
            const res = await apiManage.post_restaurant_static_dish_image(stringId, pending, queryFileName);
            if (_.get(res, 'error')) {
                throw new Error(i18n.t('general_text_failed_upload_picture'));
            }
            const fn = apiManage.parseRestaurantStaticUploadFileName(res);
            if (!fn) {
                throw new Error(i18n.t('general_text_failed_upload_picture'));
            }
            return fn;
        }
        if (removeSrv) {
            const delFn = extractFileNameForRestaurantStaticDelete(refAtOpen);
            if (delFn) {
                const delRes = await apiManage.delete_restaurant_static_dish_image(stringId, delFn);
                if (_.get(delRes, 'error')) {
                    throw new Error(i18n.t('global_text_operate_failure'));
                }
            }
            return '';
        }
        return String(fallbackDishImage ?? '').trim();
    }

    /**
     * 新建菜单弹窗：裁剪后的一级菜单图，仅在「保存」时上传 restaurant-static（`deferStaticUpload`）
     */
    createDialogPendingMenuImageFile: File | null = null;

    /** 新建弹窗内用户删除已有 `menu_image` 的意图，保存时再请求删除静态资源 */
    createDialogPendingRemoveServerMenuImage: boolean = false;

    /** 打开新建弹窗或上次保存成功时快照的 `menu_image`，供延迟删除解析文件名 */
    createDialogMenuImageRefAtDialogOpen: string = '';

    /** 新建弹窗内当前展示用的 `menu_image`（完整 URL 或文件名） */
    createDialogMenuImageRef: string = '';

    setCreateDialogPendingMenuImageFile(file: File | null) {
        if (file != null) {
            this.createDialogPendingRemoveServerMenuImage = false;
        }
        this.createDialogPendingMenuImageFile = file;
    }

    markCreateDialogPendingRemoveServerMenuImage() {
        this.createDialogPendingRemoveServerMenuImage = true;
        this.createDialogPendingMenuImageFile = null;
        this.createDialogMenuImageRef = '';
    }

    /**
     * 重置新建弹窗菜单图暂存；`menuImageRefFromServer` 为打开弹窗或保存成功后的基线
     */
    resetCreateDialogMenuImageStaging(menuImageRefFromServer: string) {
        this.createDialogPendingMenuImageFile = null;
        this.createDialogPendingRemoveServerMenuImage = false;
        const ref = String(menuImageRefFromServer ?? '').trim();
        this.createDialogMenuImageRefAtDialogOpen = ref;
        this.createDialogMenuImageRef = ref;
    }

    /**
     * 新建弹窗保存时：按 `main_group_id` 调用 menu-image 上传/删除；返回供前端展示的 `menu_image` 字符串（不写入 `patch_first_menu` 请求体）
     */
    async resolveCreateMenuDialogMenuImageForSave(
        stringId: string,
        menuId: number | string,
        fallbackMenuImage: string,
    ): Promise<string> {
        const pending = this.createDialogPendingMenuImageFile;
        const removeSrv = this.createDialogPendingRemoveServerMenuImage;
        const refAtOpen = this.createDialogMenuImageRefAtDialogOpen;
        if (this.isCloudCreateDialog) {
            const cloudMenuId = String(menuId).trim();
            if (!cloudMenuId) {
                return String(fallbackMenuImage ?? '').trim();
            }
            if (pending) {
                const res = await cloudDishLibraryApi.post_cloud_dish_library_menu_image(cloudMenuId, pending, stringId);
                if (_.get(res, 'error')) {
                    throw new Error(i18n.t('general_text_failed_upload_picture'));
                }
                return pickCloudImageUploadRef(res) ?? String(fallbackMenuImage ?? '').trim();
            }
            if (removeSrv) {
                const res = await cloudDishLibraryApi.post_cloud_dish_library_images_delete(
                    { type: 'menu', cloud_menu_id: cloudMenuId },
                    stringId,
                );
                if (_.get(res, 'error')) {
                    throw new Error(i18n.t('global_text_operate_failure'));
                }
                return '';
            }
            return String(fallbackMenuImage ?? '').trim();
        }
        if (pending) {
            const queryFileName = buildRestaurantStaticUploadQueryFileName(Number(menuId), pending);
            const res = await apiManage.post_restaurant_static_menu_image(stringId, pending, queryFileName);
            if (_.get(res, 'error')) {
                throw new Error(i18n.t('general_text_failed_upload_picture'));
            }
            const fn = apiManage.parseRestaurantStaticUploadFileName(res);
            if (!fn) {
                throw new Error(i18n.t('general_text_failed_upload_picture'));
            }
            return fn;
        }
        if (removeSrv) {
            const delFn = extractFileNameForRestaurantStaticDelete(refAtOpen);
            if (delFn) {
                const delRes = await apiManage.delete_restaurant_static_menu_image(stringId, delFn);
                if (_.get(delRes, 'error')) {
                    throw new Error(i18n.t('global_text_operate_failure'));
                }
            }
            return '';
        }
        return String(fallbackMenuImage ?? '').trim();
    }

    /**
     * 云端菜品保存时处理延迟图片上传或删除；无图片变更时保留原引用。
     * @param cloudMenuId 云端菜单 id
     * @param cloudDishId 云端菜品 id
     * @param fallbackDishImage 原菜品图片引用
     */
    async resolveCloudDishImageForSave(
        cloudMenuId: string,
        cloudDishId: string,
        fallbackDishImage: string,
    ): Promise<string> {
        const pending = this.menuLibraryAddDishPendingDishImageFile;
        if (pending) {
            const res = await cloudDishLibraryApi.post_cloud_dish_library_dish_image(
                cloudMenuId,
                cloudDishId,
                pending,
                this.resolveRbpStringIdForApi(),
            );
            if (_.get(res, 'error')) {
                throw new Error(i18n.t('general_text_failed_upload_picture'));
            }
            return pickCloudImageUploadRef(res) ?? String(fallbackDishImage ?? '').trim();
        }
        if (this.menuLibraryAddDishPendingRemoveServerDishImage) {
            const res = await cloudDishLibraryApi.post_cloud_dish_library_images_delete(
                { type: 'dish', cloud_menu_id: cloudMenuId, cloud_dish_id: cloudDishId },
                this.resolveRbpStringIdForApi(),
            );
            if (_.get(res, 'error')) {
                throw new Error(i18n.t('global_text_operate_failure'));
            }
            return '';
        }
        return String(fallbackDishImage ?? '').trim();
    }

    /**
     * 新建弹窗子分类详情 — 各子类别 `tempId` 下的菜品列表缓存（与 `MenuDish` 一致，待接列表接口后写入）
     */
    createMenuSubcategoryDetailDishesByTempId: Record<string, MenuDish[]> = {};

    /** 新建菜单弹窗：绑定费用所选星期（`holiday` 对应 `MENU_LIBRARY_WEEK_HOLIDAY_NUM`） */
    createMenuSelectedWeekKey: number | 'holiday' = 1;

    /**
     * 新建菜单弹窗：新价格方案 `set_item_main === '1'` 下的按周 `value` 草稿（含节假日一行），与 `first-menu-detail` / `setEmptyNewPriceValue` 结构一致
     */
    createMenuPriceValueRows: NewMenuPrice[] = [];

    /**
     * 新建菜单弹窗：绑定价方案。`'1'` 按周 `value` + `patch_first_menu_day_price`；`'0'` 仅根级 `extra_*` 与三档价（与 `FirstMenu.set_item_main`、制作器旧方案一致）
     */
    createDialogSetItemMain: '0' | '1' = '1';

    /**
     * 新建菜单弹窗：当前菜单菜品不受轮次限制（仅云端新建展示并写入 `menus/create.not_joining_global_limit`；本地新建隐藏）
     */
    createMenuRoundLimitUnrestricted: boolean = false;

    /** 新建菜单弹窗：大类限点 `max_limit`（与详情顶栏 `detailHeaderMaxLimitDraft` 语义一致） */
    createDialogMaxLimitDraft: string = '';

    /** 创建提交中 */
    isSubmittingCreate: boolean = false;

    /**
     * 新建菜单弹窗：已成功创建一级菜单后的 `main_group_id`（弹窗不关闭时「保存修改」走 PATCH，「新增子类别」走 `post_create_second_menu`）
     */
    createDialogPersistedMainGroupId: number | null = null;

    /** 新建云端菜单已持久化的菜单 id；同一弹窗后续保存走云端局部 update */
    createDialogPersistedCloudMenuId: string | null = null;

    /**
     * 新建菜单弹窗：下一个二级菜单 `dmi_slu_index`（与已写入后端的二级数量一致）
     */
    createDialogNextSecondIndex: number = 0;

    // ---------- 详情视图 ----------
    /** 详情数据来源：本地一级菜单 / 云端菜单 */
    detailSource: 'local' | 'cloud' = 'local';

    /** 云端详情当前菜单 id；与 `detailMainGroupId` 互斥（云端时本地 id 保持 null） */
    detailCloudMenuId: string | null = null;

    /** 非空表示处于本地详情（当前一级菜单 id）；云端详情为 null，主键用 `detailCloudMenuId` */
    detailMainGroupId: number | null = null;

    /** 详情页加载中 */
    detailLoading: boolean = false;

    /**
     * 当前详情会话内是否已完成至少一次 `loadDetailData`（`openMenuDetail` 时置 false）。
     * 仅首次进入详情、首次请求未完成前展示整页骨架；刷新/保存后重载仍用 `detailLoading` + 表格 loading，不再全屏骨架。
     */
    detailFirstLoadCompleted: boolean = false;

    /** 当前一级菜单完整数据（含二级与菜品） */
    detailFirstMenu: FirstMenu | null = null;

    /** 当前选中的二级菜单 id（本地详情用 `dmi_slu_id`） */
    selectedSecondSluId: number | null = null;

    /**
     * 云端详情当前选中小类 `_id`；本地详情为 null。
     * 云端手工创建时 `dmi_slu_id` 常为 0，不能用本地数字 id 做选中键。
     */
    selectedCloudSluId: string | null = null;

    /** 详情单项删除确认对象；仅空菜单或空小类可设置。 */
    detailDeleteConfirmType: 'menu' | 'subcategory' | null = null;

    /** 详情单项删除请求是否进行中。 */
    detailDeleteSubmitting: boolean = false;

    /** 绑定费用所选星期 */
    selectedWeek: number = 1;

    /** 详情页（及顶栏语言按钮）：菜品列表展示用名称字段（见 `getDishDisplayName`）；非新建弹窗一级菜单名编辑专用 */
    menuDisplayLang: MenuLibraryDisplayLang = 'unspecified';

    /** 菜品搜索关键字（右表过滤） */
    dishSearchKeyword: string = '';

    /** 「搜索菜品」弹窗（Figma 3424:35387） */
    isDishSearchDialogOpen: boolean = false;

    /** 是否展开筛选区（预留；筛选能力已迁至「筛选菜品」弹窗） */
    isFilterExpanded: boolean = false;

    /** 菜单详情 — 菜品筛选弹窗（Figma 3330:25007） */
    isDishFilterDialogOpen: boolean = false;

    /** 弹窗内草稿：快速定位 chip */
    dishFilterQuickPreset: DishFilterQuickPreset = 'all';

    /** 弹窗内草稿：下拉 — 菜品类别 / 图片 / 打印组，值 `all` 或具体选项 */
    dishFilterCategoryValue: string = 'all';

    dishFilterImageValue: string = 'all';

    dishFilterPrintValue: string = 'all';

    /** 已应用到右侧菜品表的筛选条件 */
    appliedDishFilterQuickPreset: DishFilterQuickPreset = 'all';

    appliedDishFilterCategoryValue: string = 'all';

    appliedDishFilterImageValue: string = 'all';

    appliedDishFilterPrintValue: string = 'all';

    /** 批量「不受轮次限制」开关：patch 请求进行中 */
    isBatchRoundToggleLoading: boolean = false;

    /** 打印组 id -> 名称 */
    printGroupMap: Record<number, string> = {};

    /** 详情顶区是否处于页内编辑（名称 + 绑定费用） */
    isDetailHeaderEditMode: boolean = false;

    /** 子分类拖拽排序提交中 */
    isDetailSecondMenuReorderSaving: boolean = false;

    /**
     * 子分类拖拽：本地顺序已改、尚未经防抖落库 `PATCH menus/second/index`（为 false 时 `persistDetailSecondMenuOrderToServer` 直接返回）
     */
    detailSecondMenuReorderDirtySinceLastPersist: boolean = false;

    /** 子分类顺序落库防抖定时器（浏览器下为 number） */
    detailSecondMenuReorderDebounceTimer: ReturnType<typeof setTimeout> | null = null;

    /**
     * 连续拖动时合并为一次 PATCH 的静默时间（ms）；仅真正请求时置 `isDetailSecondMenuReorderSaving`
     */
    readonly DETAIL_SECOND_MENU_REORDER_DEBOUNCE_MS = 500;

    /** 详情当前子类下菜品拖拽排序 PATCH 进行中 */
    isDetailDishReorderSaving: boolean = false;

    /**
     * 详情菜品行：本地顺序已改、尚未经防抖落库。
     * 本地：`PATCH menus/first/index`（`{ item_id, slu_priority }[]`）；云端：`POST .../dishes/sort`。
     */
    detailDishReorderDirtySinceLastPersist: boolean = false;

    /** 详情菜品顺序落库防抖定时器 */
    detailDishReorderDebounceTimer: ReturnType<typeof setTimeout> | null = null;

    /** 详情菜品行拖拽合并 PATCH 静默时间（ms） */
    readonly DETAIL_DISH_REORDER_DEBOUNCE_MS = 500;

    /** 保存顶区修改请求中 */
    isDetailHeaderSaveLoading: boolean = false;

    /**
     * 详情顶栏编辑：一级菜单名称多语言当前槽（与 `menuDisplayLang` 菜品列表展示分离）
     */
    detailHeaderMenuNameDisplayLang: MenuLibraryDisplayLang = 'unspecified';
    /** 详情顶栏编辑：`main_group_name` 草稿 */
    detailHeaderMenuNamePrimary: string = '';
    /** 详情顶栏编辑：`main_group_name_i18n` JSON 草稿 */
    detailHeaderMenuNameI18nJson: string = '';

    /** 详情顶栏编辑：大类限点 `max_limit`（输入框字符串） */
    detailHeaderMaxLimitDraft: string = '';

    /**
     * 详情顶栏编辑：`set_item_main === '1'` 时价格方案 `value` 是否包含节假日行（week=7）
     */
    detailHeaderHolidayMenuEnabled: boolean = false;

    /**
     * 门店设置 id=153「节假日菜单当日是否启用」（与 RBP2 制作器 `getSettingById(153)` / `patchSettingById(153, { is_holidays })` 一致）
     */
    storeHolidayMenuOpen: boolean = false;

    /**
     * 门店设置 id=22 菜品排序（与 RBP2 `dishSortValue` 一致；用于 `get_menus_v3.sort` 与拖拽门禁）
     */
    dishSortValue: string = MENU_LIBRARY_DEFAULT_DISH_SORT;

    /** 门店设置 id=103 是否允许按优先级调整本地子类别顺序 */
    menuPrioritySortEnabled: boolean = false;

    /**
     * 详情子类别是否允许按优先级拖拽排序。
     * 云端不受 setting 103 限制，本地由 setting 103 控制。
     */
    get isDetailSecondMenuPrioritySortEnabled(): boolean {
        if (this.detailSource === 'cloud') {
            return true;
        }
        return this.menuPrioritySortEnabled;
    }

    /**
     * 详情菜品表是否允许拖拽排序。
     * - 云端：不读 setting 22，始终可拖（顺序由详情返回 + `dishes/sort` 写回）。
     * - 本地：仅「优先级 / slu_priority DESC」时与 RBP2 一致可拖。
     */
    get isDetailDishPrioritySortEnabled(): boolean {
        if (this.isCloudMenuDetail) {
            return true;
        }
        return this.dishSortValue === MENU_LIBRARY_DISH_SORT_BY_PRIORITY;
    }

    /** 正在切换设置 153（避免重复点击） */
    storeHolidayMenuToggleLoading: boolean = false;

    /** 正在拉取 setting 153（列表顶栏开关初始值）；与 PATCH loading 并行时用 IOSSwitch `loading` 合并展示 */
    storeHolidayMenuSettingFetchLoading: boolean = false;

    /**
     * 额外费用菜品（type=1 菜单下扁平化），与制作器 `getExtraMenuData` 同源，用于绑定费用多选
     */
    extraMenuData: MenuDish[] = [];

    /**
     * 页内编辑：成人档所选额外费用 item_id（与 `FirstMenu.extra_normal` / `NewMenuPrice.extra_normal` 逗号串一致）
     */
    editExtraNormalIds: string[] = [];

    /** 页内编辑：儿童档所选额外费用 item_id */
    editExtraChildIds: string[] = [];

    /** 页内编辑：其他档所选额外费用 item_id */
    editExtraOtherIds: string[] = [];

    /**
     * 详情顶栏编辑：复合型菜单价格按周草稿；非 null 时切换星期前先 persist 再 load
     */
    detailEditPriceValueDraft: NewMenuPrice[] | null = null;

    /**
     * 新建菜单弹窗：成人/儿童/其他档所选额外费用 item_id（与详情顶栏绑定费用交互一致；创建接口暂不落库，仅 UI 与详情对齐）
     */
    createExtraNormalIds: string[] = [];

    /** 新建菜单弹窗：儿童档所选额外费用 item_id */
    createExtraChildIds: string[] = [];

    /** 新建菜单弹窗：其他档所选额外费用 item_id */
    createExtraOtherIds: string[] = [];

    /**
     * 新建菜单弹窗：一级菜单名称编辑所用的展示语言（对应 RBP2 `selectedI18nName` 与「默认/各语言槽」的语义）。
     * 与详情页菜品列表的 `menuDisplayLang`、子类别草稿行 `displayLang` 互不覆盖。
     */
    createDialogFirstMenuDisplayLang: MenuLibraryDisplayLang = 'unspecified';

    /**
     * 新建弹窗内名称输入框绑定（随 `createDialogFirstMenuDisplayLang` 切换槽位，与 RBP2 `main_group_name` / `main_group_name_i18n` 一致）
     */
    get newMenuName(): string {
        return getMainGroupNameForDisplay({
            primary: this.newMenuPrimaryName,
            i18nJson: this.createDialogMainGroupNameI18nJson,
            lang: this.createDialogFirstMenuDisplayLang,
        });
    }

    /**
     * 详情顶栏编辑态名称输入框绑定（随 `detailHeaderMenuNameDisplayLang` 切换槽位）
     */
    get editMenuNameDraft(): string {
        return getMainGroupNameForDisplay({
            primary: this.detailHeaderMenuNamePrimary,
            i18nJson: this.detailHeaderMenuNameI18nJson,
            lang: this.detailHeaderMenuNameDisplayLang,
        });
    }

    /** 详情「新增子类别」弹窗名称输入（随 `detailAddSubcategoryDisplayLang` 切换槽位） */
    get detailAddSubcategoryNameDraft(): string {
        return getMainGroupNameForDisplay({
            primary: this.detailAddSubcategoryNamePrimary,
            i18nJson: this.detailAddSubcategoryNameI18nJson,
            lang: this.detailAddSubcategoryDisplayLang,
        });
    }

    /** 分享菜单弹窗 */
    isShareMenuDialogOpen: boolean = false;

    /** 分享主弹窗：点击「同步至菜单商店」后，直至「商店目标」列表拉取结束（成功或失败） */
    shareMenuDialogMenuStoreOpeningLoading: boolean = false;

    /** 分享主弹窗：`POST /v3/menu/share` 同步至所选门店请求中 */
    shareMenuDialogSelectedStoresSubmitting: boolean = false;

    /** 分享 — 选择同步至商店：新建或覆盖已上传（Figma 4029:27608） */
    isShareStoreTargetDialogOpen: boolean = false;

    /** 分享 — 商店目标弹窗内拉取「我的菜单」列表中 */
    shareStoreTargetListLoading: boolean = false;

    /** 分享 — 提交新建/覆盖商店模板请求中 */
    shareStoreTargetSubmitting: boolean = false;

    /**
     * `GET .../my-menus/uploaded` 分页累加的 **`list`**（用于「我上传」覆盖候选）。
     */
    shareStoreTargetMyMenusRaw: MenuStoreMenuListItem[] = [];

    /** 分享弹窗 uploaded 侧 `total` */
    shareStoreTargetMyMenusTotal = 0;

    /** 下一页待请求的 uploaded 页码（首屏成功后为 2） */
    shareStoreTargetMyMenusNextPageToLoad = 1;

    /** 分享弹窗 uploaded 列表触底加载中 */
    shareStoreTargetMyMenusLoadingMore = false;

    /**
     * 分享弹窗拉取 uploaded 的 `pagination_per_page`（各页固定，保证 offset 连续）。
     * 取 **4 的倍数**，与末行裁切逻辑配合；单页 48 条便于与 4 列栅格对齐。
     */
    readonly shareStoreTargetMyMenusPageSize = 48;

    /**
     * 商店同步目标：`new` 为新建文档；否则为待覆盖项的 Mongo `_id`
     */
    shareStoreTargetSelection: 'new' | string = 'new';

    /** 分享成功确认弹窗（Figma 4029:28100） */
    isShareSuccessDialogOpen: boolean = false;

    /** 成功弹窗展示的菜单名称（当前库内展示名） */
    shareSuccessDialogMenuName: string = '';

    /** 成功弹窗多行说明（已按发生步骤拼好） */
    shareSuccessDialogLines: string[] = [];

    /**
     * 分享结果弹窗是否展示成功图标；false 时表示无任何门店同步成功或接口失败（与菜单商店立刻使用结果语义一致）。
     */
    shareSuccessHadSuccessfulSync = true;

    /** 详情页「新增子类别」弹窗（Figma 3342:33380） */
    isDetailAddSubcategoryDialogOpen: boolean = false;

    /** 子类别弹窗：新增 / 编辑 */
    detailAddSubcategoryDialogMode: 'create' | 'edit' = 'create';

    /** 编辑模式下目标二级菜单 `dmi_slu_id`（本地）；新增时为 null */
    detailEditSubcategorySluId: number | null = null;

    /** 编辑模式下目标云端小类 `_id`；本地编辑或新增时为 null */
    detailEditSubcategoryCloudSluId: string | null = null;

    /** 弹窗内子类别主名称 `dmi_slu_name` 草稿 */
    detailAddSubcategoryNamePrimary: string = '';

    /** 弹窗内 `dmi_slu_name_i18n` JSON 草稿 */
    detailAddSubcategoryNameI18nJson: string = DEFAULT_MAIN_GROUP_NAME_I18N_JSON;

    /** 弹窗内名称多语言当前槽 */
    detailAddSubcategoryDisplayLang: MenuLibraryDisplayLang = 'unspecified';

    /** 每人小类点餐数量限制（字符串，与新建弹窗子类别列一致） */
    detailAddSubcategoryPerPersonLimit: string = '';

    /** 桌台小类点餐数量限制 */
    detailAddSubcategoryPerTableLimit: string = '';

    /** 提交新增子类别请求中 */
    isDetailAddSubcategorySubmitting: boolean = false;

    /**
     * 初始化列表：
     * - **本地 Tab**：先按所选门店校验隧道（v3 `check-permission`）+ v1 `version-check`，均通过后再拉一级菜单；失败则在本地列表空态展示隧道/版本文案。
     * - **云端 Tab**：**不**做隧道/版本门禁；有 `string_id` 即直接拉云端列表（可与本地隧道校验并行）。
     */
    async init() {
        const epoch = ++this.menuLibraryInitEpoch;
        runInAction(() => {
            this.courseGroupPickerMenusAll = null;
            /** 云端套餐选菜骨架一并清空，避免切门店后仍展示上一店缓存 */
            this.cloudCourseGroupPickerMenus = null;
            this.cloudCoursePickerLoadedMenuIds = new Set();
            this.cloudCoursePickerDetailLoadingId = null;
            this.menuLibraryTunnelErrorI18nKey = null;
            this.menuLibraryTunnelErrorParams = null;
            this.menuLibraryListTunnelVerified = false;
            this.menuLibraryTunnelPermissionOk = null;
            /** 门店切换 / 重载列表时退出上传多选与云端删除多选，避免选中项与新列表错位 */
            this.isUploadToCloudSelectMode = false;
            this.selectedUploadMainGroupIds = [];
            this.uploadToCloudSerial += 1;
            this.uploadToCloudSubmitting = false;
            this.uploadToCloudResult = null;
            this.isCloudMenuDeleteSelectMode = false;
            this.selectedCloudMenuIds = [];
            this.cloudMenuDeleteSerial += 1;
            this.cloudMenuDeleteSubmitting = false;
            this.isCloudMenuDeleteConfirmOpen = false;
            this.cloudMenuDeleteResult = null;
            this.cloudMenuList = [];
            /** 当前已在云端 Tab（含门店切换重 init）时先展示加载，避免空列表误显「暂无数据」 */
            this.cloudMenuListLoading = this.menuLibraryListTab === 'cloud';
            this.closeLoadCloudDishDialog();
        });
        const sid = this.resolveRbpStringIdForApi().trim();
        if (!sid) {
            runInAction(() => {
                this.menuLibraryList.replace([]);
                this.menuLibraryTunnelErrorI18nKey = 'business_analysis_page_none_restaurant';
                this.menuLibraryTunnelErrorParams = null;
                this.menuLibraryTunnelPermissionOk = false;
                /** 无门店时不展示上一门店的节假日开关状态 */
                this.storeHolidayMenuOpen = false;
                this.cloudMenuListLoading = false;
            });
            return;
        }

        /** 云端列表与隧道门禁解耦：当前在云端 Tab 时立刻拉取，不等待本地校验 */
        if (this.menuLibraryListTab === 'cloud') {
            void this.fetchCloudMenuList();
        }

        this.setLoading(true);
        try {
            const tunnelRes = await apiManage_rbp.checkPermissionByRestaurantStringId(sid);
            if (epoch !== this.menuLibraryInitEpoch) {
                return;
            }
            if (tunnelRes && tunnelRes.error) {
                runInAction(() => {
                    this.menuLibraryList.replace([]);
                    this.menuLibraryTunnelErrorI18nKey = mapErrorToI18nKey(tunnelRes);
                    this.menuLibraryTunnelErrorParams = null;
                    this.menuLibraryTunnelPermissionOk = false;
                    /** 隧道不可用时清空节假日开关，避免沿用其他门店状态 */
                    this.storeHolidayMenuOpen = false;
                });
                return;
            }

            /** 隧道 check-permission 已通过（版本门禁另计；云端下拉仅看本标志） */
            runInAction(() => {
                this.menuLibraryTunnelPermissionOk = true;
            });

            /** 隧道通后单独调 v1 `version-check`，与 `check-permission` 无关 */
            const vres = await apiManage.get_restaurant_version_check(sid);
            if (epoch !== this.menuLibraryInitEpoch) {
                return;
            }
            if (vres && vres.error) {
                runInAction(() => {
                    this.menuLibraryList.replace([]);
                    this.menuLibraryTunnelErrorI18nKey = mapErrorToI18nKey(vres);
                    this.menuLibraryTunnelErrorParams = null;
                    this.storeHolidayMenuOpen = false;
                });
                return;
            }

            const dbVersionBlock = getRestaurantVersionCheckBlockI18n(vres);
            if (dbVersionBlock) {
                runInAction(() => {
                    this.menuLibraryList.replace([]);
                    this.menuLibraryTunnelErrorI18nKey = dbVersionBlock.key;
                    this.menuLibraryTunnelErrorParams = dbVersionBlock.params;
                    this.storeHolidayMenuOpen = false;
                });
                return;
            }

            runInAction(() => {
                this.menuLibraryListTunnelVerified = true;
            });
            try {
                /** 列表态须同步 setting 153；详情态仍由 `loadDetailData` 并联拉取 */
                await Promise.all([
                    this.fetchFirstMenuList(epoch),
                    this.refreshStoreHolidayMenuToggleFromServer(epoch),
                    this.fetchMenuLibraryRestaurantContext({ initEpoch: epoch }),
                ]);
            } catch (error) {
                if (epoch === this.menuLibraryInitEpoch) {
                    console.error('MenuLibraryManagementState.init failed:', error);
                    GlobalNotification.error(i18n.t('global_text_operate_failure'));
                }
            }
            void this.ensureCourseGroupPickerMenusLoaded(epoch);
        } catch (error) {
            if (epoch === this.menuLibraryInitEpoch) {
                console.error('MenuLibraryManagementState.init tunnel check failed:', error);
                runInAction(() => {
                    this.menuLibraryList.replace([]);
                    this.menuLibraryTunnelErrorI18nKey = 'business_analysis_tunnel_config_error_connection';
                    this.menuLibraryTunnelErrorParams = null;
                    this.menuLibraryTunnelPermissionOk = false;
                    this.storeHolidayMenuOpen = false;
                });
            }
        } finally {
            if (epoch === this.menuLibraryInitEpoch) {
                this.setLoading(false);
            }
        }
    }

    /**
     * 拉取一级菜单列表
     * @param initEpoch 若由 `init()` 传入，则仅当仍与当前 `menuLibraryInitEpoch` 一致时才写入列表（丢弃过期响应）
     */
    async fetchFirstMenuList(initEpoch?: number) {
        const result = await apiManage_rbp.get_menus_v3(
            {
                type: 0,
                with_dish: false,
                rbpSort: 1,
            },
            this.resolveRbpStringIdForApi(),
        );
        if (initEpoch !== undefined && initEpoch !== this.menuLibraryInitEpoch) {
            return;
        }
        if (_.get(result, 'error')) {
            // 取消请求时勿清空列表（避免切页/卸载后误把界面置空）
            if (_.get(result, 'message') === 'canceled') {
                return;
            }
            runInAction(() => {
                this.menuLibraryList.replace([]);
            });
            return;
        }
        const list = (result as FirstMenu[]) || [];
        const items: MenuLibraryItem[] = list.map((m) => ({
            main_group_id: m.main_group_id as number,
            title: (m.main_group_name as string) || '',
            subtitle: m.last_updated_time ? moment(m.last_updated_time).format('DD/MM/YYYY') : '—',
            main_group_index: m.main_group_index,
        }));
        runInAction(() => {
            this.menuLibraryList.replace(items);
        });
    }

    /**
     * 预取套餐组选菜所需全菜单（与 course-group-dialog 原 `get_menus_v3` 参数一致）。
     * 成功则写入 `courseGroupPickerMenusAll`；失败保持原值（force 时可能短暂为旧树至成功写入）。
     * 打开转移/复制/套餐选菜弹窗走本方法（默认有缓存不重拉）；菜单树变更后用 `force: true` 或 `refreshLocalMenusAfterMutation`。
     * @param initEpoch 由 `init()` 传入时：新 init 会递增 epoch，进行中请求返回后若已过期则丢弃且不误清 loading；无参调用仍用 loading 防重复
     * @param options.force 为 true 时忽略已有缓存并重拉（对齐 RBP2 写成功后 `initMenuList`）
     */
    async ensureCourseGroupPickerMenusLoaded(initEpoch?: number, options?: { force?: boolean }) {
        /** 是否强制重拉（菜单树变更后） */
        const force = options?.force === true;
        if (!force && this.courseGroupPickerMenusAll !== null) {
            return;
        }
        if (initEpoch !== undefined && initEpoch !== this.menuLibraryInitEpoch) {
            return;
        }
        if (!force && initEpoch === undefined && this.courseGroupPickerMenusLoading) {
            return;
        }

        const serial = ++this.coursePickerMenusFetchSerial;
        runInAction(() => {
            this.courseGroupPickerMenusLoading = true;
        });
        try {
            const rbpId = this.resolveRbpStringIdForApi();
            const result = await apiManage_rbp.get_menus_v3(
                {
                    type: 0,
                    with_dish: true,
                    rbpSort: 1,
                },
                rbpId,
            );
            if (initEpoch !== undefined && initEpoch !== this.menuLibraryInitEpoch) {
                return;
            }
            if (serial !== this.coursePickerMenusFetchSerial) {
                return;
            }
            runInAction(() => {
                if (!_.get(result, 'error') && Array.isArray(result)) {
                    this.courseGroupPickerMenusAll = result as FirstMenu[];
                }
            });
        } catch (e) {
            console.error('courseGroupPickerMenus', e);
        } finally {
            if (serial === this.coursePickerMenusFetchSerial) {
                runInAction(() => {
                    this.courseGroupPickerMenusLoading = false;
                });
            }
        }
    }

    /**
     * 预取云端套餐选菜菜单列表骨架（`GET .../menus`，无菜品树）。
     * 点击一级菜单后再 `loadCloudCourseGroupPickerMenuDetail`。
     * @param options.force 为 true 时忽略已有缓存并重拉
     */
    async ensureCloudCourseGroupPickerMenusLoaded(options?: { force?: boolean }) {
        /** 是否强制重拉（云端菜单树变更后） */
        const force = options?.force === true;
        if (!force && this.cloudCourseGroupPickerMenus !== null) {
            return;
        }
        if (!force && this.cloudCourseGroupPickerMenusLoading) {
            return;
        }
        /** force 时抬升序号，丢弃进行中的旧请求结果 */
        const serial = ++this.cloudCoursePickerMenusFetchSerial;
        runInAction(() => {
            this.cloudCourseGroupPickerMenusLoading = true;
            if (force) {
                this.cloudCoursePickerLoadedMenuIds = new Set();
                this.cloudCoursePickerDetailLoadingId = null;
            }
        });
        try {
            const rbpId = this.resolveRbpStringIdForApi();
            const result = await cloudDishLibraryApi.get_cloud_dish_library_menus(rbpId);
            if (serial !== this.cloudCoursePickerMenusFetchSerial) {
                return;
            }
            if (_.get(result, 'error')) {
                return;
            }
            const list = Array.isArray(result) ? result : [];
            runInAction(() => {
                this.cloudCourseGroupPickerMenus = mapCloudMenuListToPickerSkeleton(list);
                this.cloudCoursePickerLoadedMenuIds = new Set();
            });
        } catch (e) {
            console.error('ensureCloudCourseGroupPickerMenusLoaded', e);
        } finally {
            if (serial === this.cloudCoursePickerMenusFetchSerial) {
                runInAction(() => {
                    this.cloudCourseGroupPickerMenusLoading = false;
                });
            }
        }
    }

    /**
     * 本地菜单树/菜品变更成功后：刷新卡片列表，并强制重拉选菜树（对齐 RBP2 `getMenuData`→`initMenuList`）。
     * 打开转移/复制/套餐选菜弹窗本身不单独请求，依赖本方法保持缓存新鲜。
     * @param initEpoch 可选，透传给 `fetchFirstMenuList`
     */
    async refreshLocalMenusAfterMutation(initEpoch?: number) {
        await this.fetchFirstMenuList(initEpoch);
        await this.ensureCourseGroupPickerMenusLoaded(undefined, { force: true });
    }

    /**
     * 云端菜单树/菜品变更成功后：刷新云端卡片列表，并强制重拉套餐选菜骨架。
     * 云端转移/复制弹窗另用 `cloudMenuList`（打开时会 `fetchCloudMenuList`）；本方法保证套餐选菜与列表摘要同步。
     */
    async refreshCloudMenusAfterMutation() {
        await this.fetchCloudMenuList();
        await this.ensureCloudCourseGroupPickerMenusLoaded({ force: true });
    }

    /**
     * 懒加载单个云端菜单详情并 merge 进选菜骨架。
     * @param cloudMenuId - 云端菜单 id
     */
    async loadCloudCourseGroupPickerMenuDetail(cloudMenuId: string) {
        const id = String(cloudMenuId ?? '').trim();
        if (!id) {
            return;
        }
        if (this.cloudCoursePickerLoadedMenuIds.has(id)) {
            return;
        }
        if (this.cloudCoursePickerDetailLoadingId === id) {
            return;
        }
        runInAction(() => {
            this.cloudCoursePickerDetailLoadingId = id;
        });
        try {
            const rbpId = this.resolveRbpStringIdForApi();
            const detail = await cloudDishLibraryApi.get_cloud_dish_library_menu_detail(id, rbpId);
            if (_.get(detail, 'error') || !detail) {
                return;
            }
            runInAction(() => {
                const cur = this.cloudCourseGroupPickerMenus ?? [];
                this.cloudCourseGroupPickerMenus = mergeCloudMenuDetailIntoPickerSkeleton(cur, detail);
                this.cloudCoursePickerLoadedMenuIds.add(id);
            });
        } catch (e) {
            console.error('loadCloudCourseGroupPickerMenuDetail', e);
            GlobalNotification.error(i18n.t('global_text_operate_failure'));
        } finally {
            runInAction(() => {
                if (this.cloudCoursePickerDetailLoadingId === id) {
                    this.cloudCoursePickerDetailLoadingId = null;
                }
            });
        }
    }

    setLoading(loading: boolean) {
        this.isLoading = loading;
    }

    /**
     * 设置菜单库模块内当前餐厅（不修改全局 MainPageState.currentRestaurant）
     * @param restaurant 归属列表中的餐厅项
     */
    setMenuLibraryRestaurant(restaurant: Restaurant) {
        this.menuLibraryRestaurant = restaurant;
    }

    /**
     * 在未选择或所选已不在归属列表时，用全局当前餐厅或列表首项初始化模块餐厅上下文
     * @param globalCurrent 框架全局当前餐厅
     * @param belonged 可切换餐厅列表
     */
    ensureMenuLibraryRestaurantFromScope(globalCurrent: Restaurant, belonged: Restaurant[]) {
        const currentId = this.menuLibraryRestaurant?.string_id;
        if (currentId && belonged.some((r) => r.string_id === currentId)) {
            return;
        }
        const matchGlobal =
            globalCurrent?.string_id != null && globalCurrent.string_id !== ''
                ? belonged.find((r) => r.string_id === globalCurrent.string_id)
                : undefined;
        const pick = matchGlobal ?? belonged[0] ?? null;
        runInAction(() => {
            this.menuLibraryRestaurant = pick;
        });
    }

    /**
     * RBP 餐厅码：与菜单库顶栏门店下拉 `SelectRestaurantList` 的 `value` 一致（仅 `menuLibraryRestaurant.string_id`），
     * 不再回退全局 `getRBPStringId`，避免与模块内所选门店不一致。
     */
    resolveRbpStringIdForApi(): string {
        return this.menuLibraryRestaurant?.string_id || '';
    }

    /** 菜单库模块内所选店货币符号（符号在数字前） */
    getModuleCurrencyName(): string {
        const trimmed = (this.menuLibraryDisplayCurrency || '').trim();
        return trimmed.length > 0 ? trimmed : '€';
    }

    /** 模块内所选店是否意大利（菜品名/助记码意式校验） */
    get isMenuLibraryRestaurantItaly(): boolean {
        return this.menuLibraryRbpCountryName.trim() === 'Italy';
    }

    /**
     * 拉取模块内所选店 RBP 国家与货币（与 App `menuLibraryStore.getRestaurantCountryAndCurrency` 一致）。
     * @param options.initEpoch - 与 `init()` 一致时丢弃过期响应
     * @param options.stringIdOverride - 菜单商店外灌只读等独立 `string_id`
     */
    async fetchMenuLibraryRestaurantContext(options?: {
        initEpoch?: number;
        stringIdOverride?: string;
    }): Promise<void> {
        const sid = (options?.stringIdOverride?.trim() || this.resolveRbpStringIdForApi()).trim();
        if (!sid) {
            return;
        }
        try {
            const [countryRes, currencyRes] = await Promise.all([
                apiManage_rbp.get_restaurant_info_by_string_id(sid),
                apiManage_rbp.get_rbp_settings_currency_by_string_id(sid),
            ]);
            if (
                options?.initEpoch !== undefined &&
                options.initEpoch !== this.menuLibraryInitEpoch
            ) {
                return;
            }
            let country = '';
            let currencyNameLocal = '';
            if (!_.get(countryRes, 'error')) {
                country = String(_.get(countryRes, 'name', '')).trim();
                currencyNameLocal = String(_.get(countryRes, 'currency_name', '')).trim();
            }
            let currencyNameCloud = '';
            if (!_.get(currencyRes, 'error')) {
                if (typeof currencyRes === 'string') {
                    currencyNameCloud = currencyRes.trim();
                } else if (currencyRes != null && typeof currencyRes === 'object') {
                    currencyNameCloud = String(_.get(currencyRes, 'currency_name', '')).trim();
                }
            }
            const displayCurrency = currencyNameLocal || currencyNameCloud || '€';
            runInAction(() => {
                this.menuLibraryRbpCountryName = country;
                this.menuLibraryDisplayCurrency = displayCurrency;
            });
        } catch (error) {
            console.error('fetchMenuLibraryRestaurantContext', error);
        }
    }

    /**
     * 新建弹窗子分类详情：保存新菜品后追加到当前 tempId 下缓存列表
     */
    appendCreateMenuSubcategoryDish(dish: MenuDish) {
        const tid = this.createMenuSubcategoryDetailTempId;
        if (!tid) {
            return;
        }
        const prev = this.createMenuSubcategoryDetailDishesByTempId[tid] ?? [];
        runInAction(() => {
            this.createMenuSubcategoryDetailDishesByTempId = {
                ...this.createMenuSubcategoryDetailDishesByTempId,
                [tid]: [...prev, dish],
            };
        });
    }

    /**
     * 子分类详情菜品列表：按索引替换一行（用于编辑保存后更新缓存）
     */
    replaceCreateMenuSubcategoryDishAtIndex(index: number, dish: MenuDish) {
        const tid = this.createMenuSubcategoryDetailTempId;
        if (!tid) {
            return;
        }
        const prev = this.createMenuSubcategoryDetailDishesByTempId[tid] ?? [];
        if (index < 0 || index >= prev.length) {
            return;
        }
        const next = [...prev];
        next[index] = dish;
        runInAction(() => {
            this.createMenuSubcategoryDetailDishesByTempId = {
                ...this.createMenuSubcategoryDetailDishesByTempId,
                [tid]: next,
            };
        });
    }

    /**
     * 当前「新增/编辑菜品」弹窗若为编辑模式，返回对应行与索引；否则 `null`。
     */
    getCreateMenuSubcategoryAddDishEditTarget(): { dish: MenuDish; index: number } | null {
        const tid = this.createMenuSubcategoryDetailTempId;
        const key = this.createMenuSubcategoryAddDishEditRowKey;
        if (!tid || key == null) {
            return null;
        }
        const list = this.createMenuSubcategoryDetailDishesByTempId[tid] ?? [];
        for (let i = 0; i < list.length; i++) {
            if (createMenuSubcategoryDishRowKey(list[i], i) === key) {
                return { dish: list[i], index: i };
            }
        }
        return null;
    }

    /**
     * 「新增/编辑菜品」弹窗回填目标：优先新建弹窗子分类草稿行，否则菜单库详情当前二级菜单下按 `item_id` 查找。
     * 新增刚落库时列表可能尚未同步：会全量扫一级菜单下各二级菜单，并可用 post 回退对象。
     */
    /**
     * 批量菜品设置：当前勾选且已落库的菜品行（新建子分类详情或菜单库详情）。
     */
    getBatchDishSettingsTargets(): { dish: MenuDish; index: number; rowKey: string }[] {
        if (this.menuLibraryAddDishDialogMode !== 'batch') {
            return [];
        }
        const keySet = new Set(this.batchDishSettingsRowKeys);
        const tid = this.createMenuSubcategoryDetailTempId;

        /** 云端详情：`batchDishSettingsRowKeys` 为云端菜品 string id */
        if (tid == null && this.isCloudMenuDetail && this.detailCloudMenuId) {
            const list = this.getFilteredDishes();
            const out: { dish: MenuDish; index: number; rowKey: string }[] = [];
            for (let i = 0; i < list.length; i++) {
                const d = list[i];
                const cloudId = getCloudDishIdFromMenuDish(d);
                if (!cloudId || !keySet.has(cloudId)) {
                    continue;
                }
                out.push({ dish: d, index: i, rowKey: cloudId });
            }
            return out;
        }

        /** 本地菜单库详情：`batchDishSettingsRowKeys` 为 `id:${item_id}`，数据来自 `getFilteredDishes` */
        if (tid == null && this.detailMainGroupId) {
            const list = this.getFilteredDishes();
            const out: { dish: MenuDish; index: number; rowKey: string }[] = [];
            for (let i = 0; i < list.length; i++) {
                const d = list[i];
                if (d.item_id == null || d.slu_id == null) {
                    continue;
                }
                const k = `id:${d.item_id}`;
                if (keySet.has(k)) {
                    out.push({ dish: d, index: i, rowKey: k });
                }
            }
            return out;
        }

        if (!tid) {
            return [];
        }
        const list = this.createMenuSubcategoryDetailDishesByTempId[tid] ?? [];
        const out: { dish: MenuDish; index: number; rowKey: string }[] = [];
        for (let i = 0; i < list.length; i++) {
            const k = createMenuSubcategoryDishRowKey(list[i], i);
            if (keySet.has(k) && list[i].item_id != null && list[i].slu_id != null) {
                out.push({ dish: list[i], index: i, rowKey: k });
            }
        }
        return out;
    }

    getAddDishEditTarget(): { dish: MenuDish; index: number } | null {
        const draft = this.getCreateMenuSubcategoryAddDishEditTarget();
        if (draft != null) {
            return draft;
        }
        const fb = this.menuLibraryDetailPostSavedDishFallback;
        /** 云端详情：按云端菜品 string `id` 查找（勿用可能重复的 item_id=0） */
        if (this.isCloudMenuDetail) {
            const cloudId = this.menuLibraryDetailAddDishEditCloudDishId;
            if (cloudId) {
                const matchCloudId = (d: MenuDish) => getCloudDishIdFromMenuDish(d) === cloudId;
                const sm = this.getCurrentSecondMenu();
                const items = sm?.menu_item ?? [];
                const idx = items.findIndex(matchCloudId);
                if (idx >= 0) {
                    return { dish: items[idx], index: idx };
                }
                const fm = this.detailFirstMenu;
                if (fm?.menu_item_slu?.length) {
                    for (const slu of fm.menu_item_slu) {
                        const mi = slu.menu_item ?? [];
                        const j = mi.findIndex(matchCloudId);
                        if (j >= 0) {
                            return { dish: mi[j], index: j };
                        }
                    }
                }
                if (fb != null && matchCloudId(fb)) {
                    return { dish: fb, index: -1 };
                }
            }
            if (fb != null) {
                return { dish: fb, index: -1 };
            }
            return null;
        }
        const itemId = this.menuLibraryDetailAddDishEditItemId;
        /** 云端详情：打开时可无本地数字 `item_id`，用打开时快照回填 */
        if (itemId == null) {
            return null;
        }
        const idNum = Number(itemId);
        const sm = this.getCurrentSecondMenu();
        const items = sm?.menu_item ?? [];
        const idx = items.findIndex((d) => Number(d.item_id) === idNum);
        if (idx >= 0) {
            return { dish: items[idx], index: idx };
        }
        const fm = this.detailFirstMenu;
        if (fm?.menu_item_slu?.length) {
            for (const slu of fm.menu_item_slu) {
                const mi = slu.menu_item ?? [];
                const j = mi.findIndex((d) => Number(d.item_id) === idNum);
                if (j >= 0) {
                    return { dish: mi[j], index: j };
                }
            }
        }
        if (fb != null && Number(fb.item_id) === idNum) {
            return { dish: fb, index: -1 };
        }
        return null;
    }

    /**
     * 套餐组弹窗用的 `item_id`：`getAddDishEditTarget` 依赖列表已同步；首次保存后若接口列表尚未包含新菜会暂时为 `null`，
     * 此时回退 `menuLibraryDetailAddDishEditItemId` 或子分类行 key `id:n`。
     */
    getResolvedCourseGroupDishItemId(): number {
        const t = this.getAddDishEditTarget();
        const fromTarget = t?.dish?.item_id;
        if (fromTarget != null && fromTarget > 0) {
            return fromTarget;
        }
        if (this.menuLibraryDetailAddDishEditItemId != null && this.menuLibraryDetailAddDishEditItemId > 0) {
            return this.menuLibraryDetailAddDishEditItemId;
        }
        const key = this.createMenuSubcategoryAddDishEditRowKey;
        if (key?.startsWith('id:')) {
            const n = parseInt(key.slice(3), 10);
            if (!Number.isNaN(n) && n > 0) {
                return n;
            }
        }
        return 0;
    }

    /**
     * 新增/编辑菜品弹窗：删除当前编辑中的已落库菜品（草稿列表或菜单库详情列表）
     */
    async deleteAddDishEditingItem() {
        /** 云端详情：走 `dishes/delete`，禁止本地 `delete_dishes` */
        if (this.isCloudMenuDetail) {
            const cloudMenuId = this.detailCloudMenuId;
            const cloudDishId =
                this.menuLibraryDetailAddDishEditCloudDishId ??
                getCloudDishIdFromMenuDish(this.getAddDishEditTarget()?.dish);
            if (!cloudMenuId || !cloudDishId) {
                GlobalNotification.error(i18n.t('global_text_operate_failure'));
                return;
            }
            const sid = this.resolveRbpStringIdForApi().trim();
            if (!sid) {
                GlobalNotification.error(i18n.t('business_analysis_page_none_restaurant'));
                return;
            }
            setLoading(true);
            try {
                const r = await cloudDishLibraryApi.post_cloud_dish_library_dishes_delete(
                    cloudMenuId,
                    { cloud_dish_ids: [cloudDishId] },
                    sid,
                );
                if (_.get(r, 'error')) {
                    GlobalNotification.error(i18n.t('global_text_operate_failure'));
                    return;
                }
                if ((r.deleted_dish_count ?? 0) <= 0) {
                    GlobalNotification.error(i18n.t('global_text_operate_failure'));
                    return;
                }
                this.closeCreateMenuSubcategoryAddDishDialog();
                GlobalNotification.success(i18n.t('global_text_operate_success'));
                await this.loadCloudMenuDetailData(cloudMenuId);
                await this.refreshCloudMenusAfterMutation();
            } catch (e) {
                console.error('deleteAddDishEditingItem(cloud)', e);
                GlobalNotification.error(i18n.t('global_text_operate_failure'));
            } finally {
                setLoading(false);
            }
            return;
        }
        if (this.notifyCloudDetailApiNotReady()) {
            return;
        }
        const target = this.getAddDishEditTarget();
        const itemId = target?.dish.item_id;
        if (itemId == null || target == null) {
            return;
        }
        const tid = this.createMenuSubcategoryDetailTempId;
        if (tid) {
            const key = createMenuSubcategoryDishRowKey(target.dish, target.index);
            await this.deleteCreateMenuSubcategoryDishesByRowKeys([key]);
            this.closeCreateMenuSubcategoryAddDishDialog();
            return;
        }
        const sluId = target.dish.slu_id ?? this.getCurrentSecondMenu()?.dmi_slu_id;
        if (sluId == null) {
            GlobalNotification.error(i18n.t('global_text_operate_failure'));
            return;
        }
        /** 关弹窗前快照，供删除 restaurant-static 菜品图解析文件名 */
        const dishImageRefForStaticDelete = target.dish.dish_image;
        setLoading(true);
        try {
            this.closeCreateMenuSubcategoryAddDishDialog();
            const r = await apiManage_rbp.delete_dishes([{ item_id: itemId, slu_id: sluId }], this.resolveRbpStringIdForApi());
            if (_.get(r, 'error')) {
                GlobalNotification.error(i18n.t('global_text_operate_failure'));
                return;
            }
            GlobalNotification.success(i18n.t('global_text_operate_success'));
            const delFn = extractFileNameForRestaurantStaticDelete(dishImageRefForStaticDelete);
            if (delFn) {
                const delImgRes = await apiManage.delete_restaurant_static_dish_image(this.resolveRbpStringIdForApi(), delFn);
                if (_.get(delImgRes, 'error')) {
                    console.error('deleteAddDishEditingItem: delete_restaurant_static_dish_image', delImgRes);
                }
            }
            const mainId = this.detailFirstMenu?.main_group_id;
            if (mainId != null) {
                await this.loadDetailData(mainId);
                await this.refreshLocalMenusAfterMutation();
            }
        } catch (e) {
            console.error('deleteAddDishEditingItem', e);
            GlobalNotification.error(i18n.t('global_text_operate_failure'));
        } finally {
            setLoading(false);
        }
    }

    /**
     * 新建弹窗子分类详情：按行 key 批量删除；已落库的菜品先调 `delete_dishes` 再从前端列表移除。
     */
    async deleteCreateMenuSubcategoryDishesByRowKeys(keys: string[]): Promise<void> {
        if (this.notifyCloudCreateWriteApiNotReady()) {
            return;
        }
        const tid = this.createMenuSubcategoryDetailTempId;
        if (!tid || keys.length === 0) {
            return;
        }
        const prev = this.createMenuSubcategoryDetailDishesByTempId[tid] ?? [];
        const keyOf = (d: MenuDish, i: number) => createMenuSubcategoryDishRowKey(d, i);
        const payload: { item_id: number; slu_id: number }[] = [];
        prev.forEach((d, i) => {
            if (!keys.includes(keyOf(d, i))) {
                return;
            }
            if (d.item_id != null && d.slu_id != null) {
                payload.push({ item_id: d.item_id, slu_id: d.slu_id });
            }
        });
        try {
            if (payload.length > 0) {
                setLoading(true);
                const r = await apiManage_rbp.delete_dishes(payload, this.resolveRbpStringIdForApi());
                if (_.get(r, 'error')) {
                    GlobalNotification.error(i18n.t('global_text_operate_failure'));
                    return;
                }
            }
            runInAction(() => {
                const next = prev.filter((d, i) => !keys.includes(keyOf(d, i)));
                this.createMenuSubcategoryDetailDishesByTempId = {
                    ...this.createMenuSubcategoryDetailDishesByTempId,
                    [tid]: next,
                };
            });
            GlobalNotification.success(i18n.t('global_text_operate_success'));
        } catch {
            GlobalNotification.error(i18n.t('global_text_operate_failure'));
        } finally {
            setLoading(false);
        }
    }

    /**
     * 新建弹窗子分类详情：批量转移已保存菜品至目标二级菜单（与制作器二级菜单 `onChangeDishesTransfer` / `patch_dishes` 一致）。
     */
    async batchTransferCreateMenuSubcategoryDishesByRowKeys(
        rowKeys: string[],
        targetSecondMenu: SecondMenu,
    ): Promise<boolean> {
        if (this.notifyCloudCreateWriteApiNotReady()) {
            return false;
        }
        const tid = this.createMenuSubcategoryDetailTempId;
        const targetSluId = targetSecondMenu.dmi_slu_id;
        if (!tid || rowKeys.length === 0 || targetSluId == null) {
            return false;
        }
        const prev = this.createMenuSubcategoryDetailDishesByTempId[tid] ?? [];
        const keyOf = (d: MenuDish, i: number) => createMenuSubcategoryDishRowKey(d, i);
        const keySet = new Set(rowKeys);
        const toTransferKeys = new Set<string>();
        let skipped = 0;
        prev.forEach((d, i) => {
            const k = keyOf(d, i);
            if (!keySet.has(k)) {
                return;
            }
            if (d.item_id == null) {
                skipped++;
                return;
            }
            toTransferKeys.add(k);
        });
        if (toTransferKeys.size === 0) {
            GlobalNotification.info(i18n.t('menu_library_create_dialog_batch_no_saved_dishes'));
            return false;
        }
        const notJoining = targetSecondMenu.not_joining_global_limit ?? 0;
        const params: PatchDishesParams[] = [...toTransferKeys].map((rk) => {
            const idx = prev.findIndex((d, i) => keyOf(d, i) === rk);
            const d = prev[idx];
            return {
                item_id: d.item_id as number,
                slu_id: targetSluId,
                not_joining_global_limit: notJoining,
            } as PatchDishesParams;
        });
        const rbpId = this.resolveRbpStringIdForApi();
        try {
            setLoading(true);
            const r = await apiManage_rbp.patch_dishes(params, undefined, rbpId);
            if (_.get(r, 'error')) {
                GlobalNotification.error(i18n.t('global_text_operate_failure'));
                return false;
            }
            runInAction(() => {
                const next = prev.filter((d, i) => !toTransferKeys.has(keyOf(d, i)));
                this.createMenuSubcategoryDetailDishesByTempId = {
                    ...this.createMenuSubcategoryDetailDishesByTempId,
                    [tid]: next,
                };
            });
            GlobalNotification.success(i18n.t('global_text_operate_success'));
            if (skipped > 0) {
                GlobalNotification.info(i18n.t('menu_library_create_dialog_batch_skipped_unsaved', { count: skipped }));
            }
            return true;
        } catch (e) {
            console.error('batchTransferCreateMenuSubcategoryDishesByRowKeys', e);
            GlobalNotification.error(i18n.t('global_text_operate_failure'));
            return false;
        } finally {
            setLoading(false);
        }
    }

    /**
     * 新建弹窗子分类详情：批量复制已保存菜品到目标二级菜单（与制作器 `patch_copy_dishes` 一致，并同步目标子类轮次限制）。
     */
    async batchCopyCreateMenuSubcategoryDishesByRowKeys(
        rowKeys: string[],
        targetSecondMenu: SecondMenu,
    ): Promise<boolean> {
        if (this.notifyCloudCreateWriteApiNotReady()) {
            return false;
        }
        const tid = this.createMenuSubcategoryDetailTempId;
        const targetSluId = targetSecondMenu.dmi_slu_id;
        if (!tid || rowKeys.length === 0 || targetSluId == null) {
            return false;
        }
        const prev = this.createMenuSubcategoryDetailDishesByTempId[tid] ?? [];
        const keyOf = (d: MenuDish, i: number) => createMenuSubcategoryDishRowKey(d, i);
        const keySet = new Set(rowKeys);
        const dishIds: number[] = [];
        let skipped = 0;
        prev.forEach((d, i) => {
            const k = keyOf(d, i);
            if (!keySet.has(k)) {
                return;
            }
            if (d.item_id == null) {
                skipped++;
            } else {
                dishIds.push(d.item_id);
            }
        });
        if (dishIds.length === 0) {
            GlobalNotification.info(i18n.t('menu_library_create_dialog_batch_no_saved_dishes'));
            return false;
        }
        const rbpId = this.resolveRbpStringIdForApi();
        try {
            setLoading(true);
            const r = await apiManage_rbp.patch_copy_dishes(dishIds, [targetSluId], rbpId);
            if (_.get(r, 'error')) {
                GlobalNotification.error(i18n.t('global_text_operate_failure'));
                return false;
            }
            const insertItems = (r as { insert_menu_item?: Array<{ item_id?: number }> }).insert_menu_item;
            if (insertItems?.length) {
                const limitParams: PatchDishesParams[] = insertItems.map((item) => ({
                    item_id: item.item_id as number,
                    not_joining_global_limit: targetSecondMenu.not_joining_global_limit || 0,
                })) as PatchDishesParams[];
                await apiManage_rbp.patch_dishes(limitParams, true, rbpId);
            }
            GlobalNotification.success(i18n.t('global_text_operate_success'));
            if (skipped > 0) {
                GlobalNotification.info(i18n.t('menu_library_create_dialog_batch_skipped_unsaved', { count: skipped }));
            }
            return true;
        } catch (e) {
            console.error('batchCopyCreateMenuSubcategoryDishesByRowKeys', e);
            GlobalNotification.error(i18n.t('global_text_operate_failure'));
            return false;
        } finally {
            setLoading(false);
        }
    }

    /**
     * 切换列表 Tab（本地 / 云端）；切到云端时退出上传多选并**直接**拉取云端列表（不依赖隧道门禁）。
     * @param tab - 目标 Tab
     */
    setMenuLibraryListTab(tab: 'local' | 'cloud') {
        this.menuLibraryListTab = tab;
        if (tab === 'cloud') {
            this.cancelUploadToCloudSelectMode();
            void this.fetchCloudMenuList();
        } else {
            this.cancelCloudMenuDeleteSelectMode();
        }
    }

    /**
     * 进入「上传至云端」多选模式，并尽量预取含菜品菜单树以便统计菜品数。
     */
    enterUploadToCloudSelectMode() {
        this.isUploadToCloudSelectMode = true;
        this.selectedUploadMainGroupIds = [];
        void this.ensureCourseGroupPickerMenusLoaded();
    }

    /**
     * 退出上传多选并清空选中项（不改动 `uploadToCloudSubmitting`，避免与进行中请求抢状态）。
     * 保留已打开的上传结果浮层，由 {@link closeUploadToCloudResult} 关闭。
     */
    exitUploadToCloudSelectMode() {
        this.isUploadToCloudSelectMode = false;
        this.selectedUploadMainGroupIds = [];
    }

    /**
     * 用户取消多选或切 Tab：退出多选；若正在上传则作废进行中请求并关掉进度浮层与结果。
     */
    cancelUploadToCloudSelectMode() {
        if (this.uploadToCloudSubmitting) {
            this.uploadToCloudSerial += 1;
            this.uploadToCloudSubmitting = false;
        }
        this.uploadToCloudResult = null;
        this.exitUploadToCloudSelectMode();
    }

    /**
     * 关闭上传成功结果浮层。
     */
    closeUploadToCloudResult() {
        this.uploadToCloudResult = null;
    }

    /**
     * 判断某本地一级菜单是否已勾选待上传。
     * @param mainGroupId - 一级菜单 id
     */
    isUploadMainGroupSelected(mainGroupId: number): boolean {
        return this.selectedUploadMainGroupIds.includes(mainGroupId);
    }

    /**
     * 切换某本地一级菜单的上传勾选状态。
     * @param mainGroupId - 一级菜单 id
     */
    toggleUploadMainGroupSelection(mainGroupId: number) {
        if (!this.isUploadToCloudSelectMode) {
            return;
        }
        if (this.selectedUploadMainGroupIds.includes(mainGroupId)) {
            this.selectedUploadMainGroupIds = this.selectedUploadMainGroupIds.filter((id) => id !== mainGroupId);
        } else {
            this.selectedUploadMainGroupIds = [...this.selectedUploadMainGroupIds, mainGroupId];
        }
    }

    /**
     * 当前勾选待上传的菜单份数。
     */
    get selectedUploadMenuCount(): number {
        return this.selectedUploadMainGroupIds.length;
    }

    /**
     * 当前勾选菜单下的菜品总数（优先用已预取的 `courseGroupPickerMenusAll`；未就绪则为 0）。
     */
    get selectedUploadDishCount(): number {
        const menus = this.courseGroupPickerMenusAll;
        if (!menus?.length || this.selectedUploadMainGroupIds.length === 0) {
            return 0;
        }
        // 已选菜单 id 集合，便于 O(1) 判断
        const selected = new Set(this.selectedUploadMainGroupIds);
        let dishCount = 0;
        for (const fm of menus) {
            if (fm.main_group_id == null || !selected.has(fm.main_group_id)) {
                continue;
            }
            for (const slu of fm.menu_item_slu ?? []) {
                dishCount += slu.menu_item?.length ?? 0;
            }
        }
        return dishCount;
    }

    /**
     * 确认上传至云端：调用 upload API；提交中展示「菜单上传中」浮层，成功后展示结果浮层并退出多选。
     */
    async confirmUploadToCloud() {
        if (this.selectedUploadMenuCount <= 0) {
            GlobalNotification.info(i18n.t('menu_library_upload_cloud_select_at_least_one'));
            return;
        }
        const sid = this.resolveRbpStringIdForApi().trim();
        if (!sid) {
            GlobalNotification.error(i18n.t('business_analysis_page_none_restaurant'));
            return;
        }
        if (this.uploadToCloudSubmitting) {
            return;
        }
        // 待上传的本地一级菜单 id
        const mainGroupIds = [...this.selectedUploadMainGroupIds];
        // 成功浮层列表：本地卡片 title + subtitle（last_updated_time 已格式化为 DD/MM/YYYY）
        const resultItems = mainGroupIds.map((id) => {
            const card = this.menuLibraryList.find((m) => m.main_group_id === id);
            return {
                primary: card?.title?.trim() || '—',
                secondary: card?.subtitle && card.subtitle !== '—' ? card.subtitle : undefined,
            };
        });
        // 本轮请求代次；取消 / 切 Tab / init 会递增以丢弃回写
        const serial = ++this.uploadToCloudSerial;
        this.uploadToCloudResult = null;
        this.uploadToCloudSubmitting = true;
        try {
            const r = await apiManage_rbp.post_cloud_dish_library_menus_upload(
                { main_group_ids: mainGroupIds },
                sid,
            );
            if (serial !== this.uploadToCloudSerial) {
                return;
            }
            if (_.get(r, 'error')) {
                GlobalNotification.error(i18n.t('global_text_operate_failure'));
                return;
            }
            const parsed = normalizeCloudUploadResult(r);
            // 标题份数与紫条列表对齐：优先接口 uploaded_count，再按本地快照截断/兜底
            const uploaded = parsed?.uploaded_count ?? resultItems.length;
            const items =
                uploaded < resultItems.length
                    ? resultItems.slice(0, uploaded)
                    : resultItems;
            const menuCount = items.length;
            runInAction(() => {
                if (serial !== this.uploadToCloudSerial) {
                    return;
                }
                this.uploadToCloudResult = {
                    menuCount,
                    items,
                };
                this.exitUploadToCloudSelectMode();
                // 使云端列表缓存失效：本地 Tab 上传后进入加载弹窗也能看到新菜单
                this.cloudMenuList = [];
            });
            if (serial === this.uploadToCloudSerial && this.menuLibraryListTab === 'cloud') {
                void this.refreshCloudMenusAfterMutation();
            }
        } catch (e) {
            if (serial !== this.uploadToCloudSerial) {
                return;
            }
            console.error('confirmUploadToCloud', e);
            GlobalNotification.error(i18n.t('global_text_operate_failure'));
        } finally {
            runInAction(() => {
                if (serial === this.uploadToCloudSerial) {
                    this.uploadToCloudSubmitting = false;
                }
            });
        }
    }

    /**
     * 拉取当前餐厅云端菜单列表（不依赖隧道/版本门禁；云端 API 走云端契约）。
     */
    async fetchCloudMenuList() {
        const sid = this.resolveRbpStringIdForApi().trim();
        if (!sid) {
            runInAction(() => {
                this.cloudMenuList = [];
                this.cloudMenuListLoading = false;
            });
            return;
        }
        this.cloudMenuListLoading = true;
        try {
            const r = await apiManage_rbp.get_cloud_dish_library_menus(sid);
            if (_.get(r, 'error')) {
                GlobalNotification.error(i18n.t('global_text_operate_failure'));
                runInAction(() => {
                    this.cloudMenuList = [];
                });
                return;
            }
            runInAction(() => {
                this.cloudMenuList = normalizeCloudMenuListResponse(r);
            });
        } catch (e) {
            console.error('fetchCloudMenuList', e);
            GlobalNotification.error(i18n.t('global_text_operate_failure'));
            runInAction(() => {
                this.cloudMenuList = [];
            });
        } finally {
            runInAction(() => {
                this.cloudMenuListLoading = false;
            });
        }
    }

    /**
     * 进入云端删除多选模式。
     */
    enterCloudMenuDeleteSelectMode() {
        this.isCloudMenuDeleteSelectMode = true;
        this.selectedCloudMenuIds = [];
    }

    /**
     * 退出云端删除多选并清空选中项（不改动 `cloudMenuDeleteSubmitting`，避免与进行中请求抢状态）。
     * 保留已打开的删除结果浮层，由 {@link closeCloudMenuDeleteResult} 关闭。
     */
    exitCloudMenuDeleteSelectMode() {
        this.isCloudMenuDeleteSelectMode = false;
        this.selectedCloudMenuIds = [];
        this.isCloudMenuDeleteConfirmOpen = false;
    }

    /**
     * 用户取消多选或切 Tab：退出多选；若正在删除则作废进行中请求并关掉二次确认、进度与结果。
     */
    cancelCloudMenuDeleteSelectMode() {
        if (this.cloudMenuDeleteSubmitting) {
            this.cloudMenuDeleteSerial += 1;
            this.cloudMenuDeleteSubmitting = false;
        }
        this.isCloudMenuDeleteConfirmOpen = false;
        this.cloudMenuDeleteResult = null;
        this.exitCloudMenuDeleteSelectMode();
    }

    /**
     * 打开删除二次确认弹窗（底部确认条「确定」）。
     */
    openCloudMenuDeleteConfirmDialog() {
        if (this.selectedCloudMenuCount <= 0) {
            GlobalNotification.info(i18n.t('menu_library_cloud_delete_select_at_least_one'));
            return;
        }
        if (this.cloudMenuDeleteSubmitting) {
            return;
        }
        this.isCloudMenuDeleteConfirmOpen = true;
    }

    /**
     * 关闭删除二次确认弹窗。
     */
    closeCloudMenuDeleteConfirmDialog() {
        this.isCloudMenuDeleteConfirmOpen = false;
    }

    /**
     * 关闭删除成功结果浮层。
     */
    closeCloudMenuDeleteResult() {
        this.cloudMenuDeleteResult = null;
    }

    /**
     * 判断云端菜单是否已勾选待删除。
     * @param cloudMenuId - 云端菜单 id
     */
    isCloudMenuSelected(cloudMenuId: string): boolean {
        return this.selectedCloudMenuIds.includes(cloudMenuId);
    }

    /**
     * 切换云端菜单删除勾选。
     * @param cloudMenuId - 云端菜单 id
     */
    toggleCloudMenuSelection(cloudMenuId: string) {
        if (!this.isCloudMenuDeleteSelectMode) {
            return;
        }
        if (this.selectedCloudMenuIds.includes(cloudMenuId)) {
            this.selectedCloudMenuIds = this.selectedCloudMenuIds.filter((id) => id !== cloudMenuId);
        } else {
            this.selectedCloudMenuIds = [...this.selectedCloudMenuIds, cloudMenuId];
        }
    }

    /**
     * 已选待删除云端菜单份数。
     */
    get selectedCloudMenuCount(): number {
        return this.selectedCloudMenuIds.length;
    }

    /**
     * 已选云端菜单菜品总数（删除确认文案）。
     */
    get selectedCloudDishCount(): number {
        return sumSelectedCloudDishCount(this.cloudMenuList, this.selectedCloudMenuIds);
    }

    /**
     * 云端卡片副标题（日期）。
     * @param menu - 云端菜单摘要
     */
    getCloudMenuCardSubtitle(menu: CloudDishLibraryMenuSummary): string {
        return formatCloudMenuCardSubtitle(menu);
    }

    /**
     * 二次确认后删除已选云端菜单：展示「菜单删除中」浮层，成功后展示结果浮层并退出多选。
     */
    async confirmDeleteCloudMenus() {
        if (this.selectedCloudMenuCount <= 0) {
            GlobalNotification.info(i18n.t('menu_library_cloud_delete_select_at_least_one'));
            return;
        }
        const sid = this.resolveRbpStringIdForApi().trim();
        if (!sid) {
            GlobalNotification.error(i18n.t('business_analysis_page_none_restaurant'));
            return;
        }
        if (this.cloudMenuDeleteSubmitting) {
            return;
        }
        // 待删除的云端菜单 id
        const ids = [...this.selectedCloudMenuIds];
        // 成功浮层列表：云端卡片名 + 日期副标题
        const resultItems = ids.map((id) => {
            const card = this.cloudMenuList.find((m) => m.id === id);
            const secondary = card ? formatCloudMenuCardSubtitle(card) : undefined;
            return {
                primary: card?.main_group_name?.trim() || '—',
                secondary: secondary && secondary !== '—' ? secondary : undefined,
            };
        });
        // 本轮请求代次；取消 / 切 Tab / init 会递增以丢弃回写
        const serial = ++this.cloudMenuDeleteSerial;
        this.isCloudMenuDeleteConfirmOpen = false;
        this.cloudMenuDeleteResult = null;
        this.cloudMenuDeleteSubmitting = true;
        try {
            const r = await apiManage_rbp.post_cloud_dish_library_menus_delete(
                { cloud_menu_ids: ids },
                sid,
            );
            if (serial !== this.cloudMenuDeleteSerial) {
                return;
            }
            if (_.get(r, 'error')) {
                GlobalNotification.error(i18n.t('global_text_operate_failure'));
                return;
            }
            const parsed = normalizeCloudDeleteResult(r);
            // 未找到的 id：结果列表排除，避免 slice 份数时把未删项显示为已删
            const notFoundSet = new Set(parsed?.not_found_ids ?? []);
            const deletedItems = resultItems.filter((_, index) => !notFoundSet.has(ids[index]));
            // 标题份数：优先接口 deleted_menu_count，再与过滤后列表对齐
            const deletedCount = parsed?.deleted_menu_count ?? deletedItems.length;
            const items =
                deletedCount < deletedItems.length
                    ? deletedItems.slice(0, deletedCount)
                    : deletedItems;
            const menuCount = items.length;
            // 部分 id 未找到时补充提示
            if (notFoundSet.size > 0) {
                GlobalNotification.info(
                    i18n.t('menu_library_cloud_delete_partial_not_found', {
                        count: notFoundSet.size,
                    }),
                );
            }
            // 全部未删成功：失败 Toast，保留多选便于重试，不弹「0 份成功」结果
            if (menuCount <= 0) {
                GlobalNotification.error(i18n.t('global_text_operate_failure'));
                return;
            }
            runInAction(() => {
                if (serial !== this.cloudMenuDeleteSerial) {
                    return;
                }
                this.cloudMenuDeleteResult = {
                    menuCount,
                    items,
                };
                this.exitCloudMenuDeleteSelectMode();
            });
            if (serial === this.cloudMenuDeleteSerial) {
                await this.refreshCloudMenusAfterMutation();
            }
        } catch (e) {
            if (serial !== this.cloudMenuDeleteSerial) {
                return;
            }
            console.error('confirmDeleteCloudMenus', e);
            GlobalNotification.error(i18n.t('global_text_operate_failure'));
        } finally {
            runInAction(() => {
                if (serial === this.cloudMenuDeleteSerial) {
                    this.cloudMenuDeleteSubmitting = false;
                }
            });
        }
    }

    /**
     * 打开「加载云端菜品」弹窗；若无云端列表则先拉取。
     */
    async openLoadCloudDishDialog() {
        if (this.notifyCloudDetailApiNotReady()) {
            return;
        }
        if (!this.detailMainGroupId) {
            return;
        }
        if (this.selectedSecondSluId == null) {
            GlobalNotification.info(i18n.t('menu_library_load_cloud_need_local_subcategory'));
            return;
        }
        this.isLoadCloudDishDialogOpen = true;
        this.loadCloudSelectedDishIds = [];
        this.loadCloudDishSearchKeyword = '';
        this.loadCloudSelectedCategoryId = null;
        this.loadCloudAddResult = null;
        // 每次打开强制刷新云端列表，避免本地上传后仍用旧缓存
        await this.fetchCloudMenuList();
        // 默认选中第一份云端菜单
        const firstId = this.cloudMenuList[0]?.id ?? null;
        this.loadCloudSelectedMenuId = firstId;
        if (firstId) {
            await this.fetchLoadCloudMenuDetail(firstId);
        } else {
            this.loadCloudMenuDetailRaw = null;
        }
    }

    /**
     * 关闭加载云端菜品弹窗并清空临时状态。
     */
    closeLoadCloudDishDialog() {
        this.isLoadCloudDishDialogOpen = false;
        this.loadCloudSelectedMenuId = null;
        this.loadCloudMenuDetailRaw = null;
        this.loadCloudMenuDetailLoading = false;
        this.loadCloudSelectedDishIds = [];
        this.loadCloudDishSearchKeyword = '';
        this.loadCloudSelectedCategoryId = null;
        this.loadCloudAddDishesSubmitting = false;
        this.isLoadCloudConfirmOpen = false;
        this.loadCloudAddResult = null;
    }

    /**
     * 打开二次确认弹窗（不发起 add-dishes）。
     */
    openLoadCloudConfirmDialog() {
        if (this.loadCloudSelectedDishIds.length <= 0) {
            GlobalNotification.info(i18n.t('menu_library_load_cloud_select_dishes_first'));
            return;
        }
        if (this.selectedSecondSluId == null) {
            GlobalNotification.info(i18n.t('menu_library_load_cloud_need_local_subcategory'));
            return;
        }
        this.isLoadCloudConfirmOpen = true;
    }

    /**
     * 关闭二次确认弹窗。
     */
    closeLoadCloudConfirmDialog() {
        if (this.loadCloudAddDishesSubmitting) {
            return;
        }
        this.isLoadCloudConfirmOpen = false;
    }

    /**
     * 关闭加载成功浮层；同时关闭主加载弹窗并清空勾选。
     */
    closeLoadCloudAddResultAndDialog() {
        this.loadCloudAddResult = null;
        this.closeLoadCloudDishDialog();
    }

    /**
     * 加载弹窗切换云端菜单并拉详情。
     * @param cloudMenuId - 云端菜单 id
     */
    async setLoadCloudSelectedMenuId(cloudMenuId: string) {
        if (this.loadCloudSelectedMenuId === cloudMenuId) {
            return;
        }
        this.loadCloudSelectedMenuId = cloudMenuId;
        this.loadCloudSelectedDishIds = [];
        this.loadCloudSelectedCategoryId = null;
        await this.fetchLoadCloudMenuDetail(cloudMenuId);
    }

    /**
     * 拉取云端菜单详情（加载弹窗）。
     * @param cloudMenuId - 云端菜单 id
     */
    async fetchLoadCloudMenuDetail(cloudMenuId: string) {
        const sid = this.resolveRbpStringIdForApi().trim();
        if (!sid || !cloudMenuId) {
            return;
        }
        this.loadCloudMenuDetailLoading = true;
        try {
            const r = await apiManage_rbp.get_cloud_dish_library_menu_detail(cloudMenuId, sid);
            if (_.get(r, 'error')) {
                GlobalNotification.error(i18n.t('global_text_operate_failure'));
                runInAction(() => {
                    this.loadCloudMenuDetailRaw = null;
                });
                return;
            }
            runInAction(() => {
                this.loadCloudMenuDetailRaw = r;
                // 尝试默认选中第一个小类
                const cats = extractCloudDetailCategories(r);
                this.loadCloudSelectedCategoryId = cats[0]?.id ?? null;
            });
        } catch (e) {
            console.error('fetchLoadCloudMenuDetail', e);
            GlobalNotification.error(i18n.t('global_text_operate_failure'));
            runInAction(() => {
                this.loadCloudMenuDetailRaw = null;
            });
        } finally {
            runInAction(() => {
                this.loadCloudMenuDetailLoading = false;
            });
        }
    }

    /**
     * 设置加载弹窗搜索关键字。
     * @param keyword - 名称或编码
     */
    setLoadCloudDishSearchKeyword(keyword: string) {
        this.loadCloudDishSearchKeyword = keyword;
    }

    /**
     * 切换加载弹窗云端小类（仅切换右侧列表）。
     * @param categoryId - 小类 id
     */
    setLoadCloudSelectedCategoryId(categoryId: string | null) {
        this.loadCloudSelectedCategoryId = categoryId;
        this.loadCloudSelectedDishIds = [];
    }

    /**
     * 切换云端菜品勾选。
     * @param dishId - 云端菜品 id
     */
    toggleLoadCloudDishSelection(dishId: string) {
        if (this.loadCloudSelectedDishIds.includes(dishId)) {
            this.loadCloudSelectedDishIds = this.loadCloudSelectedDishIds.filter((id) => id !== dishId);
        } else {
            this.loadCloudSelectedDishIds = [...this.loadCloudSelectedDishIds, dishId];
        }
    }

    /**
     * 全选 / 取消全选当前可见云端菜品。
     * @param dishIds - 当前页可见 id
     * @param selectAll - true 全选，false 取消这些 id
     */
    setLoadCloudDishSelectionForIds(dishIds: string[], selectAll: boolean) {
        if (selectAll) {
            const set = new Set([...this.loadCloudSelectedDishIds, ...dishIds]);
            this.loadCloudSelectedDishIds = Array.from(set);
        } else {
            const drop = new Set(dishIds);
            this.loadCloudSelectedDishIds = this.loadCloudSelectedDishIds.filter((id) => !drop.has(id));
        }
    }

    /**
     * 二次确认后提交 add-dishes：写入当前本地菜单 + 选中小类，成功后展示成功浮层并刷新详情。
     */
    async confirmAddCloudDishesToLocal() {
        if (this.loadCloudSelectedDishIds.length <= 0) {
            GlobalNotification.info(i18n.t('menu_library_load_cloud_select_dishes_first'));
            return;
        }
        const cloudMenuId = this.loadCloudSelectedMenuId;
        const targetMain = this.detailMainGroupId;
        const targetSlu = this.selectedSecondSluId;
        if (!cloudMenuId || targetMain == null || targetSlu == null) {
            GlobalNotification.info(i18n.t('menu_library_load_cloud_need_local_subcategory'));
            return;
        }
        const sid = this.resolveRbpStringIdForApi().trim();
        if (!sid) {
            GlobalNotification.error(i18n.t('business_analysis_page_none_restaurant'));
            return;
        }
        if (this.loadCloudAddDishesSubmitting) {
            return;
        }
        // 关主弹窗 + 二次确认 → 仅展示「菜品添加中」浮层（保留勾选/详情供名称兜底与请求）
        this.isLoadCloudConfirmOpen = false;
        this.isLoadCloudDishDialogOpen = false;
        this.loadCloudAddDishesSubmitting = true;
        try {
            const r = await apiManage_rbp.post_cloud_dish_library_add_dishes(
                {
                    cloud_menu_id: cloudMenuId,
                    cloud_dish_ids: [...this.loadCloudSelectedDishIds],
                    target_main_group_id: targetMain,
                    target_dmi_slu_id: targetSlu,
                },
                sid,
            );
            if (_.get(r, 'error')) {
                GlobalNotification.error(i18n.t('menu_library_load_cloud_add_failed'));
                return;
            }
            const counts = pickAddDishesResultCounts(r);
            // 若接口未带回名称，用当前勾选行名称兜底（结果浮层列表；0 成功亦展示失败态）
            const fallbackNames =
                counts.names.length > 0
                    ? counts.names
                    : extractCloudDetailDishes(this.loadCloudMenuDetailRaw, null)
                          .filter((d) => this.loadCloudSelectedDishIds.includes(d.id))
                          .map((d) => d.name);
            runInAction(() => {
                this.loadCloudAddResult = { ...counts, names: fallbackNames };
            });
            // 刷新本地详情数据（含 0 成功失败态）
            await this.loadDetailData(targetMain);
        } catch (e) {
            console.error('confirmAddCloudDishesToLocal', e);
            GlobalNotification.error(i18n.t('menu_library_load_cloud_add_failed'));
        } finally {
            runInAction(() => {
                this.loadCloudAddDishesSubmitting = false;
            });
        }
    }

    /**
     * 关闭加载成功结果条（保留主弹窗）。
     */
    clearLoadCloudAddResult() {
        this.loadCloudAddResult = null;
    }

    async handleAdd() {
        /** 多选上传模式下不允许打开新建弹窗 */
        if (this.isUploadToCloudSelectMode) {
            return;
        }
        this.openCreateMenuDialogWithSource('local');
    }

    /**
     * 云端列表「新增云端菜单」：打开与本地同壳的新建弹窗；写接口未就绪时由门禁 toast。
     */
    async handleAddCloud() {
        /** 云端删除多选态下不允许打开新建弹窗（与顶栏按钮 disabled 对齐） */
        if (this.isCloudMenuDeleteSelectMode) {
            return;
        }
        this.openCreateMenuDialogWithSource('cloud');
    }

    /**
     * 重置并打开新建菜单弹窗（本地 / 云端共用壳）。
     * @param source 创建来源；云端时写路径走 `notifyCloudCreateWriteApiNotReady`
     */
    private openCreateMenuDialogWithSource(source: 'local' | 'cloud') {
        this.createSource = source;
        this.newMenuPrimaryName = i18n.t('global_text_Menu');
        this.createDialogMainGroupNameI18nJson = DEFAULT_MAIN_GROUP_NAME_I18N_JSON;
        this.createDialogPersistedMainGroupId = null;
        this.createDialogPersistedCloudMenuId = null;
        this.createDialogNextSecondIndex = 0;
        /** 仅重置新建弹窗一级菜单名称语言，不修改详情页 `menuDisplayLang` */
        this.createDialogFirstMenuDisplayLang = 'unspecified';
        this.createMenuSubcategoryDrafts = [];
        this.createMenuRoundLimitUnrestricted = false;
        this.createDialogMaxLimitDraft = '';
        this.createMenuPriceValueRows = this.buildEmptyCreateMenuPriceRows();
        this.createMenuSelectedWeekKey = 1;
        this.createDialogSetItemMain = '1';
        this.createExtraNormalIds = [];
        this.createExtraChildIds = [];
        this.createExtraOtherIds = [];
        this.createMenuSubcategoryDetailTempId = null;
        this.isCreateMenuSubcategoryAddDishDialogOpen = false;
        this.menuLibraryAddDishDialogMode = 'single';
        this.batchDishSettingsRowKeys = [];
        this.createMenuSubcategoryAddDishEditRowKey = null;
        this.createMenuSubcategoryDetailDishesByTempId = {};
        this.resetMenuLibraryAddDishImageStaging('');
        this.resetCreateDialogMenuImageStaging('');
        /** 本地新建需 setting 78 决定绑定费用方案；云端新建不展示绑定费用，不调 78 */
        if (source === 'local') {
            void this.syncCreateDialogSetItemMainFromSetting78();
        }
        this.isCreateMenuDialogOpen = true;
        void this.loadExtraMenuDataForCreate();
    }

    closeCreateMenuDialog() {
        this.isCreateMenuDialogOpen = false;
        this.createSource = 'local';
        this.createDialogPersistedMainGroupId = null;
        this.createDialogPersistedCloudMenuId = null;
        this.createDialogNextSecondIndex = 0;
        this.createMenuSubcategoryDrafts = [];
        this.createMenuSelectedWeekKey = 1;
        this.createMenuRoundLimitUnrestricted = false;
        this.createDialogMaxLimitDraft = '';
        this.createMenuPriceValueRows = [];
        this.createDialogSetItemMain = '1';
        this.createExtraNormalIds = [];
        this.createExtraChildIds = [];
        this.createExtraOtherIds = [];
        this.createDialogFirstMenuDisplayLang = 'unspecified';
        this.newMenuPrimaryName = '';
        this.createDialogMainGroupNameI18nJson = DEFAULT_MAIN_GROUP_NAME_I18N_JSON;
        this.createMenuSubcategoryDetailTempId = null;
        this.isCreateMenuSubcategoryAddDishDialogOpen = false;
        this.menuLibraryAddDishDialogMode = 'single';
        this.batchDishSettingsRowKeys = [];
        this.createMenuSubcategoryAddDishEditRowKey = null;
        this.createMenuSubcategoryDetailDishesByTempId = {};
        this.resetMenuLibraryAddDishImageStaging('');
        this.resetCreateDialogMenuImageStaging('');
    }

    /**
     * 打开新建弹窗内子分类详情（菜品列表视图）
     * @param tempId 子类别草稿 `tempId`
     */
    openCreateMenuSubcategoryDetail(tempId: string) {
        const exists = this.createMenuSubcategoryDrafts.some((r) => r.tempId === tempId);
        if (!exists) {
            return;
        }
        if (this.createMenuSubcategoryDetailDishesByTempId[tempId] === undefined) {
            this.createMenuSubcategoryDetailDishesByTempId = {
                ...this.createMenuSubcategoryDetailDishesByTempId,
                [tempId]: [],
            };
        }
        this.createMenuSubcategoryDetailTempId = tempId;
    }

    /**
     * 新建弹窗 — 当前子分类详情下的菜品列表（来自 `createMenuSubcategoryDetailDishesByTempId`）
     */
    getCreateMenuSubcategoryDetailDishes(): MenuDish[] {
        const tid = this.createMenuSubcategoryDetailTempId;
        if (!tid) {
            return [];
        }
        return this.createMenuSubcategoryDetailDishesByTempId[tid] ?? [];
    }

    /** 关闭子分类详情，返回子类别列表视图 */
    closeCreateMenuSubcategoryDetail() {
        this.createMenuSubcategoryDetailTempId = null;
    }

    /** 打开「新增菜品」弹窗（子分类详情内） */
    openCreateMenuSubcategoryAddDishDialog() {
        this.menuLibraryAddDishDialogMode = 'single';
        this.batchDishSettingsRowKeys = [];
        this.createMenuSubcategoryAddDishEditRowKey = null;
        this.menuLibraryDetailAddDishEditItemId = null;
        this.menuLibraryDetailAddDishEditCloudDishId = null;
        this.menuLibraryDetailPostSavedDishFallback = null;
        this.isCreateMenuSubcategoryAddDishDialogOpen = true;
    }

    /** 打开「编辑菜品」弹窗：按行 key 回填 `MenuLibraryAddDishDialogContent` */
    openCreateMenuSubcategoryAddDishDialogForEdit(rowKey: string) {
        this.menuLibraryAddDishDialogMode = 'single';
        this.batchDishSettingsRowKeys = [];
        this.createMenuSubcategoryAddDishEditRowKey = rowKey;
        this.menuLibraryDetailAddDishEditItemId = null;
        this.menuLibraryDetailAddDishEditCloudDishId = null;
        this.menuLibraryDetailPostSavedDishFallback = null;
        this.isCreateMenuSubcategoryAddDishDialogOpen = true;
    }

    /**
     * 子分类详情：批量菜品设置（共用菜品详情表单；仅已保存菜品参与接口）。
     */
    openBatchDishSettingsDialog(rowKeys: string[]) {
        const tid = this.createMenuSubcategoryDetailTempId;
        if (!tid || rowKeys.length === 0) {
            return;
        }
        const list = this.createMenuSubcategoryDetailDishesByTempId[tid] ?? [];
        const keyOf = (d: MenuDish, i: number) => createMenuSubcategoryDishRowKey(d, i);
        const withId = rowKeys.filter((k) => {
            const hit = list.find((d, i) => keyOf(d, i) === k);
            return hit?.item_id != null && hit?.slu_id != null;
        });
        if (withId.length === 0) {
            GlobalNotification.info(i18n.t('menu_library_create_dialog_batch_no_saved_dishes'));
            return;
        }
        runInAction(() => {
            this.menuLibraryAddDishDialogMode = 'batch';
            this.batchDishSettingsRowKeys = withId;
            this.createMenuSubcategoryAddDishEditRowKey = null;
            this.menuLibraryDetailAddDishEditItemId = null;
            this.menuLibraryDetailAddDishEditCloudDishId = null;
            this.menuLibraryDetailPostSavedDishFallback = null;
            this.isCreateMenuSubcategoryAddDishDialogOpen = true;
        });
        if (withId.length < rowKeys.length) {
            GlobalNotification.info(
                i18n.t('menu_library_batch_settings_skipped_unsaved', { count: rowKeys.length - withId.length }),
            );
        }
    }

    /**
     * 菜单库详情：批量菜品设置。
     * 本地：`batchDishSettingsRowKeys` = `id:${item_id}`；云端：直接用云端菜品 string id。
     */
    openMenuLibraryDetailBatchDishSettingsDialog(itemIds: number[]) {
        if (this.isCloudMenuDetail) {
            return;
        }
        const sm = this.getCurrentSecondMenu();
        if (!this.detailMainGroupId || sm?.dmi_slu_id == null || itemIds.length === 0) {
            GlobalNotification.error(i18n.t('menu_library_add_dish_need_subcategory'));
            return;
        }
        const list = this.getFilteredDishes();
        const withId = itemIds.filter((id) =>
            list.some((d) => d.item_id === id && d.slu_id != null),
        );
        if (withId.length === 0) {
            GlobalNotification.info(i18n.t('menu_library_create_dialog_batch_no_saved_dishes'));
            return;
        }
        runInAction(() => {
            this.menuLibraryAddDishDialogMode = 'batch';
            this.batchDishSettingsRowKeys = withId.map((id) => `id:${id}`);
            this.createMenuSubcategoryAddDishEditRowKey = null;
            this.menuLibraryDetailAddDishEditItemId = null;
            this.menuLibraryDetailAddDishEditCloudDishId = null;
            this.menuLibraryDetailPostSavedDishFallback = null;
            this.isCreateMenuSubcategoryAddDishDialogOpen = true;
        });
        if (withId.length < itemIds.length) {
            GlobalNotification.info(
                i18n.t('menu_library_batch_settings_skipped_unsaved', {
                    count: itemIds.length - withId.length,
                }),
            );
        }
    }

    /**
     * 云端菜单详情：批量菜品设置（`batchDishSettingsRowKeys` = 云端菜品 string id）。
     * @param cloudDishIds - 勾选的云端菜品 id
     */
    openMenuLibraryDetailCloudBatchDishSettingsDialog(cloudDishIds: string[]) {
        if (!this.isCloudMenuDetail || !this.detailCloudMenuId) {
            return;
        }
        const list = this.getFilteredDishes();
        const withId = cloudDishIds
            .map((id) => String(id ?? '').trim())
            .filter((id) => id && list.some((d) => getCloudDishIdFromMenuDish(d) === id));
        if (withId.length === 0) {
            GlobalNotification.info(i18n.t('menu_library_create_dialog_batch_no_saved_dishes'));
            return;
        }
        runInAction(() => {
            this.menuLibraryAddDishDialogMode = 'batch';
            this.batchDishSettingsRowKeys = withId;
            this.createMenuSubcategoryAddDishEditRowKey = null;
            this.menuLibraryDetailAddDishEditItemId = null;
            this.menuLibraryDetailAddDishEditCloudDishId = null;
            this.menuLibraryDetailPostSavedDishFallback = null;
            this.isCreateMenuSubcategoryAddDishDialogOpen = true;
        });
        if (withId.length < cloudDishIds.length) {
            GlobalNotification.info(
                i18n.t('menu_library_batch_settings_skipped_unsaved', {
                    count: cloudDishIds.length - withId.length,
                }),
            );
        }
    }

    /** 菜单库详情：当前筛选列表中、命中 `itemIds` 且已落库的菜品 */
    private getMenuLibraryDetailDishesForBatchItemIds(itemIds: number[]): MenuDish[] {
        const idSet = new Set(itemIds);
        return this.getFilteredDishes().filter(
            (d) => d.item_id != null && d.slu_id != null && idSet.has(d.item_id as number),
        );
    }

    /**
     * 菜单库详情：批量删除（仅当前筛选列表内、已落库行）
     */
    async deleteMenuLibraryDetailDishesByItemIds(itemIds: number[]): Promise<void> {
        if (this.notifyCloudDetailApiNotReady()) {
            return;
        }
        const fm = this.detailFirstMenu;
        const mainId = fm?.main_group_id;
        if (mainId == null || itemIds.length === 0) {
            return;
        }
        const dishes = this.getMenuLibraryDetailDishesForBatchItemIds(itemIds);
        if (dishes.length === 0) {
            GlobalNotification.info(i18n.t('menu_library_create_dialog_batch_no_saved_dishes'));
            return;
        }
        const payload = dishes.map((d) => ({
            item_id: d.item_id as number,
            slu_id: d.slu_id as number,
        }));
        setLoading(true);
        try {
            const r = await apiManage_rbp.delete_dishes(payload, this.resolveRbpStringIdForApi());
            if (_.get(r, 'error')) {
                GlobalNotification.error(i18n.t('global_text_operate_failure'));
                return;
            }
            GlobalNotification.success(i18n.t('global_text_operate_success'));
            await this.loadDetailData(mainId);
            await this.refreshLocalMenusAfterMutation();
        } catch (e) {
            console.error('deleteMenuLibraryDetailDishesByItemIds', e);
            GlobalNotification.error(i18n.t('global_text_operate_failure'));
        } finally {
            setLoading(false);
        }
    }

    /**
     * 菜单库详情：批量转移至目标二级菜单
     */
    async batchTransferMenuLibraryDetailDishesByItemIds(
        itemIds: number[],
        targetSecondMenu: SecondMenu,
    ): Promise<boolean> {
        if (this.notifyCloudDetailApiNotReady()) {
            return false;
        }
        const fm = this.detailFirstMenu;
        const mainId = fm?.main_group_id;
        const targetSluId = targetSecondMenu.dmi_slu_id;
        if (mainId == null || itemIds.length === 0 || targetSluId == null) {
            return false;
        }
        const matched = this.getMenuLibraryDetailDishesForBatchItemIds(itemIds);
        const toTransfer = matched.filter((d) => d.item_id != null);
        if (toTransfer.length === 0) {
            GlobalNotification.info(i18n.t('menu_library_create_dialog_batch_no_saved_dishes'));
            return false;
        }
        const skipped = itemIds.length - toTransfer.length;
        const notJoining = targetSecondMenu.not_joining_global_limit ?? 0;
        const params: PatchDishesParams[] = toTransfer.map(
            (d) =>
                ({
                    item_id: d.item_id as number,
                    slu_id: targetSluId,
                    not_joining_global_limit: notJoining,
                }) as PatchDishesParams,
        );
        const rbpId = this.resolveRbpStringIdForApi();
        try {
            setLoading(true);
            const r = await apiManage_rbp.patch_dishes(params, undefined, rbpId);
            if (_.get(r, 'error')) {
                GlobalNotification.error(i18n.t('global_text_operate_failure'));
                return false;
            }
            GlobalNotification.success(i18n.t('global_text_operate_success'));
            if (skipped > 0) {
                GlobalNotification.info(
                    i18n.t('menu_library_create_dialog_batch_skipped_unsaved', { count: skipped }),
                );
            }
            await this.loadDetailData(mainId);
            await this.refreshLocalMenusAfterMutation();
            return true;
        } catch (e) {
            console.error('batchTransferMenuLibraryDetailDishesByItemIds', e);
            GlobalNotification.error(i18n.t('global_text_operate_failure'));
            return false;
        } finally {
            setLoading(false);
        }
    }

    /**
     * 菜单库详情：批量复制到目标二级菜单
     */
    async batchCopyMenuLibraryDetailDishesByItemIds(
        itemIds: number[],
        targetSecondMenu: SecondMenu,
    ): Promise<boolean> {
        if (this.notifyCloudDetailApiNotReady()) {
            return false;
        }
        const fm = this.detailFirstMenu;
        const mainId = fm?.main_group_id;
        const targetSluId = targetSecondMenu.dmi_slu_id;
        if (mainId == null || itemIds.length === 0 || targetSluId == null) {
            return false;
        }
        const matched = this.getMenuLibraryDetailDishesForBatchItemIds(itemIds);
        const dishIds = matched.map((d) => d.item_id as number).filter((id) => id > 0);
        const skipped = itemIds.length - dishIds.length;
        if (dishIds.length === 0) {
            GlobalNotification.info(i18n.t('menu_library_create_dialog_batch_no_saved_dishes'));
            return false;
        }
        const rbpId = this.resolveRbpStringIdForApi();
        try {
            setLoading(true);
            const r = await apiManage_rbp.patch_copy_dishes(dishIds, [targetSluId], rbpId);
            if (_.get(r, 'error')) {
                GlobalNotification.error(i18n.t('global_text_operate_failure'));
                return false;
            }
            const insertItems = (r as { insert_menu_item?: Array<{ item_id?: number }> }).insert_menu_item;
            if (insertItems?.length) {
                const limitParams: PatchDishesParams[] = insertItems.map((item) => ({
                    item_id: item.item_id as number,
                    not_joining_global_limit: targetSecondMenu.not_joining_global_limit || 0,
                })) as PatchDishesParams[];
                await apiManage_rbp.patch_dishes(limitParams, true, rbpId);
            }
            GlobalNotification.success(i18n.t('global_text_operate_success'));
            if (skipped > 0) {
                GlobalNotification.info(
                    i18n.t('menu_library_create_dialog_batch_skipped_unsaved', { count: skipped }),
                );
            }
            await this.loadDetailData(mainId);
            await this.refreshLocalMenusAfterMutation();
            return true;
        } catch (e) {
            console.error('batchCopyMenuLibraryDetailDishesByItemIds', e);
            GlobalNotification.error(i18n.t('global_text_operate_failure'));
            return false;
        } finally {
            setLoading(false);
        }
    }

    /**
     * 菜单库云端详情：当前筛选列表中命中 `cloudDishIds` 的菜品行。
     * @param cloudDishIds - 云端菜品 string id
     */
    private getMenuLibraryDetailDishesForBatchCloudDishIds(cloudDishIds: string[]): MenuDish[] {
        const idSet = new Set(cloudDishIds.filter((id) => id.trim() !== ''));
        return this.getFilteredDishes().filter((d) => {
            const cid = getCloudDishIdFromMenuDish(d);
            return cid != null && idSet.has(cid);
        });
    }

    /**
     * 菜单库云端详情：批量删除菜品（`POST .../dishes/delete`）。
     * @param cloudDishIds - 勾选的云端菜品 id
     */
    async deleteMenuLibraryDetailCloudDishesByIds(cloudDishIds: string[]): Promise<boolean> {
        const cloudMenuId = this.detailCloudMenuId;
        if (!this.isCloudMenuDetail || !cloudMenuId || cloudDishIds.length === 0) {
            return false;
        }
        const matched = this.getMenuLibraryDetailDishesForBatchCloudDishIds(cloudDishIds);
        const ids = matched
            .map((d) => getCloudDishIdFromMenuDish(d))
            .filter((id): id is string => id != null);
        if (ids.length === 0) {
            GlobalNotification.info(i18n.t('menu_library_create_dialog_batch_no_saved_dishes'));
            return false;
        }
        const sid = this.resolveRbpStringIdForApi().trim();
        if (!sid) {
            GlobalNotification.error(i18n.t('business_analysis_page_none_restaurant'));
            return false;
        }
        setLoading(true);
        try {
            const r = await cloudDishLibraryApi.post_cloud_dish_library_dishes_delete(
                cloudMenuId,
                { cloud_dish_ids: ids },
                sid,
            );
            if (_.get(r, 'error')) {
                GlobalNotification.error(i18n.t('global_text_operate_failure'));
                return false;
            }
            if ((r.deleted_dish_count ?? 0) <= 0 && (r.not_found_ids?.length ?? 0) >= ids.length) {
                GlobalNotification.error(i18n.t('global_text_operate_failure'));
                return false;
            }
            GlobalNotification.success(i18n.t('global_text_operate_success'));
            if (Array.isArray(r.not_found_ids) && r.not_found_ids.length > 0) {
                GlobalNotification.info(
                    i18n.t('menu_library_cloud_dish_delete_partial_not_found', {
                        count: r.not_found_ids.length,
                    }),
                );
            }
            await this.loadCloudMenuDetailData(cloudMenuId);
            await this.refreshCloudMenusAfterMutation();
            return true;
        } catch (e) {
            console.error('deleteMenuLibraryDetailCloudDishesByIds', e);
            GlobalNotification.error(i18n.t('global_text_operate_failure'));
            return false;
        } finally {
            setLoading(false);
        }
    }

    /**
     * 菜单库云端详情：批量转移菜品到目标云端菜单/小类（`POST .../dishes/move`，可跨菜单）。
     * @param cloudDishIds - 勾选的云端菜品 id
     * @param target - `target_cloud_menu_id` + `target_cloud_slu_id`
     */
    async batchTransferMenuLibraryDetailCloudDishesByIds(
        cloudDishIds: string[],
        target: { target_cloud_menu_id: string; target_cloud_slu_id: string },
    ): Promise<boolean> {
        const cloudMenuId = this.detailCloudMenuId;
        if (
            !this.isCloudMenuDetail ||
            !cloudMenuId ||
            cloudDishIds.length === 0 ||
            !target.target_cloud_menu_id ||
            !target.target_cloud_slu_id
        ) {
            return false;
        }
        const matched = this.getMenuLibraryDetailDishesForBatchCloudDishIds(cloudDishIds);
        const ids = matched
            .map((d) => getCloudDishIdFromMenuDish(d))
            .filter((id): id is string => id != null);
        if (ids.length === 0) {
            GlobalNotification.info(i18n.t('menu_library_create_dialog_batch_no_saved_dishes'));
            return false;
        }
        const sid = this.resolveRbpStringIdForApi().trim();
        if (!sid) {
            GlobalNotification.error(i18n.t('business_analysis_page_none_restaurant'));
            return false;
        }
        try {
            setLoading(true);
            const r = await cloudDishLibraryApi.post_cloud_dish_library_dishes_move(
                cloudMenuId,
                {
                    cloud_dish_ids: ids,
                    target_cloud_menu_id: target.target_cloud_menu_id,
                    target_cloud_slu_id: target.target_cloud_slu_id,
                },
                sid,
            );
            if (_.get(r, 'error')) {
                GlobalNotification.error(i18n.t('global_text_operate_failure'));
                return false;
            }
            if ((r.moved_count ?? 0) <= 0) {
                GlobalNotification.error(i18n.t('global_text_operate_failure'));
                if (Array.isArray(r.not_found_ids) && r.not_found_ids.length > 0) {
                    GlobalNotification.info(
                        i18n.t('menu_library_cloud_dish_delete_partial_not_found', {
                            count: r.not_found_ids.length,
                        }),
                    );
                }
                return false;
            }
            GlobalNotification.success(i18n.t('global_text_operate_success'));
            await this.loadCloudMenuDetailData(cloudMenuId);
            await this.refreshCloudMenusAfterMutation();
            return true;
        } catch (e) {
            console.error('batchTransferMenuLibraryDetailCloudDishesByIds', e);
            GlobalNotification.error(i18n.t('global_text_operate_failure'));
            return false;
        } finally {
            setLoading(false);
        }
    }

    /**
     * 菜单库云端详情：批量复制菜品到目标云端菜单/小类（`POST .../dishes/copy`，可跨菜单）。
     * @param cloudDishIds - 勾选的云端菜品 id
     * @param target - `target_cloud_menu_id` + `target_cloud_slu_id`
     */
    async batchCopyMenuLibraryDetailCloudDishesByIds(
        cloudDishIds: string[],
        target: { target_cloud_menu_id: string; target_cloud_slu_id: string },
    ): Promise<boolean> {
        const cloudMenuId = this.detailCloudMenuId;
        if (
            !this.isCloudMenuDetail ||
            !cloudMenuId ||
            cloudDishIds.length === 0 ||
            !target.target_cloud_menu_id ||
            !target.target_cloud_slu_id
        ) {
            return false;
        }
        const matched = this.getMenuLibraryDetailDishesForBatchCloudDishIds(cloudDishIds);
        const ids = matched
            .map((d) => getCloudDishIdFromMenuDish(d))
            .filter((id): id is string => id != null);
        if (ids.length === 0) {
            GlobalNotification.info(i18n.t('menu_library_create_dialog_batch_no_saved_dishes'));
            return false;
        }
        const sid = this.resolveRbpStringIdForApi().trim();
        if (!sid) {
            GlobalNotification.error(i18n.t('business_analysis_page_none_restaurant'));
            return false;
        }
        try {
            setLoading(true);
            const r = await cloudDishLibraryApi.post_cloud_dish_library_dishes_copy(
                cloudMenuId,
                {
                    cloud_dish_ids: ids,
                    target_cloud_menu_id: target.target_cloud_menu_id,
                    target_cloud_slu_id: target.target_cloud_slu_id,
                },
                sid,
            );
            if (_.get(r, 'error')) {
                GlobalNotification.error(i18n.t('global_text_operate_failure'));
                return false;
            }
            if ((r.copied_count ?? 0) <= 0) {
                GlobalNotification.error(i18n.t('global_text_operate_failure'));
                if (Array.isArray(r.not_found_ids) && r.not_found_ids.length > 0) {
                    GlobalNotification.info(
                        i18n.t('menu_library_cloud_dish_delete_partial_not_found', {
                            count: r.not_found_ids.length,
                        }),
                    );
                }
                return false;
            }
            GlobalNotification.success(i18n.t('global_text_operate_success'));
            await this.loadCloudMenuDetailData(cloudMenuId);
            await this.refreshCloudMenusAfterMutation();
            return true;
        } catch (e) {
            console.error('batchCopyMenuLibraryDetailCloudDishesByIds', e);
            GlobalNotification.error(i18n.t('global_text_operate_failure'));
            return false;
        } finally {
            setLoading(false);
        }
    }

    /** 菜单库详情：打开「新增菜品」弹窗（与新建弹窗共用 `MenuLibraryAddDishDialogContent`） */
    openMenuLibraryDetailAddDishDialog() {
        const sm = this.getCurrentSecondMenu();
        const hasCloudTarget = this.isCloudMenuDetail && !!this.detailCloudMenuId && !!this.getCloudSluIdFromSecondMenu(sm);
        const hasLocalTarget = !this.isCloudMenuDetail && !!this.detailMainGroupId && sm?.dmi_slu_id != null;
        if (!hasCloudTarget && !hasLocalTarget) {
            GlobalNotification.error(i18n.t('menu_library_add_dish_need_subcategory'));
            return;
        }
        this.menuLibraryAddDishDialogMode = 'single';
        this.batchDishSettingsRowKeys = [];
        this.menuLibraryDetailAddDishEditItemId = null;
        this.menuLibraryDetailAddDishEditCloudDishId = null;
        this.createMenuSubcategoryAddDishEditRowKey = null;
        this.menuLibraryDetailPostSavedDishFallback = null;
        this.isCreateMenuSubcategoryAddDishDialogOpen = true;
    }

    /**
     * 菜单库详情：按 `item_id` 打开「编辑菜品」弹窗。
     * 云端详情可打开同款可编辑壳；云端主键写入 `menuLibraryDetailAddDishEditCloudDishId`。
     * @param itemId - 本地菜品 `item_id`（须 > 0）；云端无有效数字 id 时传 `null` 并配合 `dishFallback`
     * @param dishFallback - 打开时快照行（云端回填；本地一般可不传）
     */
    openMenuLibraryDetailAddDishDialogForEdit(itemId: number | null, dishFallback?: MenuDish | null) {
        const sm = this.getCurrentSecondMenu();
        const hasCloudTarget =
            this.isCloudMenuDetail && !!this.detailCloudMenuId && !!this.getCloudSluIdFromSecondMenu(sm);
        const hasLocalTarget = !this.isCloudMenuDetail && !!this.detailMainGroupId && sm?.dmi_slu_id != null;
        if (!hasCloudTarget && !hasLocalTarget) {
            GlobalNotification.error(i18n.t('menu_library_add_dish_need_subcategory'));
            return;
        }
        this.menuLibraryAddDishDialogMode = 'single';
        this.batchDishSettingsRowKeys = [];
        /** 云端：仅保留有效本地数字 id；0/负数不当作查找键 */
        const normalizedItemId =
            itemId != null && Number(itemId) > 0 ? Number(itemId) : null;
        this.menuLibraryDetailAddDishEditItemId = normalizedItemId;
        this.menuLibraryDetailAddDishEditCloudDishId = this.isCloudMenuDetail
            ? getCloudDishIdFromMenuDish(dishFallback)
            : null;
        this.createMenuSubcategoryAddDishEditRowKey = null;
        this.menuLibraryDetailPostSavedDishFallback = dishFallback ?? null;
        this.isCreateMenuSubcategoryAddDishDialogOpen = true;
    }

    /**
     * 菜单库详情内首次 `post_dishes` 成功后，将弹窗切为与 `openMenuLibraryDetailAddDishDialogForEdit` 一致的编辑态，
     * 便于 `getAddDishEditTarget` 命中且套餐组弹窗能拿到 `dishItemId`。
     * @param savedDish 接口返回 insertId 后组装的行；列表尚未刷新时 `getAddDishEditTarget` 可回退到此对象。
     */
    promoteMenuLibraryDetailAddDishEditAfterFirstSave(itemId: number, savedDish?: MenuDish) {
        runInAction(() => {
            this.menuLibraryAddDishDialogMode = 'single';
            this.batchDishSettingsRowKeys = [];
            this.menuLibraryDetailAddDishEditItemId = itemId;
            this.createMenuSubcategoryAddDishEditRowKey = null;
            this.menuLibraryDetailPostSavedDishFallback =
                savedDish != null && Number(savedDish.item_id) === Number(itemId) ? savedDish : null;
        });
    }

    /**
     * 新建弹窗子分类内首次 `post_dishes` 成功后，将编辑行 key 设为 `id:itemId`，与列表行 key 规则一致。
     */
    promoteCreateSubcategoryAddDishEditAfterSave(itemId: number) {
        runInAction(() => {
            this.menuLibraryAddDishDialogMode = 'single';
            this.batchDishSettingsRowKeys = [];
            this.createMenuSubcategoryAddDishEditRowKey = `id:${itemId}`;
            this.menuLibraryDetailAddDishEditItemId = null;
            this.menuLibraryDetailAddDishEditCloudDishId = null;
        });
    }

    /** 关闭「新增/编辑菜品」弹窗 */
    closeCreateMenuSubcategoryAddDishDialog() {
        this.isCreateMenuSubcategoryAddDishDialogOpen = false;
        this.menuLibraryAddDishDialogMode = 'single';
        this.batchDishSettingsRowKeys = [];
        this.createMenuSubcategoryAddDishEditRowKey = null;
        this.menuLibraryDetailAddDishEditItemId = null;
        this.menuLibraryDetailAddDishEditCloudDishId = null;
        this.menuLibraryDetailPostSavedDishFallback = null;
        this.resetMenuLibraryAddDishImageStaging('');
    }

    /**
     * 新建菜单弹窗辅助数据：打印岗位写入 `printGroupMap`（菜品表列）；
     * **仅本地**另拉 type=1 额外费用菜单扁平化为 `extraMenuData`（绑定费用多选）。
     * 云端新建不展示绑定费用，故不请求 `menus?type=1`（见领域文档 §4.7）。
     */
    async loadExtraMenuDataForCreate() {
        const rbpId = this.resolveRbpStringIdForApi();
        // 云端创建弹窗不展示 / 不编辑绑定费用
        const skipExtraFeeMenus = this.isCloudCreateDialog;
        /** 云端 + 隧道不通：不请求 print_class */
        const skipPrintClass = this.isCloudCreateDialog && !this.shouldFetchCloudTunnelDropdownApis;
        try {
            const printPromise = skipPrintClass
                ? Promise.resolve(null)
                : apiManage_rbp.getPrinterClass(rbpId);
            const extraMenusPromise = skipExtraFeeMenus
                ? Promise.resolve(null)
                : apiManage_rbp.get_menus_v3(
                      {
                          type: 1,
                          with_dish: true,
                      },
                      rbpId,
                  );
            const [extraMenusRes, printRes] = await Promise.all([extraMenusPromise, printPromise]);
            const extraMenuData: MenuDish[] = [];
            if (!skipExtraFeeMenus && !_.get(extraMenusRes, 'error') && Array.isArray(extraMenusRes)) {
                for (const v of extraMenusRes as FirstMenu[]) {
                    for (const v1 of v.menu_item_slu || []) {
                        for (const v2 of v1.menu_item || []) {
                            extraMenuData.push(v2);
                        }
                    }
                }
            }
            const map: Record<number, string> = { [-1]: i18n.t('print_setting_page_none0') };
            if (!_.get(printRes, 'error') && Array.isArray(printRes)) {
                (printRes as { print_class_id?: number; print_class_name?: string }[]).forEach((p) => {
                    if (p.print_class_id !== undefined && p.print_class_name) {
                        map[p.print_class_id] = p.print_class_name;
                    }
                });
            }
            runInAction(() => {
                this.extraMenuData = extraMenuData;
                this.printGroupMap = map;
            });
        } catch (e) {
            console.error('loadExtraMenuDataForCreate', e);
            runInAction(() => {
                this.extraMenuData = [];
                /** 隧道辅助接口失败：打印组仅保留「无」，不挡新建弹窗 */
                this.printGroupMap = { [-1]: i18n.t('print_setting_page_none0') };
            });
        }
    }

    /**
     * 新建菜单弹窗：切换绑定费用星期（先将当前药丸草稿写入上一周行，再加载目标周）
     * @param key 周一至周日数字或 `holiday`（内部为 `MENU_LIBRARY_WEEK_HOLIDAY_NUM`）
     */
    setCreateMenuSelectedWeekKey(key: number | 'holiday') {
        if (this.createDialogSetItemMain === '0') {
            return;
        }
        runInAction(() => {
            const prev = this.createMenuSelectedWeekKey;
            this.persistCreateExtrasIntoWeekKey(prev);
            this.createMenuSelectedWeekKey = key;
            this.loadCreateExtrasFromWeekKey(key);
        });
    }

    /**
     * 从门店设置 {@link MENU_LIBRARY_SETTING_ID_CREATE_MENU_PRICE_SCHEME} 同步新建弹窗 `set_item_main` 对应字段；失败时按周新方案
     */
    private async syncCreateDialogSetItemMainFromSetting78(): Promise<void> {
        try {
            const res = await apiManage_rbp.getSettingById(
                MENU_LIBRARY_SETTING_ID_CREATE_MENU_PRICE_SCHEME,
                this.resolveRbpStringIdForApi(),
            );
            const mode = MenuLibraryManagementState.parseSetItemMainFromSetting78(res);
            runInAction(() => {
                this.createDialogSetItemMain = mode;
                this.createMenuSelectedWeekKey = 1;
                this.loadCreateExtrasFromWeekKey(1);
            });
        } catch (e) {
            console.error('syncCreateDialogSetItemMainFromSetting78', e);
            runInAction(() => {
                this.createDialogSetItemMain = '1';
                this.createMenuSelectedWeekKey = 1;
                this.loadCreateExtrasFromWeekKey(1);
            });
        }
    }

    /**
     * 一键复制：将当前选中星期的绑定费用与汇总价覆盖到其余所有星期行（与 `FirstMenuDetail.oneClickCopy` 一致）
     */
    createMenuOneClickCopy() {
        runInAction(() => {
            this.persistCreateExtrasIntoWeekKey(this.createMenuSelectedWeekKey);
            const w = this.resolveCreateMenuWeekNumber(this.createMenuSelectedWeekKey);
            const source = this.createMenuPriceValueRows.find((r) => r.week === w);
            if (!source) {
                return;
            }
            this.createMenuPriceValueRows = this.createMenuPriceValueRows.map((r) => ({
                ...r,
                extra_normal: source.extra_normal,
                extra_child: source.extra_child,
                extra_other: source.extra_other,
                normal_price: source.normal_price,
                child_price: source.child_price,
                other_price: source.other_price,
            }));
            this.loadCreateExtrasFromWeekKey(this.createMenuSelectedWeekKey);
        });
    }

    /** 新建菜单弹窗：切换「不受轮次限制」 */
    setCreateMenuRoundLimitUnrestricted(value: boolean) {
        this.createMenuRoundLimitUnrestricted = value;
    }

    /** 新建菜单弹窗：大类限点输入 */
    setCreateDialogMaxLimitDraft(value: string) {
        this.createDialogMaxLimitDraft = value;
    }

    /**
     * 构建空的新价格方案 `value` 行：周一至周日 + 节假日（week=7）
     */
    private buildEmptyCreateMenuPriceRows(): NewMenuPrice[] {
        return [
            ...setEmptyNewPriceValue(),
            {
                week: MENU_LIBRARY_WEEK_HOLIDAY_NUM,
                extra_child: '',
                extra_normal: '',
                extra_other: '',
                normal_price: null,
                child_price: null,
                other_price: null,
            },
        ];
    }

    /**
     * UI 星期键 → `NewMenuPrice.week` 数值
     * @param key 数字周或 `holiday`
     */
    private resolveCreateMenuWeekNumber(key: number | 'holiday'): number {
        return key === 'holiday' ? MENU_LIBRARY_WEEK_HOLIDAY_NUM : key;
    }

    /**
     * 将当前三档药丸所选写回 `createMenuPriceValueRows` 中对应星期行
     * @param weekKey 当前或上一 UI 星期键
     */
    private persistCreateExtrasIntoWeekKey(weekKey: number | 'holiday') {
        const w = this.resolveCreateMenuWeekNumber(weekKey);
        const idx = this.createMenuPriceValueRows.findIndex((r) => r.week === w);
        if (idx < 0) {
            return;
        }
        const extraNormalStr = this.createExtraNormalIds.join(',');
        const extraChildStr = this.createExtraChildIds.join(',');
        const extraOtherStr = this.createExtraOtherIds.join(',');
        const next = [...this.createMenuPriceValueRows];
        next[idx] = {
            ...next[idx],
            extra_normal: extraNormalStr,
            extra_child: extraChildStr,
            extra_other: extraOtherStr,
            normal_price: Number(this.sumExtraPricesForIds(this.createExtraNormalIds).toFixed(2)),
            child_price: Number(this.sumExtraPricesForIds(this.createExtraChildIds).toFixed(2)),
            other_price: Number(this.sumExtraPricesForIds(this.createExtraOtherIds).toFixed(2)),
        };
        this.createMenuPriceValueRows = next;
    }

    /**
     * 从 `createMenuPriceValueRows` 加载指定星期行到三档药丸草稿
     * @param weekKey UI 星期键
     */
    private loadCreateExtrasFromWeekKey(weekKey: number | 'holiday') {
        const w = this.resolveCreateMenuWeekNumber(weekKey);
        const row = this.createMenuPriceValueRows.find((r) => r.week === w);
        if (!row) {
            this.createExtraNormalIds = [];
            this.createExtraChildIds = [];
            this.createExtraOtherIds = [];
            return;
        }
        this.createExtraNormalIds = this.parseExtraCsvIds(row.extra_normal);
        this.createExtraChildIds = this.parseExtraCsvIds(row.extra_child);
        this.createExtraOtherIds = this.parseExtraCsvIds(row.extra_other);
    }

    setNewMenuName(value: string) {
        const next = applyMainGroupNameInput({
            primary: this.newMenuPrimaryName,
            i18nJson: this.createDialogMainGroupNameI18nJson,
            lang: this.createDialogFirstMenuDisplayLang,
            value,
        });
        this.newMenuPrimaryName = next.primary;
        this.createDialogMainGroupNameI18nJson = next.i18nJson;
    }

    /**
     * 新建菜单弹窗：追加一行子类别草稿
     * @param initialName 初始名称（可选；由「新增子类别」接口创建后回写）
     * @param dmiSluId 已创建的二级菜单 id（可选）
     */
    addCreateMenuSubcategoryDraft(initialName?: string, dmiSluId?: number, cloudSluId?: string) {
        const tempId = `tmp-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
        this.createMenuSubcategoryDrafts.push({
            tempId,
            dmi_slu_id: dmiSluId,
            cloud_slu_id: cloudSluId,
            name: initialName?.trim() ?? '',
            nameI18nJson: DEFAULT_MAIN_GROUP_NAME_I18N_JSON,
            displayLang: 'unspecified',
            perPersonLimit: '',
            perTableLimit: '',
        });
    }

    /**
     * 新建菜单弹窗：「新增菜单子类别」——已有 `main_group_id` 时直接 `post_create_second_menu`；否则先执行保存（创建一级菜单），再新增一个二级菜单（与制作器 `_handleAddNextLevelMenu` 同源接口）
     */
    async addCreateMenuSubcategoryRowOrPersist() {
        if (this.isSubmittingCreate) {
            return;
        }
        try {
            if (this.isCloudCreateDialog && this.createDialogPersistedCloudMenuId == null) {
                await this.submitCreateFirstMenu({ notifySuccess: false });
                if (this.createDialogPersistedCloudMenuId == null) {
                    return;
                }
            } else if (!this.isCloudCreateDialog && this.createDialogPersistedMainGroupId == null) {
                await this.submitCreateFirstMenu({ notifySuccess: false });
                if (this.createDialogPersistedMainGroupId == null) {
                    return;
                }
            }
            this.isSubmittingCreate = true;
            setLoading(true);
            await this.postOneDefaultSecondMenuAndDraft();
            GlobalNotification.success(i18n.t('global_text_operate_success'));
            await (this.isCloudCreateDialog ? this.refreshCloudMenusAfterMutation() : this.refreshLocalMenusAfterMutation());
        } catch (error) {
            console.error('addCreateMenuSubcategoryRowOrPersist failed:', error);
            GlobalNotification.error(i18n.t('global_text_operate_failure'));
        } finally {
            setLoading(false);
            runInAction(() => {
                this.isSubmittingCreate = false;
            });
        }
    }

    /**
     * 调用后端新增一个默认名称的二级菜单，并追加一行草稿与下标（仅新建弹窗内使用）
     */
    private async postOneDefaultSecondMenuAndDraft() {
        if (this.isCloudCreateDialog) {
            const cloudMenuId = this.createDialogPersistedCloudMenuId;
            if (!cloudMenuId) {
                return;
            }
            const defaultName = i18n.t('global_text_Menu');
            const result = await cloudDishLibraryApi.post_cloud_dish_library_menu_update(
                cloudMenuId,
                {
                    menu_item_slu_add: [{
                        dmi_slu_name: defaultName,
                        dmi_slu_name_i18n: DEFAULT_MAIN_GROUP_NAME_I18N_JSON,
                    }],
                },
                this.resolveRbpStringIdForApi(),
            );
            if (_.get(result, 'error')) {
                throw new Error('post_cloud_dish_library_menu_update failed');
            }
            // 联调真实体：新增小类 id 在 `added_slu[0]._id`，无需再拉详情比对
            const cloudSluId = pickFirstAddedCloudSluId(result);
            if (!cloudSluId) {
                throw new Error('cloud subcategory id not found');
            }
            runInAction(() => {
                this.createDialogNextSecondIndex += 1;
                this.addCreateMenuSubcategoryDraft(defaultName, undefined, cloudSluId);
            });
            return;
        }
        const mainId = this.createDialogPersistedMainGroupId;
        if (mainId == null) {
            return;
        }
        const defaultName = i18n.t('global_text_Menu');
        const second: SecondMenu = {
            dmi_slu_name: defaultName,
            dmi_slu_name_i18n: DEFAULT_MAIN_GROUP_NAME_I18N_JSON,
            dmi_slu_index: this.createDialogNextSecondIndex,
        };
        const r = await apiManage_rbp.post_create_second_menu(mainId, second, this.resolveRbpStringIdForApi());
        if (_.get(r, 'error')) {
            throw new Error('post_create_second_menu failed');
        }
        const insertId = r?.insertId as number | undefined;
        runInAction(() => {
            this.createDialogNextSecondIndex += 1;
            this.addCreateMenuSubcategoryDraft(defaultName, insertId);
        });
    }

    /**
     * 已存在一级菜单 id 时：PATCH 名称与绑定费用。
     * 新方案 `createDialogSetItemMain === '1'`：`patch_first_menu` + `patch_first_menu_day_price`；
     * 旧方案 `'0'`：仅 `patch_first_menu` 写根级 `extra_*` 与三档价（与详情顶栏旧方案一致）。
     * @param options.notifySuccess 是否弹出成功提示
     */
    private async patchCreateMenuDialogFirstMenu(options?: { notifySuccess?: boolean }) {
        const notifySuccess = options?.notifySuccess !== false;
        const name = this.newMenuPrimaryName.trim();
        if (!name) {
            GlobalNotification.error(i18n.t('menu_library_detail_menu_name_required'));
            return;
        }
        const i18nNameJson = normalizeMainGroupNameI18nJson(this.createDialogMainGroupNameI18nJson);
        if (this.isCloudCreateDialog) {
            const cloudMenuId = this.createDialogPersistedCloudMenuId;
            if (!cloudMenuId) {
                return;
            }
            this.isSubmittingCreate = true;
            setLoading(true);
            try {
                const maxLimit = this.parseCreateDialogMaxLimitOptional() ?? 9999;
                const result = await cloudDishLibraryApi.post_cloud_dish_library_menu_update(
                    cloudMenuId,
                    { main_group_name: name, main_group_name_i18n: i18nNameJson, max_limit: maxLimit },
                    this.resolveRbpStringIdForApi(),
                );
                if (_.get(result, 'error')) {
                    GlobalNotification.error(i18n.t('global_text_operate_failure'));
                    return;
                }
                const menuImgResolved = await this.resolveCreateMenuDialogMenuImageForSave(
                    this.resolveRbpStringIdForApi(),
                    cloudMenuId,
                    this.createDialogMenuImageRef.trim(),
                );
                runInAction(() => {
                    this.createDialogMaxLimitDraft = String(maxLimit);
                    this.resetCreateDialogMenuImageStaging(menuImgResolved);
                });
                if (notifySuccess) {
                    GlobalNotification.success(i18n.t('global_text_operate_success'));
                }
                await this.refreshCloudMenusAfterMutation();
            } catch (error) {
                console.error('patchCreateMenuDialogFirstMenu cloud failed:', error);
                GlobalNotification.error(i18n.t('global_text_operate_failure'));
            } finally {
                setLoading(false);
                runInAction(() => {
                    this.isSubmittingCreate = false;
                });
            }
            return;
        }
        const gid = this.createDialogPersistedMainGroupId;
        if (gid == null) {
            return;
        }
        this.isSubmittingCreate = true;
        setLoading(true);
        try {
            runInAction(() => {
                this.persistCreateExtrasIntoWeekKey(this.createMenuSelectedWeekKey);
            });
            const rbp = this.resolveRbpStringIdForApi();
            let menuImgResolved = this.createDialogMenuImageRef.trim();
            try {
                menuImgResolved = await this.resolveCreateMenuDialogMenuImageForSave(
                    rbp,
                    gid,
                    this.createDialogMenuImageRef.trim(),
                );
            } catch (e) {
                GlobalNotification.error(
                    e instanceof Error ? e.message : i18n.t('global_text_operate_failure'),
                );
                return;
            }
            const w = this.resolveCreateMenuWeekNumber(this.createMenuSelectedWeekKey);
            const row = this.createMenuPriceValueRows.find((r) => r.week === w);
            const maxLimitOpt = this.parseCreateDialogMaxLimitOptional();
            if (this.createDialogSetItemMain === '1') {
                const value = _.cloneDeep(this.createMenuPriceValueRows) as NewMenuPrice[];
                const resName = await apiManage_rbp.patch_first_menu(
                    {
                        main_group_id: gid,
                        main_group_name: name,
                        main_group_name_i18n: i18nNameJson,
                        ...(maxLimitOpt !== undefined ? { max_limit: maxLimitOpt } : {}),
                        extra_child: row?.extra_child ?? '',
                        extra_normal: row?.extra_normal ?? '',
                        extra_other: row?.extra_other ?? '',
                    } as FirstMenu,
                    rbp,
                );
                if (_.get(resName, 'error')) {
                    GlobalNotification.error(i18n.t('global_text_operate_failure'));
                    return;
                }
                const resPrice = await apiManage_rbp.patch_first_menu_day_price(
                    {
                        main_group_id: gid,
                        main_group_name: name,
                        main_group_name_i18n: i18nNameJson,
                        menu_item_slu: [],
                        set_item_main: '1',
                        value,
                    } as FirstMenu,
                    rbp,
                );
                if (_.get(resPrice, 'error')) {
                    GlobalNotification.error(i18n.t('global_text_operate_failure'));
                    return;
                }
            } else {
                const res = await apiManage_rbp.patch_first_menu(
                    {
                        main_group_id: gid,
                        main_group_name: name,
                        main_group_name_i18n: i18nNameJson,
                        ...(maxLimitOpt !== undefined ? { max_limit: maxLimitOpt } : {}),
                        set_item_main: '0',
                        extra_normal: row?.extra_normal ?? '',
                        extra_child: row?.extra_child ?? '',
                        extra_other: row?.extra_other ?? '',
                        normal_price: row?.normal_price ?? null,
                        child_price: row?.child_price ?? null,
                        other_price: row?.other_price ?? null,
                    } as FirstMenu,
                    rbp,
                );
                if (_.get(res, 'error')) {
                    GlobalNotification.error(i18n.t('global_text_operate_failure'));
                    return;
                }
            }
            runInAction(() => {
                this.resetCreateDialogMenuImageStaging(menuImgResolved);
            });
            if (notifySuccess) {
                GlobalNotification.success(i18n.t('global_text_operate_success'));
            }
            await this.refreshLocalMenusAfterMutation();
        } catch (error) {
            console.error('patchCreateMenuDialogFirstMenu failed:', error);
            GlobalNotification.error(i18n.t('global_text_operate_failure'));
        } finally {
            setLoading(false);
            runInAction(() => {
                this.isSubmittingCreate = false;
            });
        }
    }

    /**
     * 新建菜单弹窗：删除子类别草稿行（仅本地；不调接口）
     * @param tempId 临时行 id
     */
    removeCreateMenuSubcategoryDraft(tempId: string) {
        this.createMenuSubcategoryDrafts = this.createMenuSubcategoryDrafts.filter((r) => r.tempId !== tempId);
        this.createDialogNextSecondIndex = this.createMenuSubcategoryDrafts.filter((d) => d.dmi_slu_id != null).length;
        if (this.createMenuSubcategoryDetailTempId === tempId) {
            this.createMenuSubcategoryDetailTempId = null;
        }
        if (this.createMenuSubcategoryDetailDishesByTempId[tempId] !== undefined) {
            const rest = { ...this.createMenuSubcategoryDetailDishesByTempId };
            delete rest[tempId];
            this.createMenuSubcategoryDetailDishesByTempId = rest;
        }
    }

    /**
     * 新建菜单弹窗：确认删除子类别——已落库的调 `delete_second_menu`，再移除草稿
     * @param tempId 临时行 id
     */
    async deleteCreateMenuSubcategoryDraftConfirmed(tempId: string) {
        const row = this.createMenuSubcategoryDrafts.find((r) => r.tempId === tempId);
        if (!row) {
            return;
        }
        const mainId = this.createDialogPersistedMainGroupId;
        const cloudMenuId = this.createDialogPersistedCloudMenuId;
        if (this.isCloudCreateDialog && row.cloud_slu_id && cloudMenuId) {
            setLoading(true);
            try {
                const result = await cloudDishLibraryApi.post_cloud_dish_library_menu_update(
                    cloudMenuId,
                    { menu_item_slu_delete: [row.cloud_slu_id] },
                    this.resolveRbpStringIdForApi(),
                );
                if (_.get(result, 'error')) {
                    GlobalNotification.error(i18n.t('global_text_operate_failure'));
                    return;
                }
            } catch (error) {
                console.error('deleteCreateMenuSubcategoryDraftConfirmed cloud failed:', error);
                GlobalNotification.error(i18n.t('global_text_operate_failure'));
                return;
            } finally {
                setLoading(false);
            }
        }
        if (!this.isCloudCreateDialog && row.dmi_slu_id != null && mainId != null) {
            setLoading(true);
            try {
                const r = await apiManage_rbp.delete_second_menu(
                    row.dmi_slu_id,
                    mainId,
                    this.resolveRbpStringIdForApi(),
                );
                if (_.get(r, 'error')) {
                    GlobalNotification.error(i18n.t('global_text_operate_failure'));
                    return;
                }
            } catch (error) {
                console.error('deleteCreateMenuSubcategoryDraftConfirmed failed:', error);
                GlobalNotification.error(i18n.t('global_text_operate_failure'));
                return;
            } finally {
                setLoading(false);
            }
        }
        runInAction(() => {
            this.removeCreateMenuSubcategoryDraft(tempId);
        });
        GlobalNotification.success(i18n.t('global_text_operate_success'));
        await (this.isCloudCreateDialog ? this.refreshCloudMenusAfterMutation() : this.refreshLocalMenusAfterMutation());
    }

    /**
     * 新建菜单弹窗：更新子类别名称
     * @param tempId 临时行 id
     * @param name 名称
     */
    setCreateMenuSubcategoryDraftName(tempId: string, name: string) {
        const idx = this.createMenuSubcategoryDrafts.findIndex((r) => r.tempId === tempId);
        if (idx < 0) {
            return;
        }
        const row = this.createMenuSubcategoryDrafts[idx];
        const next = [...this.createMenuSubcategoryDrafts];
        const applied = applyMainGroupNameInput({
            primary: row.name,
            i18nJson: row.nameI18nJson,
            lang: row.displayLang,
            value: name,
        });
        next[idx] = { ...row, name: applied.primary, nameI18nJson: applied.i18nJson };
        this.createMenuSubcategoryDrafts = next;
    }

    /**
     * 新建菜单弹窗：更新子类别行展示语言（仅切换 `displayLang`；名称展示随 `getMainGroupNameForDisplay` 读对应槽，与 RBP2 `second-menu-detail` 一致）
     * @param tempId 临时行 id
     * @param displayLang 展示语言
     */
    setCreateMenuSubcategoryDraftDisplayLang(tempId: string, displayLang: MenuLibraryDisplayLang) {
        const idx = this.createMenuSubcategoryDrafts.findIndex((r) => r.tempId === tempId);
        if (idx < 0) {
            return;
        }
        const next = [...this.createMenuSubcategoryDrafts];
        next[idx] = { ...next[idx], displayLang };
        this.createMenuSubcategoryDrafts = next;
        console.log(next[idx], 'next[idx]--');

    }

    /**
     * 新建菜单弹窗：更新每人小类点餐数量限制草稿
     * @param tempId 临时行 id
     * @param value 限制文案
     */
    setCreateMenuSubcategoryDraftPerPersonLimit(tempId: string, value: string) {
        const idx = this.createMenuSubcategoryDrafts.findIndex((r) => r.tempId === tempId);
        if (idx < 0) {
            return;
        }
        const next = [...this.createMenuSubcategoryDrafts];
        next[idx] = { ...next[idx], perPersonLimit: value };
        this.createMenuSubcategoryDrafts = next;
    }

    /**
     * 新建菜单弹窗：更新桌台小类点餐数量限制草稿
     * @param tempId 临时行 id
     * @param value 限制文案
     */
    setCreateMenuSubcategoryDraftPerTableLimit(tempId: string, value: string) {
        const idx = this.createMenuSubcategoryDrafts.findIndex((r) => r.tempId === tempId);
        if (idx < 0) {
            return;
        }
        const next = [...this.createMenuSubcategoryDrafts];
        next[idx] = { ...next[idx], perTableLimit: value };
        this.createMenuSubcategoryDrafts = next;
    }

    /** 子类别「每人/桌台」数量限制输入范围（与弹窗校验一致） */
    private static readonly SUBCATEGORY_ORDER_LIMIT_MIN = 0;

    private static readonly SUBCATEGORY_ORDER_LIMIT_MAX = 10000;

    /**
     * 将数量限制字符串解析为 0–10000 的整数；空串为 null（表示未设置）
     */
    private parseSubcategoryOrderLimitToNumberOrNull(raw: string): number | null {
        const t = raw.trim();
        if (t === '') {
            return null;
        }
        if (!/^\d+$/.test(t)) {
            return null;
        }
        const n = parseInt(t, 10);
        if (Number.isNaN(n)) {
            return null;
        }
        return Math.min(
            MenuLibraryManagementState.SUBCATEGORY_ORDER_LIMIT_MAX,
            Math.max(MenuLibraryManagementState.SUBCATEGORY_ORDER_LIMIT_MIN, n),
        );
    }

    /**
     * 与 `persistCreateMenuSubcategoryDraftField` 语义一致：判断失焦值是否与进入编辑态前的值相同（未改则不调接口）
     * @param field 字段
     * @param valueAfterBlur 失焦后的值（名称已 trim 前原样；限制为 clamp 后字符串）
     * @param valueBeforeEdit 进入编辑态时的值
     */
    isCreateMenuSubcategoryFieldUnchangedForPersist(
        field: 'name' | 'perPersonLimit' | 'perTableLimit',
        valueAfterBlur: string,
        valueBeforeEdit: string,
    ): boolean {
        if (field === 'name') {
            return valueAfterBlur.trim() === valueBeforeEdit.trim();
        }
        const a = this.parseSubcategoryOrderLimitToNumberOrNull(valueAfterBlur);
        const b = this.parseSubcategoryOrderLimitToNumberOrNull(valueBeforeEdit);
        return a === b;
    }

    /**
     * 子类别草稿：名称 / 每人限制 / 桌台限制失焦时同步到后端（需已存在 `dmi_slu_id`）
     * @param tempId 临时行 id
     * @param field 字段
     * @param value 当前值
     */
    private resetDetailAddSubcategoryDialogForm() {
        this.detailAddSubcategoryDialogMode = 'create';
        this.detailEditSubcategorySluId = null;
        this.detailEditSubcategoryCloudSluId = null;
        this.detailAddSubcategoryNamePrimary = '';
        this.detailAddSubcategoryNameI18nJson = DEFAULT_MAIN_GROUP_NAME_I18N_JSON;
        this.detailAddSubcategoryDisplayLang = 'unspecified';
        this.detailAddSubcategoryPerPersonLimit = '';
        this.detailAddSubcategoryPerTableLimit = '';
    }

    /** 详情左侧「新增子类别」：打开弹窗 */
    openDetailAddSubcategoryDialog() {
        if (!this.isCloudMenuDetail && (!this.detailMainGroupId || this.detailFirstMenu?.main_group_id == null)) {
            return;
        }
        this.resetDetailAddSubcategoryDialogForm();
        this.detailAddSubcategoryNamePrimary = i18n.t('global_text_Menu');
        this.isDetailAddSubcategoryDialogOpen = true;
    }

    /**
     * 详情左侧「编辑子类别」：打开同一弹窗并回填当前二级菜单（本地按 `dmi_slu_id`）。
     * @param sluId `dmi_slu_id`
     */
    openDetailEditSubcategoryDialog(sluId: number) {
        if (!this.isDetailHeaderEditMode) {
            return;
        }
        if (!this.isCloudMenuDetail && !this.detailFirstMenu?.main_group_id) {
            return;
        }
        const slu = this.detailFirstMenu?.menu_item_slu?.find((s) => s.dmi_slu_id === sluId);
        if (!slu) {
            return;
        }
        this.applyDetailEditSubcategoryDialogFromSlu(slu, { localSluId: sluId, cloudSluId: null });
    }

    /**
     * 云端详情左侧「编辑子类别」：按小类 `_id` 打开弹窗并回填（`dmi_slu_id` 可能均为 0）。
     * @param cloudSluId 云端小类 `_id`
     */
    openDetailEditSubcategoryDialogByCloudSluId(cloudSluId: string) {
        if (!this.isDetailHeaderEditMode) {
            return;
        }
        if (!this.isCloudMenuDetail) {
            return;
        }
        const id = cloudSluId.trim();
        if (!id) {
            return;
        }
        const slu = this.detailFirstMenu?.menu_item_slu?.find(
            (s) => getCloudSluIdFromSecondMenuEntity(s) === id,
        );
        if (!slu) {
            return;
        }
        this.applyDetailEditSubcategoryDialogFromSlu(slu, { localSluId: null, cloudSluId: id });
    }

    /**
     * 将指定小类回填到「新增/编辑子类别」弹窗并打开。
     * @param slu 目标小类
     * @param ids 本地 `dmi_slu_id` 与/或云端 `_id`
     */
    private applyDetailEditSubcategoryDialogFromSlu(
        slu: SecondMenu,
        ids: { localSluId: number | null; cloudSluId: string | null },
    ) {
        const rawI18n = slu.dmi_slu_name_i18n;
        this.detailAddSubcategoryNamePrimary = slu.dmi_slu_name ?? '';
        this.detailAddSubcategoryNameI18nJson =
            rawI18n != null && String(rawI18n).trim() !== ''
                ? normalizeMainGroupNameI18nJson(String(rawI18n))
                : DEFAULT_MAIN_GROUP_NAME_I18N_JSON;
        this.detailAddSubcategoryDisplayLang = 'unspecified';
        const cp = slu.category_order_limit;
        this.detailAddSubcategoryPerPersonLimit =
            cp != null && Number.isFinite(Number(cp)) ? String(cp) : '';
        const ct = slu.table_category_order_limit;
        this.detailAddSubcategoryPerTableLimit =
            ct != null && Number.isFinite(Number(ct)) ? String(ct) : '';
        this.detailEditSubcategorySluId = ids.localSluId;
        this.detailEditSubcategoryCloudSluId = ids.cloudSluId;
        this.detailAddSubcategoryDialogMode = 'edit';
        this.isDetailAddSubcategoryDialogOpen = true;
    }

    closeDetailAddSubcategoryDialog() {
        this.isDetailAddSubcategoryDialogOpen = false;
        this.resetDetailAddSubcategoryDialogForm();
    }

    setDetailAddSubcategoryNameInput(value: string) {
        const next = applyMainGroupNameInput({
            primary: this.detailAddSubcategoryNamePrimary,
            i18nJson: this.detailAddSubcategoryNameI18nJson,
            lang: this.detailAddSubcategoryDisplayLang,
            value,
        });
        this.detailAddSubcategoryNamePrimary = next.primary;
        this.detailAddSubcategoryNameI18nJson = next.i18nJson;
    }

    setDetailAddSubcategoryDisplayLang(lang: MenuLibraryDisplayLang) {
        this.detailAddSubcategoryDisplayLang = lang;
    }

    private static clampDetailSubcategoryLimitDigits(raw: string): string {
        const digits = raw.replace(/\D/g, '');
        if (digits === '') {
            return '';
        }
        const n = parseInt(digits, 10);
        if (Number.isNaN(n)) {
            return '';
        }
        return String(Math.min(10000, Math.max(0, n)));
    }

    setDetailAddSubcategoryPerPersonLimit(v: string) {
        this.detailAddSubcategoryPerPersonLimit =
            MenuLibraryManagementState.clampDetailSubcategoryLimitDigits(v);
    }

    setDetailAddSubcategoryPerTableLimit(v: string) {
        this.detailAddSubcategoryPerTableLimit =
            MenuLibraryManagementState.clampDetailSubcategoryLimitDigits(v);
    }

    private computeNextDetailSecondMenuIndex(): number {
        const slu = this.detailFirstMenu?.menu_item_slu ?? [];
        if (slu.length === 0) {
            return 0;
        }
        const nums = slu
            .map((s) => (s.dmi_slu_index != null ? Number(s.dmi_slu_index) : NaN))
            .filter((n) => Number.isFinite(n));
        if (nums.length === 0) {
            return slu.length;
        }
        return Math.max(...nums) + 1;
    }

    /**
     * 详情「新增 / 编辑子类别」弹窗：提交创建或 PATCH 并刷新详情
     */
    async submitDetailAddSubcategoryDialog() {
        const primary = this.detailAddSubcategoryNamePrimary.trim();
        if (!primary) {
            GlobalNotification.error(i18n.t('menu_library_detail_menu_name_required'));
            return;
        }
        const fm = this.detailFirstMenu;
        const mainId = fm?.main_group_id;
        const cloudMenuId = this.detailCloudMenuId;
        if (!this.isCloudMenuDetail && mainId == null) {
            return;
        }
        if (this.isCloudMenuDetail && !cloudMenuId) {
            return;
        }
        if (this.isDetailAddSubcategorySubmitting) {
            return;
        }
        const i18nJson = normalizeMainGroupNameI18nJson(this.detailAddSubcategoryNameI18nJson);
        const perPerson = this.parseSubcategoryOrderLimitToNumberOrNull(this.detailAddSubcategoryPerPersonLimit);
        const perTable = this.parseSubcategoryOrderLimitToNumberOrNull(this.detailAddSubcategoryPerTableLimit);
        const isEdit = this.detailAddSubcategoryDialogMode === 'edit';
        const editSluId = this.detailEditSubcategorySluId;
        const editCloudSluId = this.detailEditSubcategoryCloudSluId;
        if (isEdit) {
            if (this.isCloudMenuDetail) {
                if (!editCloudSluId || !editCloudSluId.trim()) {
                    return;
                }
            } else if (editSluId == null || !Number.isFinite(editSluId)) {
                return;
            }
        }
        const existingSlu =
            isEdit
                ? this.isCloudMenuDetail && editCloudSluId
                    ? this.detailFirstMenu?.menu_item_slu?.find(
                        (s) => getCloudSluIdFromSecondMenuEntity(s) === editCloudSluId,
                    )
                    : editSluId != null
                        ? this.detailFirstMenu?.menu_item_slu?.find((s) => s.dmi_slu_id === editSluId)
                        : undefined
                : undefined;
        const second: SecondMenu = {
            dmi_slu_name: primary,
            dmi_slu_name_i18n: i18nJson,
            ...(isEdit && existingSlu?.dmi_slu_index != null
                ? { dmi_slu_index: existingSlu.dmi_slu_index }
                : !isEdit
                    ? { dmi_slu_index: this.computeNextDetailSecondMenuIndex() }
                    : {}),
            ...(perPerson != null ? { category_order_limit: perPerson } : {}),
            ...(perTable != null ? { table_category_order_limit: perTable } : {}),
        };
        runInAction(() => {
            this.isDetailAddSubcategorySubmitting = true;
        });
        setLoading(true);
        try {
            if (this.isCloudMenuDetail && cloudMenuId) {
                const cloudSluId = isEdit
                    ? (editCloudSluId ?? this.getCloudSluIdFromSecondMenu(existingSlu))
                    : null;
                const result = await cloudDishLibraryApi.post_cloud_dish_library_menu_update(
                    cloudMenuId,
                    isEdit
                        ? {
                            menu_item_slu_update: cloudSluId
                                ? [{
                                    _id: cloudSluId,
                                    dmi_slu_name: primary,
                                    dmi_slu_name_i18n: i18nJson,
                                    ...(perPerson != null ? { category_order_limit: perPerson } : {}),
                                    ...(perTable != null ? { table_category_order_limit: perTable } : {}),
                                }]
                                : [],
                        }
                        : {
                            menu_item_slu_add: [{
                                dmi_slu_name: primary,
                                dmi_slu_name_i18n: i18nJson,
                                ...(perPerson != null ? { category_order_limit: perPerson } : {}),
                                ...(perTable != null ? { table_category_order_limit: perTable } : {}),
                            }],
                        },
                    this.resolveRbpStringIdForApi(),
                );
                if (_.get(result, 'error') || (isEdit && !cloudSluId)) {
                    GlobalNotification.error(i18n.t('global_text_operate_failure'));
                    return;
                }
                GlobalNotification.success(i18n.t('global_text_operate_success'));
                runInAction(() => {
                    this.isDetailAddSubcategoryDialogOpen = false;
                    this.resetDetailAddSubcategoryDialogForm();
                });
                await this.loadCloudMenuDetailData(cloudMenuId);
                await this.refreshCloudMenusAfterMutation();
                return;
            }
            if (isEdit && editSluId != null) {
                const r = await apiManage_rbp.patch_second_menu(editSluId, second, this.resolveRbpStringIdForApi());
                if (_.get(r, 'error')) {
                    GlobalNotification.error(i18n.t('global_text_operate_failure'));
                    return;
                }
                GlobalNotification.success(i18n.t('global_text_operate_success'));
                runInAction(() => {
                    this.isDetailAddSubcategoryDialogOpen = false;
                    this.resetDetailAddSubcategoryDialogForm();
                });
                await this.loadDetailData(mainId as number);
                await this.refreshLocalMenusAfterMutation();
                runInAction(() => {
                    this.selectedSecondSluId = editSluId;
                });
            } else {
                const r = await apiManage_rbp.post_create_second_menu(mainId as number, second, this.resolveRbpStringIdForApi());
                if (_.get(r, 'error')) {
                    GlobalNotification.error(i18n.t('global_text_operate_failure'));
                    return;
                }
                const insertId = r?.insertId as number | undefined;
                GlobalNotification.success(i18n.t('global_text_operate_success'));
                runInAction(() => {
                    this.isDetailAddSubcategoryDialogOpen = false;
                    this.resetDetailAddSubcategoryDialogForm();
                });
                await this.loadDetailData(mainId as number);
                await this.refreshLocalMenusAfterMutation();
                if (insertId != null) {
                    runInAction(() => {
                        this.selectedSecondSluId = insertId;
                    });
                }
            }
        } catch (e) {
            console.error('submitDetailAddSubcategoryDialog', e);
            GlobalNotification.error(i18n.t('global_text_operate_failure'));
        } finally {
            setLoading(false);
            runInAction(() => {
                this.isDetailAddSubcategorySubmitting = false;
            });
        }
    }

    async persistCreateMenuSubcategoryDraftField(
        tempId: string,
        field: 'name' | 'perPersonLimit' | 'perTableLimit',
        value: string,
    ): Promise<void> {
        const idx = this.createMenuSubcategoryDrafts.findIndex((r) => r.tempId === tempId);
        if (idx < 0) {
            return;
        }
        const draft = this.createMenuSubcategoryDrafts[idx];
        const sid = draft.dmi_slu_id;
        if (!this.isCloudCreateDialog && sid == null) {
            return;
        }
        if (this.isCloudCreateDialog && (!draft.cloud_slu_id || !this.createDialogPersistedCloudMenuId)) {
            return;
        }
        setLoading(true);
        try {
            const primaryName = draft.name.trim();
            if (!primaryName) {
                GlobalNotification.error(i18n.t('menu_library_detail_menu_name_required'));
                return;
            }
            const perPerson =
                field === 'perPersonLimit'
                    ? this.parseSubcategoryOrderLimitToNumberOrNull(value)
                    : this.parseSubcategoryOrderLimitToNumberOrNull(draft.perPersonLimit);
            const perTable =
                field === 'perTableLimit'
                    ? this.parseSubcategoryOrderLimitToNumberOrNull(value)
                    : this.parseSubcategoryOrderLimitToNumberOrNull(draft.perTableLimit);
            const patch: SecondMenu = {
                dmi_slu_name: primaryName,
                dmi_slu_name_i18n: normalizeMainGroupNameI18nJson(draft.nameI18nJson),
                ...(perPerson != null ? { category_order_limit: perPerson } : {}),
                ...(perTable != null ? { table_category_order_limit: perTable } : {}),
            };
            const result = this.isCloudCreateDialog
                ? await cloudDishLibraryApi.post_cloud_dish_library_menu_update(
                    this.createDialogPersistedCloudMenuId ?? '',
                    {
                        menu_item_slu_update: draft.cloud_slu_id
                            ? [{
                                _id: draft.cloud_slu_id,
                                dmi_slu_name: primaryName,
                                dmi_slu_name_i18n: normalizeMainGroupNameI18nJson(draft.nameI18nJson),
                                ...(perPerson != null ? { category_order_limit: perPerson } : {}),
                                ...(perTable != null ? { table_category_order_limit: perTable } : {}),
                            }]
                            : [],
                    },
                    this.resolveRbpStringIdForApi(),
                )
                : await apiManage_rbp.patch_second_menu(sid as number, patch, this.resolveRbpStringIdForApi());
            if (_.get(result, 'error')) {
                GlobalNotification.error(i18n.t('global_text_operate_failure'));
                return;
            }
            await (this.isCloudCreateDialog ? this.refreshCloudMenusAfterMutation() : this.refreshLocalMenusAfterMutation());
        } catch (error) {
            console.error('persistCreateMenuSubcategoryDraftField failed:', error);
            GlobalNotification.error(i18n.t('global_text_operate_failure'));
        } finally {
            setLoading(false);
        }
    }

    /**
     * 是否处于菜单详情视图（本地或云端）
     */
    get isMenuDetailOpen(): boolean {
        return this.detailSource === 'cloud'
            ? !!this.detailCloudMenuId
            : this.detailMainGroupId != null;
    }

    /**
     * 当前是否为云端菜单详情
     */
    get isCloudMenuDetail(): boolean {
        return this.detailSource === 'cloud' && !!this.detailCloudMenuId;
    }

    /** 当前新建弹窗是否正在编辑云端菜单。 */
    get isCloudCreateDialog(): boolean {
        return this.createSource === 'cloud';
    }

    /**
     * 云端侧是否应请求隧道下拉 API（§5.5 / `print_class`）。
     * 仅当 `check-permission` 已通过（`menuLibraryTunnelPermissionOk === true`）；版本门禁不影响本判断。
     */
    get shouldFetchCloudTunnelDropdownApis(): boolean {
        return this.menuLibraryTunnelPermissionOk === true;
    }

    /**
     * 从云端小类详情对象中读取真实 `_id`。
     * @param sm 当前小类对象
     * @returns 云端小类 id；无有效值时为 null
     */
    getCloudSluIdFromSecondMenu(sm: SecondMenu | null | undefined): string | null {
        return getCloudSluIdFromSecondMenuEntity(sm);
    }

    /** 当前选中云端小类的真实 `_id`。 */
    getCurrentCloudSluId(): string | null {
        return this.getCloudSluIdFromSecondMenu(this.getCurrentSecondMenu());
    }

    /**
     * 云端详情 / 云端新建弹窗写操作门禁：接口未就绪时 toast 并拦截。
     * （新建弹窗内菜品保存等已统一走本方法，故同时识别 `createSource === 'cloud'`。）
     * @returns true 表示已拦截，调用方应直接 return
     */
    notifyCloudDetailApiNotReady(): boolean {
        if (!this.isCloudMenuDetail && this.createSource !== 'cloud') {
            return false;
        }
        GlobalNotification.info(i18n.t('menu_library_cloud_detail_api_not_ready'));
        return true;
    }

    /**
     * 云端新建弹窗写操作门禁（保存 / 新增子类别 / 子类失焦 PATCH 等）。
     * @returns true 表示已拦截，调用方应直接 return
     */
    notifyCloudCreateWriteApiNotReady(): boolean {
        if (this.createSource !== 'cloud') {
            return false;
        }
        GlobalNotification.info(i18n.t('menu_library_cloud_detail_api_not_ready'));
        return true;
    }

    /**
     * @deprecated 云端列表「新增云端菜单」已改为 `handleAddCloud` 打开同壳弹窗；保留供兼容。
     */
    notifyCloudCreateMenuApiNotReady(): void {
        GlobalNotification.info(i18n.t('menu_library_cloud_detail_api_not_ready'));
    }

    /**
     * 进入当前模块内的菜单详情编辑
     */
    openMenuDetail(mainGroupId: number) {
        this.clearDetailSecondMenuReorderDebounceTimer();
        this.detailSecondMenuReorderDirtySinceLastPersist = false;
        this.clearDetailDishReorderDebounceTimer();
        this.detailDishReorderDirtySinceLastPersist = false;
        this.detailFirstLoadCompleted = false;
        this.detailSource = 'local';
        this.detailCloudMenuId = null;
        this.detailMainGroupId = mainGroupId;
        this.selectedCloudSluId = null;
        this.isDetailHeaderEditMode = false;
        this.detailHeaderMenuNameDisplayLang = 'unspecified';
        this.detailHeaderMenuNamePrimary = '';
        this.detailHeaderMenuNameI18nJson = '';
        this.detailHeaderMaxLimitDraft = '';
        this.detailHeaderHolidayMenuEnabled = false;
        this.storeHolidayMenuOpen = false;
        this.storeHolidayMenuToggleLoading = false;
        this.storeHolidayMenuSettingFetchLoading = false;
        this.editExtraNormalIds = [];
        this.editExtraChildIds = [];
        this.editExtraOtherIds = [];
        this.detailEditPriceValueDraft = null;
        this.selectedWeek = 1;
        this.menuDisplayLang = 'unspecified';
        this.dishSearchKeyword = '';
        this.isDishSearchDialogOpen = false;
        this.isFilterExpanded = false;
        this.isDishFilterDialogOpen = false;
        this.resetDishFilterCriteria();
        this.isDetailAddSubcategoryDialogOpen = false;
        this.isDetailAddSubcategorySubmitting = false;
        this.detailDeleteConfirmType = null;
        this.detailDeleteSubmitting = false;
        this.resetDetailAddSubcategoryDialogForm();
        void this.loadDetailData(mainGroupId);
    }

    /**
     * 进入云端菜单详情（读：GET 详情；写：接口未就绪时 toast，方案 B）
     * @param cloudMenuId - 云端菜单 id
     */
    openCloudMenuDetail(cloudMenuId: string) {
        const id = cloudMenuId.trim();
        if (!id) {
            return;
        }
        this.clearDetailSecondMenuReorderDebounceTimer();
        this.detailSecondMenuReorderDirtySinceLastPersist = false;
        this.clearDetailDishReorderDebounceTimer();
        this.detailDishReorderDirtySinceLastPersist = false;
        this.detailFirstLoadCompleted = false;
        this.detailSource = 'cloud';
        this.detailCloudMenuId = id;
        this.detailMainGroupId = null;
        this.selectedSecondSluId = null;
        this.selectedCloudSluId = null;
        this.isDetailHeaderEditMode = false;
        this.detailHeaderMenuNameDisplayLang = 'unspecified';
        this.detailHeaderMenuNamePrimary = '';
        this.detailHeaderMenuNameI18nJson = '';
        this.detailHeaderMaxLimitDraft = '';
        this.detailHeaderHolidayMenuEnabled = false;
        this.storeHolidayMenuOpen = false;
        this.storeHolidayMenuToggleLoading = false;
        this.storeHolidayMenuSettingFetchLoading = false;
        this.editExtraNormalIds = [];
        this.editExtraChildIds = [];
        this.editExtraOtherIds = [];
        this.detailEditPriceValueDraft = null;
        this.selectedWeek = 1;
        this.menuDisplayLang = 'unspecified';
        this.dishSearchKeyword = '';
        this.isDishSearchDialogOpen = false;
        this.isFilterExpanded = false;
        this.isDishFilterDialogOpen = false;
        this.resetDishFilterCriteria();
        this.isDetailAddSubcategoryDialogOpen = false;
        this.isDetailAddSubcategorySubmitting = false;
        this.detailDeleteConfirmType = null;
        this.detailDeleteSubmitting = false;
        this.resetDetailAddSubcategoryDialogForm();
        this.extraMenuData = [];
        this.printGroupMap = { [-1]: i18n.t('print_setting_page_none0') };
        void this.loadCloudMenuDetailData(id);
    }

    /**
     * 打开详情单项删除确认弹窗。
     * @param type - 删除当前菜单或当前小类
     */
    openDetailDeleteConfirm(type: 'menu' | 'subcategory') {
        const currentSecond = this.getCurrentSecondMenu();
        const canDeleteMenu = canDeleteEmptyMenu(this.detailFirstMenu);
        const canDeleteSubcategory = canDeleteEmptySubcategory(currentSecond);
        if ((type === 'menu' && !canDeleteMenu) || (type === 'subcategory' && !canDeleteSubcategory)) {
            return;
        }
        this.detailDeleteConfirmType = type;
    }

    /**
     * 关闭详情单项删除确认弹窗。
     */
    closeDetailDeleteConfirm() {
        if (!this.detailDeleteSubmitting) {
            this.detailDeleteConfirmType = null;
        }
    }

    /**
     * 确认删除详情中的空菜单或空小类。
     */
    async confirmDetailDelete() {
        const type = this.detailDeleteConfirmType;
        const sid = this.resolveRbpStringIdForApi().trim();
        const fm = this.detailFirstMenu;
        const second = this.getCurrentSecondMenu();
        if (!type || !fm || this.detailDeleteSubmitting) {
            return;
        }
        if (!sid) {
            GlobalNotification.error(i18n.t('business_analysis_page_none_restaurant'));
            return;
        }
        if (type === 'menu' && !canDeleteEmptyMenu(fm)) {
            return;
        }
        if (type === 'subcategory' && !canDeleteEmptySubcategory(second)) {
            return;
        }
        if (
            (type === 'menu' && !this.isCloudMenuDetail && fm.main_group_id == null) ||
            (type === 'menu' && this.isCloudMenuDetail && !this.detailCloudMenuId) ||
            (type === 'subcategory' &&
                (!second ||
                    (this.isCloudMenuDetail
                        ? !this.getCloudSluIdFromSecondMenu(second)
                        : second.dmi_slu_id == null)))
        ) {
            GlobalNotification.error(i18n.t('global_text_operate_failure'));
            return;
        }
        this.detailDeleteSubmitting = true;
        try {
            if (type === 'menu') {
                if (this.isCloudMenuDetail) {
                    const result = await cloudDishLibraryApi.post_cloud_dish_library_menu_delete(
                        this.detailCloudMenuId as string,
                        sid,
                    );
                    const parsed = normalizeCloudDeleteResult(result);
                    const notFound = parsed?.not_found_ids?.includes(this.detailCloudMenuId as string);
                    if (_.get(result, 'error') || parsed?.deleted_menu_count !== 1 || notFound) {
                        throw new Error('cloud menu deletion was not completed');
                    }
                } else {
                    const result = await apiManage_rbp.delete_first_menu(fm.main_group_id as number, sid);
                    if (!isLocalMenuDeleteSuccess(result)) {
                        throw new Error('local menu deletion was not completed');
                    }
                }
                this.detailDeleteConfirmType = null;
                this.closeMenuDetail();
                return;
            }
            if (this.isCloudMenuDetail) {
                const cloudSluId = this.getCloudSluIdFromSecondMenu(second);
                if (!cloudSluId || !this.detailCloudMenuId) {
                    return;
                }
                const result = await cloudDishLibraryApi.post_cloud_dish_library_subcategory_delete(
                    this.detailCloudMenuId,
                    cloudSluId,
                    sid,
                );
                if (_.get(result, 'error')) {
                    throw new Error('cloud subcategory deletion was not completed');
                }
                await this.loadCloudMenuDetailData(this.detailCloudMenuId);
            } else {
                const result = await apiManage_rbp.delete_second_menu(
                    second?.dmi_slu_id as number,
                    fm.main_group_id as number,
                    sid,
                );
                if (!isLocalMenuDeleteSuccess(result)) {
                    throw new Error('local subcategory deletion was not completed');
                }
                await this.loadDetailData(fm.main_group_id as number);
            }
            this.detailDeleteConfirmType = null;
        } catch (error) {
            console.error('confirmDetailDelete failed:', error);
            GlobalNotification.error(i18n.t('global_text_operate_failure'));
        } finally {
            this.detailDeleteSubmitting = false;
        }
    }

    /**
     * 拉取云端菜单详情并写入 `detailFirstMenu`；并联拉取岗位打印组写入 `printGroupMap`（与本地 `loadDetailData` 一致，供菜品表列显示名称）。
     * 云端详情在本地 FirstMenu 字段上扩展 id，直接按本地逻辑展示，不做字段重映射。
     * @param cloudMenuId - 云端菜单 id
     */
    async loadCloudMenuDetailData(cloudMenuId: string) {
        this.detailLoading = true;
        setLoading(true);
        try {
            const sid = this.resolveRbpStringIdForApi();
            // 详情 GET；print_class 仅隧道 check-permission 通过时请求（失败/跳过则列名空）。云端不读 setting 22。
            const printPromise = this.shouldFetchCloudTunnelDropdownApis
                ? apiManage_rbp.getPrinterClass(sid).catch((e: unknown) => {
                      console.error('loadCloudMenuDetailData getPrinterClass', e);
                      return null;
                  })
                : Promise.resolve(null);
            const [r, printRes] = await Promise.all([
                apiManage_rbp.get_cloud_dish_library_menu_detail(cloudMenuId, sid),
                printPromise,
            ]);
            // 与本地 loadDetailData 相同：id -> 名称；至少保留「无」
            const printMap: Record<number, string> = { [-1]: i18n.t('print_setting_page_none0') };
            if (!_.get(printRes, 'error') && Array.isArray(printRes)) {
                (printRes as { print_class_id?: number; print_class_name?: string }[]).forEach((p) => {
                    if (p.print_class_id !== undefined && p.print_class_name) {
                        printMap[p.print_class_id] = p.print_class_name;
                    }
                });
            }
            if (_.get(r, 'error')) {
                GlobalNotification.error(i18n.t('global_text_operate_failure'));
                runInAction(() => {
                    this.detailFirstMenu = null;
                    this.selectedSecondSluId = null;
                    this.selectedCloudSluId = null;
                    this.printGroupMap = printMap;
                });
                return;
            }
            // 与本地同构（含 price_1 / takeout_price_1 等）；仅多云端 id 扩展字段
            const firstMenu = r as FirstMenu;
            const secondList = firstMenu.menu_item_slu || [];
            const defaultCloudSluId = getCloudSluIdFromSecondMenuEntity(secondList[0]) ?? null;
            const prevCloudSluId = this.selectedCloudSluId;
            const cloudSluIds = new Set(
                secondList
                    .map((s) => getCloudSluIdFromSecondMenuEntity(s))
                    .filter((id): id is string => !!id),
            );
            const keepSelection = prevCloudSluId != null && cloudSluIds.has(prevCloudSluId);
            runInAction(() => {
                this.detailFirstMenu = firstMenu;
                // 云端主键为 detailCloudMenuId，不以本地 main_group_id 为准
                this.detailMainGroupId = null;
                this.selectedSecondSluId = null;
                this.selectedCloudSluId = keepSelection ? prevCloudSluId : defaultCloudSluId;
                this.printGroupMap = printMap;
                this.extraMenuData = [];
                this.storeHolidayMenuOpen = false;
            });
        } catch (e) {
            console.error('loadCloudMenuDetailData', e);
            GlobalNotification.error(i18n.t('global_text_operate_failure'));
            runInAction(() => {
                this.detailFirstMenu = null;
                this.selectedSecondSluId = null;
                this.selectedCloudSluId = null;
            });
        } finally {
            runInAction(() => {
                this.detailLoading = false;
                this.detailFirstLoadCompleted = true;
            });
            setLoading(false);
        }
    }

    /**
     * 返回一级菜单卡片列表
     */
    closeMenuDetail() {
        this.closeCreateMenuSubcategoryAddDishDialog();
        this.clearDetailSecondMenuReorderDebounceTimer();
        this.clearDetailDishReorderDebounceTimer();
        const isCloudDetailSnapshot = this.detailSource === 'cloud';
        /** 关闭详情前冻结子类别排序门禁，避免重置详情状态后绕过本地 setting 103 */
        const secondMenuReorderEnabledSnapshot = this.isDetailSecondMenuPrioritySortEnabled;
        const fmSnapshot = this.detailFirstMenu;
        const snapshotSelectedSluId = this.selectedSecondSluId;
        const snapshotSelectedCloudSluId = this.selectedCloudSluId;
        const cloudMenuIdSnapshot = this.detailCloudMenuId;
        const reorderPayload =
            !isCloudDetailSnapshot &&
            secondMenuReorderEnabledSnapshot &&
            this.detailSecondMenuReorderDirtySinceLastPersist &&
            fmSnapshot
                ? this.buildDetailSecondMenuIndexPayload(fmSnapshot)
                : null;
        const reorderGid =
            reorderPayload &&
                reorderPayload.length > 0 &&
                fmSnapshot?.main_group_id != null &&
                Number.isFinite(Number(fmSnapshot.main_group_id))
                ? (fmSnapshot.main_group_id as number)
                : null;
        const cloudSluReorderPayload =
            isCloudDetailSnapshot &&
            this.detailSecondMenuReorderDirtySinceLastPersist &&
            fmSnapshot &&
            cloudMenuIdSnapshot
                ? buildCloudMenuItemSluIndexUpdateBody(fmSnapshot.menu_item_slu)
                : null;
        this.detailSecondMenuReorderDirtySinceLastPersist = false;
        const dishReorderPayload =
            !isCloudDetailSnapshot &&
            this.detailDishReorderDirtySinceLastPersist &&
            fmSnapshot &&
            snapshotSelectedSluId != null &&
            Array.isArray(fmSnapshot.menu_item_slu)
                ? this.buildDetailDishIndexPayloadFromMenuItems(
                    fmSnapshot.menu_item_slu.find((s) => s.dmi_slu_id === snapshotSelectedSluId)?.menu_item,
                )
                : null;
        /** 关闭详情时 flush 云端菜品排序（`dishes/sort`） */
        const cloudDishSortBody =
            isCloudDetailSnapshot &&
            this.detailDishReorderDirtySinceLastPersist &&
            fmSnapshot &&
            cloudMenuIdSnapshot &&
            snapshotSelectedCloudSluId
                ? buildCloudDishLibraryDishesSortBody(
                    snapshotSelectedCloudSluId,
                    fmSnapshot.menu_item_slu?.find(
                        (s) => getCloudSluIdFromSecondMenuEntity(s) === snapshotSelectedCloudSluId,
                    )?.menu_item,
                )
                : null;
        this.detailDishReorderDirtySinceLastPersist = false;
        this.detailSource = 'local';
        this.detailCloudMenuId = null;
        this.detailMainGroupId = null;
        this.detailFirstMenu = null;
        this.storeHolidayMenuOpen = false;
        this.storeHolidayMenuToggleLoading = false;
        this.storeHolidayMenuSettingFetchLoading = false;
        this.isDetailAddSubcategoryDialogOpen = false;
        this.isDetailAddSubcategorySubmitting = false;
        this.resetDetailAddSubcategoryDialogForm();
        this.extraMenuData = [];
        this.selectedSecondSluId = null;
        this.selectedCloudSluId = null;
        this.detailLoading = false;
        this.detailFirstLoadCompleted = false;
        this.isDishFilterDialogOpen = false;
        this.isDishSearchDialogOpen = false;
        this.resetDishFilterCriteria();
        this.isDetailSecondMenuReorderSaving = false;
        this.isDetailDishReorderSaving = false;
        if (reorderGid != null && reorderPayload && reorderPayload.length > 0) {
            void this.patchDetailSecondMenuIndex(reorderGid, reorderPayload);
        }
        if (
            cloudMenuIdSnapshot &&
            cloudSluReorderPayload &&
            cloudSluReorderPayload.length > 0
        ) {
            void this.postCloudDetailSecondMenuIndex(cloudMenuIdSnapshot, cloudSluReorderPayload);
        }
        if (dishReorderPayload && dishReorderPayload.length > 0) {
            void this.patchDetailDishFirstMenuIndex(dishReorderPayload);
        }
        if (cloudMenuIdSnapshot && cloudDishSortBody) {
            void this.postCloudDetailDishSort(cloudMenuIdSnapshot, cloudDishSortBody);
        }
        void (
            isCloudDetailSnapshot
                ? this.refreshCloudMenusAfterMutation()
                : this.refreshLocalMenusAfterMutation()
        );
    }

    /**
     * 子分类列表拖拽落位：立即更新本地顺序，经防抖后落库。
     * 本地：`PATCH /v3/menus/second/index`；云端：`POST .../menus/{id}/update` 的 `menu_item_slu_update[].dmi_slu_index`。
     */
    applyDetailSecondMenuDragReorder(fromIndex: number, toIndex: number) {
        if (!this.isDetailSecondMenuPrioritySortEnabled) {
            this.clearDetailSecondMenuReorderDebounceTimer();
            this.detailSecondMenuReorderDirtySinceLastPersist = false;
            return;
        }
        const fm = this.detailFirstMenu;
        if (!fm?.menu_item_slu?.length) {
            return;
        }
        /** 本地须有 main_group_id；云端须有 detailCloudMenuId */
        if (this.isCloudMenuDetail) {
            if (!this.detailCloudMenuId) {
                return;
            }
        } else if (fm.main_group_id == null) {
            return;
        }
        if (fromIndex === toIndex || fromIndex < 0 || toIndex < 0) {
            return;
        }
        const len = fm.menu_item_slu.length;
        if (fromIndex >= len || toIndex >= len) {
            return;
        }
        const prev = fm.menu_item_slu.slice();
        const next = prev.slice();
        const [moved] = next.splice(fromIndex, 1);
        next.splice(toIndex, 0, moved);
        runInAction(() => {
            fm.menu_item_slu = next;
            next.forEach((slu, i) => {
                slu.dmi_slu_index = i + 1;
            });
        });
        this.detailSecondMenuReorderDirtySinceLastPersist = true;
        this.schedulePersistDetailSecondMenuOrderAfterDebounce();
    }

    /** 清除子类顺序防抖定时器（不发起 PATCH） */
    private clearDetailSecondMenuReorderDebounceTimer() {
        if (this.detailSecondMenuReorderDebounceTimer != null) {
            clearTimeout(this.detailSecondMenuReorderDebounceTimer);
            this.detailSecondMenuReorderDebounceTimer = null;
        }
    }

    /** 重置防抖计时：在静默期结束后由 `persistDetailSecondMenuOrderToServer` 落库 */
    private schedulePersistDetailSecondMenuOrderAfterDebounce() {
        this.clearDetailSecondMenuReorderDebounceTimer();
        if (!this.isDetailSecondMenuPrioritySortEnabled) {
            this.detailSecondMenuReorderDirtySinceLastPersist = false;
            return;
        }
        this.detailSecondMenuReorderDebounceTimer = setTimeout(() => {
            this.detailSecondMenuReorderDebounceTimer = null;
            void this.persistDetailSecondMenuOrderToServer();
        }, this.DETAIL_SECOND_MENU_REORDER_DEBOUNCE_MS);
    }

    /**
     * 从当前 `detailFirstMenu.menu_item_slu` 生成 `PATCH menus/second/index` 的 body（index 从 1 连续）
     */
    private buildDetailSecondMenuIndexPayload(fm: FirstMenu): SecondMenu[] | null {
        const slus = fm.menu_item_slu;
        if (!slus?.length) {
            return null;
        }
        return slus.map((slu, i) => ({
            dmi_slu_id: slu.dmi_slu_id,
            dmi_slu_index: i + 1,
        }));
    }

    /**
     * 执行子类顺序 PATCH（与拖拽落库同源）；关闭详情时可能已无 `detailFirstMenu`，故单独提供 payload
     */
    private async patchDetailSecondMenuIndex(mainGroupId: number, payload: SecondMenu[]) {
        runInAction(() => {
            this.isDetailSecondMenuReorderSaving = true;
        });
        try {
            const res = await apiManage_rbp.patch_change_second_menu_index(payload, this.resolveRbpStringIdForApi());
            if (_.get(res, 'error')) {
                GlobalNotification.error(i18n.t('global_text_operate_failure'));
                if (this.detailMainGroupId != null && this.detailMainGroupId === mainGroupId) {
                    await this.loadDetailData(mainGroupId);
                } else {
                    void this.refreshLocalMenusAfterMutation();
                }
                return;
            }
            GlobalNotification.success(i18n.t('global_text_operate_success'));
        } catch (e) {
            console.error('patchDetailSecondMenuIndex', e);
            GlobalNotification.error(i18n.t('global_text_operate_failure'));
            if (this.detailMainGroupId != null && this.detailMainGroupId === mainGroupId) {
                await this.loadDetailData(mainGroupId);
            } else {
                void this.refreshLocalMenusAfterMutation();
            }
        } finally {
            runInAction(() => {
                this.isDetailSecondMenuReorderSaving = false;
            });
        }
    }

    /**
     * 云端详情：将小类顺序写入 `POST .../menus/{cloud_menu_id}/update`（`menu_item_slu_update[].dmi_slu_index`）。
     * @param cloudMenuId - 云端菜单 id（关闭详情时可能已清空 state，故由调用方传入快照）
     * @param updates - `_id` + `dmi_slu_index` 列表
     */
    private async postCloudDetailSecondMenuIndex(
        cloudMenuId: string,
        updates: CloudDishLibraryUpdateMenuSluUpdateItem[],
    ) {
        runInAction(() => {
            this.isDetailSecondMenuReorderSaving = true;
        });
        try {
            const res = await cloudDishLibraryApi.post_cloud_dish_library_menu_update(
                cloudMenuId,
                { menu_item_slu_update: updates },
                this.resolveRbpStringIdForApi(),
            );
            if (_.get(res, 'error')) {
                GlobalNotification.error(i18n.t('global_text_operate_failure'));
                if (this.isCloudMenuDetail && this.detailCloudMenuId === cloudMenuId) {
                    await this.loadCloudMenuDetailData(cloudMenuId);
                } else {
                    void this.refreshCloudMenusAfterMutation();
                }
                return;
            }
            GlobalNotification.success(i18n.t('global_text_operate_success'));
        } catch (e) {
            console.error('postCloudDetailSecondMenuIndex', e);
            GlobalNotification.error(i18n.t('global_text_operate_failure'));
            if (this.isCloudMenuDetail && this.detailCloudMenuId === cloudMenuId) {
                await this.loadCloudMenuDetailData(cloudMenuId);
            } else {
                void this.refreshCloudMenusAfterMutation();
            }
        } finally {
            runInAction(() => {
                this.isDetailSecondMenuReorderSaving = false;
            });
        }
    }

    /**
     * 若有未落库的子类顺序（含防抖等待中），立即清除定时器并 PATCH；用于取消顶栏编辑等场景
     */
    flushPendingDetailSecondMenuReorderNow() {
        this.clearDetailSecondMenuReorderDebounceTimer();
        if (!this.isDetailSecondMenuPrioritySortEnabled) {
            this.detailSecondMenuReorderDirtySinceLastPersist = false;
            return;
        }
        void this.persistDetailSecondMenuOrderToServer();
    }

    /**
     * 将 `detailSecondMenuReorderDirtySinceLastPersist` 对应的当前列表顺序写入服务端；无脏数据时立即返回。
     * 本地走 `PATCH menus/second/index`；云端走 `POST .../menus/{id}/update`（`dmi_slu_index`）。
     */
    async persistDetailSecondMenuOrderToServer() {
        if (!this.isDetailSecondMenuPrioritySortEnabled) {
            this.clearDetailSecondMenuReorderDebounceTimer();
            this.detailSecondMenuReorderDirtySinceLastPersist = false;
            return;
        }
        if (!this.detailSecondMenuReorderDirtySinceLastPersist) {
            return;
        }
        /** 允许拖拽与请求重叠：等当前请求结束后再落库，避免并发写 */
        if (this.isDetailSecondMenuReorderSaving) {
            await when(() => !this.isDetailSecondMenuReorderSaving);
            if (!this.detailSecondMenuReorderDirtySinceLastPersist) {
                return;
            }
            if (!this.isDetailSecondMenuPrioritySortEnabled) {
                this.clearDetailSecondMenuReorderDebounceTimer();
                this.detailSecondMenuReorderDirtySinceLastPersist = false;
                return;
            }
        }
        this.detailSecondMenuReorderDirtySinceLastPersist = false;
        const fm = this.detailFirstMenu;
        if (!fm?.menu_item_slu?.length) {
            return;
        }
        if (this.detailSource === 'cloud') {
            const cloudMenuId = this.detailCloudMenuId;
            if (!cloudMenuId) {
                return;
            }
            const updates = buildCloudMenuItemSluIndexUpdateBody(fm.menu_item_slu);
            if (!updates.length) {
                return;
            }
            await this.postCloudDetailSecondMenuIndex(cloudMenuId, updates);
            return;
        }
        const gid = fm.main_group_id;
        if (gid == null) {
            return;
        }
        const payload = this.buildDetailSecondMenuIndexPayload(fm);
        if (!payload?.length) {
            return;
        }
        await this.patchDetailSecondMenuIndex(gid as number, payload);
    }

    /** 清除详情菜品顺序防抖定时器 */
    private clearDetailDishReorderDebounceTimer() {
        if (this.detailDishReorderDebounceTimer != null) {
            clearTimeout(this.detailDishReorderDebounceTimer);
            this.detailDishReorderDebounceTimer = null;
        }
    }

    /** 详情菜品顺序：防抖后 `persistDetailDishOrderToServer` */
    private schedulePersistDetailDishOrderAfterDebounce() {
        this.clearDetailDishReorderDebounceTimer();
        this.detailDishReorderDebounceTimer = setTimeout(() => {
            this.detailDishReorderDebounceTimer = null;
            void this.persistDetailDishOrderToServer();
        }, this.DETAIL_DISH_REORDER_DEBOUNCE_MS);
    }

    /**
     * 当前子类 `menu_item` 生成 `PATCH /v3/menus/first/index` 的 body（仅含已落库 `item_id`；`slu_priority = length - index`）
     */
    private buildDetailDishIndexPayloadFromMenuItems(
        items: MenuDish[] | undefined,
    ): Array<{ item_id: number; slu_priority: number }> | null {
        return buildDetailDishSluPriorityIndexPayload(items);
    }

    /**
     * 执行菜品顺序 PATCH（`{ item_id, slu_priority }[]`）；关闭详情后 `detailMainGroupId` 可能已为 null
     */
    private async patchDetailDishFirstMenuIndex(payload: Array<{ item_id: number; slu_priority: number }>) {
        runInAction(() => {
            this.isDetailDishReorderSaving = true;
        });
        const gid = this.detailMainGroupId;
        try {
            const res = await apiManage_rbp.patch_menus_first_index_dish_priorities(
                payload,
                this.resolveRbpStringIdForApi(),
            );
            if (_.get(res, 'error')) {
                GlobalNotification.error(i18n.t('global_text_operate_failure'));
                if (gid != null) {
                    await this.loadDetailData(gid);
                } else {
                    void this.refreshLocalMenusAfterMutation();
                }
                return;
            }
            GlobalNotification.success(i18n.t('global_text_operate_success'));
        } catch (e) {
            console.error('patchDetailDishFirstMenuIndex', e);
            GlobalNotification.error(i18n.t('global_text_operate_failure'));
            if (gid != null) {
                await this.loadDetailData(gid);
            } else {
                void this.refreshLocalMenusAfterMutation();
            }
        } finally {
            runInAction(() => {
                this.isDetailDishReorderSaving = false;
            });
        }
        if (this.detailDishReorderDirtySinceLastPersist) {
            await this.persistDetailDishOrderToServer();
        }
    }

    /**
     * 云端详情：将当前小类菜品顺序写入 `POST .../menus/{cloud_menu_id}/dishes/sort`。
     * @param cloudMenuId - 云端菜单 id（关闭详情时可能已清空 state，故由调用方传入快照）
     * @param body - `cloud_slu_id` + `dishes`
     */
    private async postCloudDetailDishSort(cloudMenuId: string, body: CloudDishLibraryDishesSortBody) {
        runInAction(() => {
            this.isDetailDishReorderSaving = true;
        });
        try {
            const res = await cloudDishLibraryApi.post_cloud_dish_library_dishes_sort(
                cloudMenuId,
                body,
                this.resolveRbpStringIdForApi(),
            );
            if (_.get(res, 'error')) {
                GlobalNotification.error(i18n.t('global_text_operate_failure'));
                if (this.isCloudMenuDetail && this.detailCloudMenuId === cloudMenuId) {
                    await this.loadCloudMenuDetailData(cloudMenuId);
                } else {
                    void this.refreshCloudMenusAfterMutation();
                }
                return;
            }
            GlobalNotification.success(i18n.t('global_text_operate_success'));
        } catch (e) {
            console.error('postCloudDetailDishSort', e);
            GlobalNotification.error(i18n.t('global_text_operate_failure'));
            if (this.isCloudMenuDetail && this.detailCloudMenuId === cloudMenuId) {
                await this.loadCloudMenuDetailData(cloudMenuId);
            } else {
                void this.refreshCloudMenusAfterMutation();
            }
        } finally {
            runInAction(() => {
                this.isDetailDishReorderSaving = false;
            });
        }
        if (this.detailDishReorderDirtySinceLastPersist) {
            await this.persistDetailDishOrderToServer();
        }
    }

    /**
     * 若有未落库的详情菜品顺序，立即清除定时器并 PATCH（用于取消顶栏编辑等）
     */
    flushPendingDetailDishReorderNow() {
        this.clearDetailDishReorderDebounceTimer();
        void this.persistDetailDishOrderToServer();
    }

    /**
     * 将 `detailDishReorderDirtySinceLastPersist` 对应当前子类菜品顺序写入服务端。
     * 本地：`PATCH menus/first/index`；云端：`POST .../dishes/sort`。
     */
    async persistDetailDishOrderToServer() {
        if (!this.detailDishReorderDirtySinceLastPersist) {
            return;
        }
        if (this.isDetailDishReorderSaving) {
            await when(() => !this.isDetailDishReorderSaving);
            if (!this.detailDishReorderDirtySinceLastPersist) {
                return;
            }
        }
        this.detailDishReorderDirtySinceLastPersist = false;
        const sm = this.getCurrentSecondMenu();
        if (this.detailSource === 'cloud') {
            const cloudMenuId = this.detailCloudMenuId;
            const cloudSluId = this.getCloudSluIdFromSecondMenu(sm);
            if (!cloudMenuId || !cloudSluId) {
                return;
            }
            const body = buildCloudDishLibraryDishesSortBody(cloudSluId, sm?.menu_item);
            if (!body) {
                return;
            }
            await this.postCloudDetailDishSort(cloudMenuId, body);
            return;
        }
        const payload = this.buildDetailDishIndexPayloadFromMenuItems(sm?.menu_item);
        if (!payload?.length) {
            return;
        }
        await this.patchDetailDishFirstMenuIndex(payload);
    }

    /**
     * 菜单库详情：当前子类下菜品行拖拽落位（本地立即改序；本地防抖 PATCH first/index，云端防抖 dishes/sort）
     */
    applyDetailDishDragReorderInSelectedSlu(fromIndex: number, toIndex: number) {
        if (this.detailSource === 'cloud' && !this.detailCloudMenuId) {
            return;
        }
        const sm = this.getCurrentSecondMenu();
        if (!sm || !sm.menu_item?.length) {
            return;
        }
        const list = sm.menu_item;
        if (fromIndex === toIndex || fromIndex < 0 || toIndex < 0) {
            return;
        }
        const len = list.length;
        if (fromIndex >= len || toIndex >= len) {
            return;
        }
        const next = list.slice();
        const [moved] = next.splice(fromIndex, 1);
        next.splice(toIndex, 0, moved);
        runInAction(() => {
            sm.menu_item = next;
            /** 与 RBP2 一致：首行 `slu_priority` 最大 */
            const len = next.length;
            next.forEach((d, i) => {
                d.slu_priority = len - i;
            });
        });
        this.detailDishReorderDirtySinceLastPersist = true;
        this.schedulePersistDetailDishOrderAfterDebounce();
    }

    /**
     * 加载详情：一级菜单（含菜品）+ 打印组；菜品顺序由 setting 22 驱动 `sort`（对齐 RBP2）
     */
    async loadDetailData(mainGroupId: number) {
        this.clearDetailSecondMenuReorderDebounceTimer();
        this.clearDetailDishReorderDebounceTimer();
        while (this.detailSecondMenuReorderDirtySinceLastPersist) {
            await this.persistDetailSecondMenuOrderToServer();
        }
        while (this.detailDishReorderDirtySinceLastPersist) {
            await this.persistDetailDishOrderToServer();
        }
        this.detailLoading = true;
        setLoading(true);
        try {
            const rbpId = this.resolveRbpStringIdForApi();
            /** 同阶段读取菜品排序 setting 22 与子类别排序门禁 setting 103 */
            const [setting22Res, setting103Res] = await Promise.all([
                apiManage_rbp.getSettingById(22, rbpId),
                apiManage_rbp.getSettingById(103, rbpId).catch((error: unknown) => {
                    console.error('loadDetailData getSettingById(103)', error);
                    return null;
                }),
            ]);
            /** 菜品排序值用于随后菜单详情请求的 sort 参数 */
            const dishSortValue = parseDishSortSetting22(setting22Res);
            /** setting 103 异常或错误响应均按关闭处理，避免沿用上次详情状态 */
            const menuPrioritySortEnabled = parseMenuPrioritySortSetting103(setting103Res);
            runInAction(() => {
                this.dishSortValue = dishSortValue;
                this.menuPrioritySortEnabled = menuPrioritySortEnabled;
            });
            const [menuRes, printRes, extraMenusRes, setting153Res] = await Promise.all([
                apiManage_rbp.get_menus_v3(
                    {
                        with_dish: true,
                        main_group_id: mainGroupId,
                        sort: dishSortValue,
                    },
                    rbpId,
                ),
                apiManage_rbp.getPrinterClass(rbpId),
                apiManage_rbp.get_menus_v3(
                    {
                        type: 1,
                        with_dish: true,
                    },
                    rbpId,
                ),
                apiManage_rbp.getSettingById(153, rbpId),
            ]);
            const map: Record<number, string> = { [-1]: i18n.t('print_setting_page_none0') };
            if (!_.get(printRes, 'error') && Array.isArray(printRes)) {
                (printRes as { print_class_id?: number; print_class_name?: string }[]).forEach((p) => {
                    if (p.print_class_id !== undefined && p.print_class_name) {
                        map[p.print_class_id] = p.print_class_name;
                    }
                });
            }
            const menus = (_.get(menuRes, 'error') ? [] : menuRes) as FirstMenu[];
            const first = menus[0] || null;
            const secondList = first?.menu_item_slu || [];
            const defaultSlu = secondList[0]?.dmi_slu_id ?? null;
            const prevSluId = this.selectedSecondSluId;
            const sluIds = new Set(
                secondList.map((s) => s.dmi_slu_id).filter((id): id is number => id != null && Number.isFinite(Number(id))),
            );
            const keepSelection = prevSluId != null && sluIds.has(prevSluId);
            const nextSecondSluId = keepSelection ? prevSluId : defaultSlu;
            /** 与 menu-dish-maker `getExtraMenuData` 相同：扁平化 type=1 菜单下的菜品，供绑定费用多选 */
            const extraMenuData: MenuDish[] = [];
            if (!_.get(extraMenusRes, 'error') && Array.isArray(extraMenusRes)) {
                for (const v of extraMenusRes as FirstMenu[]) {
                    for (const v1 of v.menu_item_slu || []) {
                        for (const v2 of v1.menu_item || []) {
                            extraMenuData.push(v2);
                        }
                    }
                }
            }
            const holidayOn = MenuLibraryManagementState.parseIsHolidaysSetting153(setting153Res);
            runInAction(() => {
                this.detailFirstMenu = first;
                this.printGroupMap = map;
                /** 刷新详情（如保存菜品）时保留当前选中的子类；首次进入或子类已不存在时回到第一项 */
                this.selectedSecondSluId = nextSecondSluId;
                this.selectedCloudSluId = null;
                this.extraMenuData = extraMenuData;
                this.storeHolidayMenuOpen = holidayOn;
            });
        } catch (e) {
            console.error('loadDetailData', e);
            GlobalNotification.error(i18n.t('global_text_operate_failure'));
            runInAction(() => {
                this.detailFirstMenu = null;
                this.extraMenuData = [];
                this.storeHolidayMenuOpen = false;
            });
        } finally {
            setLoading(false);
            runInAction(() => {
                this.detailLoading = false;
                this.detailFirstLoadCompleted = true;
            });
        }
    }

    /**
     * 切换二级菜单
     */
    setSelectedSecondSluId(id: number | null) {
        if (id !== this.selectedSecondSluId && this.detailDishReorderDirtySinceLastPersist) {
            this.flushPendingDetailDishReorderNow();
        }
        this.selectedSecondSluId = id;
        this.selectedCloudSluId = null;
    }

    /**
     * 云端详情：按小类 `_id` 选中左侧二级菜单。
     * @param cloudSluId 云端小类 `_id`；传 null 清空
     */
    setSelectedCloudSluId(cloudSluId: string | null) {
        const id = cloudSluId != null ? cloudSluId.trim() : '';
        const next = id || null;
        if (next !== this.selectedCloudSluId && this.detailDishReorderDirtySinceLastPersist) {
            this.flushPendingDetailDishReorderNow();
        }
        this.selectedCloudSluId = next;
        this.selectedSecondSluId = null;
    }

    /**
     * 详情左侧点击小类：云端按 `_id`，本地按 `dmi_slu_id`。
     * @param slu 被点击的小类行
     */
    setSelectedDetailSecondSlu(slu: SecondMenu) {
        if (this.isCloudMenuDetail) {
            this.setSelectedCloudSluId(getCloudSluIdFromSecondMenuEntity(slu));
            return;
        }
        this.setSelectedSecondSluId(slu.dmi_slu_id ?? null);
    }

    /**
     * 详情左侧某小类是否为当前选中项。
     * @param slu 小类行
     */
    isDetailSecondSluSelected(slu: SecondMenu): boolean {
        if (this.isCloudMenuDetail) {
            const cloudId = getCloudSluIdFromSecondMenuEntity(slu);
            return cloudId != null && this.selectedCloudSluId === cloudId;
        }
        return this.selectedSecondSluId != null && slu.dmi_slu_id === this.selectedSecondSluId;
    }

    setSelectedWeek(week: number) {
        const prevWeek = this.selectedWeek;
        if (this.isDetailHeaderEditMode && this.detailEditPriceValueDraft != null) {
            this.persistDetailEditExtrasIntoWeek(prevWeek);
        }
        this.selectedWeek = week;
        if (this.isDetailHeaderEditMode) {
            this.syncDetailHeaderPriceDraftsFromFirstMenu();
        }
    }

    setMenuDisplayLang(lang: MenuLibraryDisplayLang) {
        this.menuDisplayLang = lang;
    }

    /**
     * 新建菜单弹窗：一级菜单名称旁语言按钮（与详情 `menuDisplayLang` 独立）
     * @param lang 展示语言
     */
    setCreateDialogFirstMenuDisplayLang(lang: MenuLibraryDisplayLang) {
        this.createDialogFirstMenuDisplayLang = lang;
        this.setNewMenuName(getMainGroupNameForDisplay({
            primary: this.newMenuPrimaryName,
            i18nJson: this.createDialogMainGroupNameI18nJson,
            lang,
        }));
    }

    setDishSearchKeyword(k: string) {
        this.dishSearchKeyword = k;
    }

    closeDishSearchDialog() {
        this.isDishSearchDialogOpen = false;
    }

    toggleDishSearchDialog() {
        this.isDishSearchDialogOpen = !this.isDishSearchDialogOpen;
    }

    toggleFilterExpanded() {
        this.isFilterExpanded = !this.isFilterExpanded;
    }

    /** 打开「筛选菜品」弹窗：草稿与已应用条件同步 */
    openDishFilterDialog() {
        this.dishFilterQuickPreset = this.appliedDishFilterQuickPreset;
        this.dishFilterCategoryValue = this.appliedDishFilterCategoryValue;
        this.dishFilterImageValue = this.appliedDishFilterImageValue;
        this.dishFilterPrintValue = this.appliedDishFilterPrintValue;
        this.isDishFilterDialogOpen = true;
    }

    closeDishFilterDialog() {
        this.isDishFilterDialogOpen = false;
    }

    setDishFilterQuickPreset(v: DishFilterQuickPreset) {
        this.dishFilterQuickPreset = v;
    }

    setDishFilterCategoryValue(v: string) {
        this.dishFilterCategoryValue = v;
    }

    setDishFilterImageValue(v: string) {
        this.dishFilterImageValue = v;
    }

    setDishFilterPrintValue(v: string) {
        this.dishFilterPrintValue = v;
    }

    /** 弹窗内「重置」：仅清空草稿 */
    resetDishFilterDraft() {
        this.dishFilterQuickPreset = 'all';
        this.dishFilterCategoryValue = 'all';
        this.dishFilterImageValue = 'all';
        this.dishFilterPrintValue = 'all';
    }

    /** 离开详情或进入详情时清空草稿与已应用筛选 */
    resetDishFilterCriteria() {
        this.resetDishFilterDraft();
        this.appliedDishFilterQuickPreset = 'all';
        this.appliedDishFilterCategoryValue = 'all';
        this.appliedDishFilterImageValue = 'all';
        this.appliedDishFilterPrintValue = 'all';
    }

    /** 弹窗「应用筛选」 */
    applyDishFiltersFromDialog() {
        this.appliedDishFilterQuickPreset = this.dishFilterQuickPreset;
        this.appliedDishFilterCategoryValue = this.dishFilterCategoryValue;
        this.appliedDishFilterImageValue = this.dishFilterImageValue;
        this.appliedDishFilterPrintValue = this.dishFilterPrintValue;
        this.isDishFilterDialogOpen = false;
    }

    /** 是否存在非默认的已应用筛选（用于工具栏按钮高亮） */
    hasActiveDishFilters(): boolean {
        return (
            this.appliedDishFilterQuickPreset !== 'all' ||
            this.appliedDishFilterCategoryValue !== 'all' ||
            this.appliedDishFilterImageValue !== 'all' ||
            this.appliedDishFilterPrintValue !== 'all'
        );
    }

    /**
     * 筛选弹窗内「查询结果预览」：与列表相同关键字 + 当前草稿条件
     */
    getDishFilterPreviewDishes(): MenuDish[] {
        const sm = this.getCurrentSecondMenu();
        let items = sm?.menu_item || [];
        const kw = this.dishSearchKeyword.trim().toLowerCase();
        if (kw) {
            items = items.filter((d) => this.dishMatchesMenuLibrarySearchKeyword(d, kw));
        }
        return this.applyMenuDishFilterCriteria(items, {
            quick: this.dishFilterQuickPreset,
            category: this.dishFilterCategoryValue,
            image: this.dishFilterImageValue,
            print: this.dishFilterPrintValue,
        });
    }

    /**
     * 按筛选条件过滤菜品（快速 chip + 三个下拉）
     */
    private applyMenuDishFilterCriteria(
        items: MenuDish[],
        c: {
            quick: DishFilterQuickPreset;
            category: string;
            image: string;
            print: string;
        },
    ): MenuDish[] {
        return items.filter((d) => {
            if (c.quick !== 'all') {
                if (c.quick === 'combo' && d.item_type !== 3) {
                    return false;
                }
                if (c.quick === 'seasonal' && d.item_type !== 2) {
                    return false;
                }
                /** 称重：`item_type === 6`（`ITEM_TYPE` 称重菜品，与 RBP2 类型表一致） */
                if (c.quick === 'weight') {
                    const isWeight =
                        Number(d.item_type) === 6;
                    if (!isWeight) {
                        return false;
                    }
                }
                /** 新品推荐：`special_attributes === 2`（与新增菜品表单「新品推荐」一致） */
                if (c.quick === 'new' && Number(d.special_attributes) !== 2) {
                    return false;
                }
                /**
                 * 招牌必点：`special_attributes === 1`（与表单「厨师推荐」同一字段；文案 chip 与表单标签不同）
                 */
                if (c.quick === 'signature' && Number(d.special_attributes) !== 1) {
                    return false;
                }
                /** 禁止打折：与新增菜品表单一致 class_id 3 为不打折 */
                if (c.quick === 'no_discount' && Number(d.class_id) !== 3) {
                    return false;
                }
            }
            if (c.category !== 'all') {
                const t = Number(c.category);
                if (!Number.isFinite(t) || d.item_type !== t) {
                    return false;
                }
            }
            if (c.image === 'uploaded') {
                const hasImg = (d.dish_image && String(d.dish_image).trim()) || d.foodImage || d.bigFoodImage;
                if (!hasImg) {
                    return false;
                }
            }
            if (c.image === 'not_uploaded') {
                if ((d.dish_image && String(d.dish_image).trim()) || d.foodImage || d.bigFoodImage) {
                    return false;
                }
            }
            if (c.print !== 'all') {
                const pid = Number(c.print);
                if (!Number.isFinite(pid) || d.print_class !== pid) {
                    return false;
                }
            }
            return true;
        });
    }

    /**
     * 菜品表「查看详情」：本地/云端均打开同款可编辑弹窗；云端保存/删除时 toast 接口未就绪。
     * @param row - 当前菜品行
     */
    openDishDetailDialog(row: MenuDish) {
        if (this.isCloudMenuDetail) {
            const rawItemId = row.item_id != null ? Number(row.item_id) : null;
            /** 云端 item_id 常为 0，不当作有效查找键 */
            const itemId = rawItemId != null && rawItemId > 0 ? rawItemId : null;
            this.openMenuLibraryDetailAddDishDialogForEdit(itemId, row);
            return;
        }
        if (row.item_id != null) {
            this.openMenuLibraryDetailAddDishDialogForEdit(row.item_id);
            return;
        }
        GlobalNotification.error(i18n.t('global_text_operate_failure'));
    }

    /**
     * 一级菜单头图展示 URL（`menu_image` 为接口返回的完整链接）
     */
    getFirstMenuAvatarUrl(): string {
        return buildRestaurantStaticImageDisplayUrl(this.detailFirstMenu?.menu_image);
    }

    /**
     * 一级菜单图上传成功后并刷新详情
     */
    async patchDetailFirstMenuImage(): Promise<void> {
        if (this.isCloudMenuDetail && this.detailCloudMenuId) {
            await this.loadCloudMenuDetailData(this.detailCloudMenuId);
            return;
        }
        if (this.notifyCloudDetailApiNotReady()) {
            return;
        }
        const res = await apiManage_rbp.get_menus_v3(
            {
                with_dish: true,
                main_group_id: this.detailFirstMenu?.main_group_id,
            },
            this.resolveRbpStringIdForApi(),
        )
        if (_.get(res, 'error')) {
            if (_.get(res, 'message') !== 'canceled') {
                GlobalNotification.error(i18n.t('global_text_operate_failure'));
            }
            return;
        }
        const fm = res?.[0] as FirstMenu;
        if (!fm) {
            GlobalNotification.error(i18n.t('global_text_operate_failure'));
            return;
        }
        this.detailFirstMenu = fm;
        GlobalNotification.success(i18n.t('global_text_operate_success'));
    }

    /** 上传云端详情菜单头图，并刷新云端详情数据。 */
    async uploadCloudDetailMenuImage(file: File): Promise<void> {
        const cloudMenuId = this.detailCloudMenuId;
        if (!cloudMenuId) {
            return;
        }
        const result = await cloudDishLibraryApi.post_cloud_dish_library_menu_image(
            cloudMenuId,
            file,
            this.resolveRbpStringIdForApi(),
        );
        if (_.get(result, 'error')) {
            throw new Error(i18n.t('general_text_failed_upload_picture'));
        }
        await this.patchDetailFirstMenuImage();
    }

    /** 删除云端详情菜单头图，并刷新云端详情数据。 */
    async removeCloudDetailMenuImage(): Promise<void> {
        const cloudMenuId = this.detailCloudMenuId;
        if (!cloudMenuId) {
            return;
        }
        const result = await cloudDishLibraryApi.post_cloud_dish_library_images_delete(
            { type: 'menu', cloud_menu_id: cloudMenuId },
            this.resolveRbpStringIdForApi(),
        );
        if (_.get(result, 'error')) {
            throw new Error(i18n.t('global_text_operate_failure'));
        }
        await this.patchDetailFirstMenuImage();
    }

    /**
     * 在当前云端菜单内新增菜品，并返回后端解析出的云端菜品 id。
     * @param body 云端新增菜品请求体
     */
    async createCloudDish(body: CloudDishLibraryCreateDishBody): Promise<string | null> {
        const cloudMenuId = this.detailCloudMenuId ?? this.createDialogPersistedCloudMenuId;
        if (!cloudMenuId) {
            throw new Error('cloud menu id is required');
        }
        const result = await cloudDishLibraryApi.post_cloud_dish_library_dishes_create(
            cloudMenuId,
            body,
            this.resolveRbpStringIdForApi(),
        );
        if (_.get(result, 'error')) {
            throw new Error(i18n.t('global_text_operate_failure'));
        }
        return pickCloudDishIdFromCreateResult(result);
    }

    /**
     * 编辑已有云端菜品（`POST .../menus/dishes/update`）。
     * @param body - 含 `cloud_menu_id` + `cloud_dish_id` 的编辑体
     */
    async updateCloudDish(body: CloudDishLibraryUpdateDishBody): Promise<void> {
        const result = await cloudDishLibraryApi.post_cloud_dish_library_dishes_update(
            body,
            this.resolveRbpStringIdForApi(),
        );
        if (_.get(result, 'error')) {
            throw new Error(i18n.t('global_text_operate_failure'));
        }
    }

    /**
     * 批量设置云端菜品（`POST .../menus/dishes/batch-update`）。
     * @param body - 含 `cloud_menu_id` + `cloud_dish_ids` + 局部补丁
     */
    async batchUpdateCloudDishes(body: CloudDishLibraryBatchUpdateDishBody): Promise<void> {
        const result = await cloudDishLibraryApi.post_cloud_dish_library_dishes_batch_update(
            body,
            this.resolveRbpStringIdForApi(),
        );
        if (_.get(result, 'error')) {
            throw new Error(i18n.t('global_text_operate_failure'));
        }
    }

    /**
     * 云端详情顶栏：批量「不受轮次限制」（`POST .../menus/{cloud_menu_id}/batch-not-limit`）。
     * @param cloudMenuId - 云端菜单 id
     * @param notJoiningGlobalLimit - `0` 或 `1`
     */
    async batchNotLimitCloudDishes(cloudMenuId: string, notJoiningGlobalLimit: number): Promise<void> {
        const body = buildCloudDishLibraryBatchNotLimitBody(notJoiningGlobalLimit);
        const result = await cloudDishLibraryApi.post_cloud_dish_library_menus_batch_not_limit(
            cloudMenuId,
            body,
            this.resolveRbpStringIdForApi(),
        );
        if (_.get(result, 'error')) {
            throw new Error(i18n.t('global_text_operate_failure'));
        }
    }

    /**
     * 当前选中的二级菜单实体
     */
    getCurrentSecondMenu(): SecondMenu | null {
        const list = this.detailFirstMenu?.menu_item_slu || [];
        if (this.isCloudMenuDetail) {
            const cloudId = this.selectedCloudSluId;
            if (!cloudId) {
                return null;
            }
            return list.find((s) => getCloudSluIdFromSecondMenuEntity(s) === cloudId) || null;
        }
        const sid = this.selectedSecondSluId;
        if (sid === null) {
            return null;
        }
        return list.find((s) => s.dmi_slu_id === sid) || null;
    }

    /**
     * 详情/筛选预览搜索：名称1、名称2、菜品备注、编号(menu_item_id)、有效 item_id、origin_id、助记码(nlu)；
     * 云端另含菜品 string `id`。`item_id` 仅当 `> 0` 时参与匹配，避免云端新建（item_id=0）被「0」全中。
     * @param kwLower 已 trim 且 toLowerCase
     */
    private dishMatchesMenuLibrarySearchKeyword(dish: MenuDish, kwLower: string): boolean {
        if (!kwLower) {
            return true;
        }
        const rawItemId = dish.item_id != null ? Number(dish.item_id) : null;
        /** 仅有效本地数字 id 进入搜索；0 / 负数忽略 */
        const itemIdHay =
            rawItemId != null && Number.isFinite(rawItemId) && rawItemId > 0
                ? String(rawItemId)
                : '';
        /** 云端详情：用 string id 可检索；本地不读扩展 id */
        const cloudIdHay = this.isCloudMenuDetail
            ? getCloudDishIdFromMenuDish(dish) ?? ''
            : '';
        const hay = [
            dish.item_name1,
            dish.item_name2,
            dish.item_description,
            dish.nlu,
            itemIdHay,
            cloudIdHay,
            dish.menu_item_id != null ? String(dish.menu_item_id) : '',
            dish.origin_id != null ? String(dish.origin_id) : '',
        ].map((s) => String(s ?? '').toLowerCase());
        return hay.some((h) => h.includes(kwLower));
    }

    /**
     * 当前二级菜单下菜品（经搜索过滤）
     */
    getFilteredDishes(): MenuDish[] {
        const sm = this.getCurrentSecondMenu();
        let items = sm?.menu_item || [];
        const kw = this.dishSearchKeyword.trim().toLowerCase();
        if (kw) {
            items = items.filter((d) => this.dishMatchesMenuLibrarySearchKeyword(d, kw));
        }
        return this.applyMenuDishFilterCriteria(items, {
            quick: this.appliedDishFilterQuickPreset,
            category: this.appliedDishFilterCategoryValue,
            image: this.appliedDishFilterImageValue,
            print: this.appliedDishFilterPrintValue,
        });
    }

    /**
     * 展示用菜品名称
     */
    getDishDisplayName(dish: MenuDish): string {
        if (this.menuDisplayLang === 'en') {
            return dish.item_name2 || dish.item_name1 || '';
        }
        // unspecified / zh / it / sp：主名称（name1）优先
        return dish.item_name1 || dish.item_name2 || '';
    }

    /**
     * 绑定费用展示：成人 / 儿童 / 其他（随星期切换）
     */
    getPriceLineForWeek(): { adult: string; child: string; other: string } {
        const fm = this.detailFirstMenu;
        const w = this.selectedWeek;
        if (!fm) {
            return { adult: '—', child: '—', other: '—' };
        }
        if (fm.set_item_main === '1' && fm.value?.length) {
            const row = fm.value.find((v) => v.week === w);
            return {
                adult: row?.normal_price != null ? String(row.normal_price) : '—',
                child: row?.child_price != null ? String(row.child_price) : '—',
                other: row?.other_price != null ? String(row.other_price) : '—',
            };
        }
        return {
            adult: fm.normal_price != null ? String(fm.normal_price) : '—',
            child: fm.child_price != null ? String(fm.child_price) : '—',
            other: fm.other_price != null ? String(fm.other_price) : '—',
        };
    }

    /**
     * 批量「不受轮次限制」是否全开。
     * 云端：优先读菜单级 `not_joining_global_limit`（与 create / GET 详情契约一致）；
     * 本地：仍按一级菜单下全部菜品聚合。
     */
    getBatchRoundAllUnrestricted(): boolean {
        const fm = this.detailFirstMenu;
        if (!fm) {
            return false;
        }
        // 云端详情：顶栏开关读菜单级字段（有菜/无菜均同）
        if (this.isCloudMenuDetail) {
            const menuLevel = (fm as { not_joining_global_limit?: number }).not_joining_global_limit;
            return Number(menuLevel) === 1;
        }
        if (!fm.menu_item_slu?.length) {
            return false;
        }
        const dishes: MenuDish[] = [];
        fm.menu_item_slu.forEach((slu) => {
            (slu.menu_item || []).forEach((d) => dishes.push(d));
        });
        if (!dishes.length) {
            return false;
        }
        return dishes.every((d) => Number(d.not_joining_global_limit) === 1);
    }

    /**
     * 切换批量轮次限制（与 FirstMenuDetail.onChangeSwitch 一致）。
     * 本地：`patch_dishes(..., true)`；云端：`menus/{cloud_menu_id}/batch-not-limit`。
     * 作用范围均为当前一级菜单下全部菜品。
     */
    async toggleBatchRoundLimit() {
        const fm = this.detailFirstMenu;
        if (!fm?.menu_item_slu?.length) {
            return;
        }
        const nextOn = !this.getBatchRoundAllUnrestricted();
        const value = nextOn ? 1 : 0;

        // 云端：专用 batch-not-limit（后端按菜单改全部菜品，无需传 dish ids）
        if (this.isCloudMenuDetail) {
            const cloudMenuId = this.detailCloudMenuId;
            if (!cloudMenuId) {
                return;
            }
            runInAction(() => {
                this.isBatchRoundToggleLoading = true;
            });
            setLoading(true);
            try {
                await this.batchNotLimitCloudDishes(cloudMenuId, value);
                GlobalNotification.success(i18n.t('global_text_operate_success'));
                await this.loadCloudMenuDetailData(cloudMenuId);
            } catch (e) {
                console.error(e);
                GlobalNotification.error(i18n.t('global_text_operate_failure'));
            } finally {
                setLoading(false);
                runInAction(() => {
                    this.isBatchRoundToggleLoading = false;
                });
            }
            return;
        }

        // 本地：patch_dishes 写一级菜单下全部菜品
        if (!fm.main_group_id) {
            return;
        }
        const patchRows: { item_id: number; not_joining_global_limit: number }[] = [];
        fm.menu_item_slu.forEach((slu) => {
            (slu.menu_item || []).forEach((d) => {
                if (d.item_id) {
                    patchRows.push({ item_id: d.item_id, not_joining_global_limit: value });
                }
            });
        });
        if (!patchRows.length) {
            return;
        }
        runInAction(() => {
            this.isBatchRoundToggleLoading = true;
        });
        setLoading(true);
        try {
            const payload = patchRows;
            const res = await apiManage_rbp.patch_dishes(payload, true, this.resolveRbpStringIdForApi());
            if (!_.get(res, 'error')) {
                GlobalNotification.success(i18n.t('global_text_operate_success'));
                await this.loadDetailData(fm.main_group_id as number);
            } else {
                GlobalNotification.error(i18n.t('global_text_operate_failure'));
            }
        } catch (e) {
            console.error(e);
            GlobalNotification.error(i18n.t('global_text_operate_failure'));
        } finally {
            setLoading(false);
            runInAction(() => {
                this.isBatchRoundToggleLoading = false;
            });
        }
    }

    setEditMenuNameDraft(v: string) {
        const next = applyMainGroupNameInput({
            primary: this.detailHeaderMenuNamePrimary,
            i18nJson: this.detailHeaderMenuNameI18nJson,
            lang: this.detailHeaderMenuNameDisplayLang,
            value: v,
        });
        this.detailHeaderMenuNamePrimary = next.primary;
        this.detailHeaderMenuNameI18nJson = next.i18nJson;
    }

    /**
     * 详情顶栏编辑：一级菜单名称旁语言（与菜品列表 `menuDisplayLang` 独立）
     * @param lang 展示语言
     */
    setDetailHeaderMenuNameDisplayLang(lang: MenuLibraryDisplayLang) {
        this.detailHeaderMenuNameDisplayLang = lang;
    }

    /**
     * 额外费用多选下拉选项（与 `first-menu-detail` 中 `extraMenuDataOptions` 一致）
     */
    getExtraMenuDataOptions(): { value: string; label: string }[] {
        return this.extraMenuData.map((item) => ({
            label: `${item.item_id}. ${item.item_name1}: ${this.getModuleCurrencyName()} ${item.price_1}`,
            value: `${item.item_id}`,
        }));
    }

    /**
     * 当前档位已选 extra item_id（供药丸下拉展示选中态）
     * @param field `extra_normal` / `extra_child` / `extra_other`
     */
    getHeaderExtraIdsForField(field: 'extra_normal' | 'extra_child' | 'extra_other'): string[] {
        if (field === 'extra_normal') {
            return [...this.editExtraNormalIds];
        }
        if (field === 'extra_child') {
            return [...this.editExtraChildIds];
        }
        return [...this.editExtraOtherIds];
    }

    /**
     * 顶栏编辑药丸文案：汇总价或「—」（与只读药丸货币 + 数值格式一致）
     * @param field `extra_normal` / `extra_child` / `extra_other`
     */
    getHeaderEditPillSumDisplay(field: 'extra_normal' | 'extra_child' | 'extra_other'): string {
        const ids =
            field === 'extra_normal'
                ? this.editExtraNormalIds
                : field === 'extra_child'
                    ? this.editExtraChildIds
                    : this.editExtraOtherIds;
        if (!ids.length) {
            return '—';
        }
        return String(Number(this.sumExtraPricesForIds(ids).toFixed(2)));
    }

    /**
     * 药丸下拉：切换某一额外费用项是否选中（多选，与制作器 extra 逗号串一致）
     * @param field extra 字段
     * @param itemId 额外费用菜品 `item_id`
     */
    toggleHeaderExtraIdInMenu(field: 'extra_normal' | 'extra_child' | 'extra_other', itemId: string) {
        runInAction(() => {
            const pick = (arr: string[]) => {
                const next = [...arr];
                const i = next.indexOf(itemId);
                if (i >= 0) {
                    next.splice(i, 1);
                } else {
                    next.push(itemId);
                }
                return next;
            };
            if (field === 'extra_normal') {
                this.editExtraNormalIds = pick(this.editExtraNormalIds);
            } else if (field === 'extra_child') {
                this.editExtraChildIds = pick(this.editExtraChildIds);
            } else {
                this.editExtraOtherIds = pick(this.editExtraOtherIds);
            }
        });
    }

    /**
     * 药丸下拉：清空当前档位已选额外费用
     * @param field extra 字段
     */
    clearHeaderExtraField(field: 'extra_normal' | 'extra_child' | 'extra_other') {
        runInAction(() => {
            if (field === 'extra_normal') {
                this.editExtraNormalIds = [];
            } else if (field === 'extra_child') {
                this.editExtraChildIds = [];
            } else {
                this.editExtraOtherIds = [];
            }
        });
    }

    /**
     * 新建弹窗：当前档位已选 extra item_id（与详情 getHeaderExtraIdsForField 语义一致）
     * @param field `extra_normal` / `extra_child` / `extra_other`
     */
    getCreateExtraIdsForField(field: 'extra_normal' | 'extra_child' | 'extra_other'): string[] {
        if (field === 'extra_normal') {
            return [...this.createExtraNormalIds];
        }
        if (field === 'extra_child') {
            return [...this.createExtraChildIds];
        }
        return [...this.createExtraOtherIds];
    }

    /**
     * 新建弹窗：顶栏药丸汇总价展示（与详情 getHeaderEditPillSumDisplay 一致）
     * @param field `extra_normal` / `extra_child` / `extra_other`
     */
    getCreateEditPillSumDisplay(field: 'extra_normal' | 'extra_child' | 'extra_other'): string {
        const ids =
            field === 'extra_normal'
                ? this.createExtraNormalIds
                : field === 'extra_child'
                    ? this.createExtraChildIds
                    : this.createExtraOtherIds;
        if (!ids.length) {
            return '—';
        }
        return String(Number(this.sumExtraPricesForIds(ids).toFixed(2)));
    }

    /**
     * 新建弹窗：药丸下拉多选切换（与 toggleHeaderExtraIdInMenu 一致）
     * @param field extra 字段
     * @param itemId 额外费用菜品 item_id
     */
    toggleCreateExtraIdInMenu(field: 'extra_normal' | 'extra_child' | 'extra_other', itemId: string) {
        runInAction(() => {
            const pick = (arr: string[]) => {
                const next = [...arr];
                const i = next.indexOf(itemId);
                if (i >= 0) {
                    next.splice(i, 1);
                } else {
                    next.push(itemId);
                }
                return next;
            };
            if (field === 'extra_normal') {
                this.createExtraNormalIds = pick(this.createExtraNormalIds);
            } else if (field === 'extra_child') {
                this.createExtraChildIds = pick(this.createExtraChildIds);
            } else {
                this.createExtraOtherIds = pick(this.createExtraOtherIds);
            }
            this.persistCreateExtrasIntoWeekKey(this.createMenuSelectedWeekKey);
        });
    }

    /**
     * 新建弹窗：清空当前档位已选额外费用
     * @param field extra 字段
     */
    clearCreateExtraField(field: 'extra_normal' | 'extra_child' | 'extra_other') {
        runInAction(() => {
            if (field === 'extra_normal') {
                this.createExtraNormalIds = [];
            } else if (field === 'extra_child') {
                this.createExtraChildIds = [];
            } else {
                this.createExtraOtherIds = [];
            }
            this.persistCreateExtrasIntoWeekKey(this.createMenuSelectedWeekKey);
        });
    }

    /**
     * 将逗号分隔的 extra id 解析为数组（与制作器 split 行为一致）
     * @param raw 接口返回的 extra_* 字符串
     */
    private parseExtraCsvIds(raw?: string | null): string[] {
        if (raw === undefined || raw === null) {
            return [];
        }
        const s = String(raw).trim();
        if (!s) {
            return [];
        }
        return s
            .split(',')
            .map((x) => x.trim())
            .filter(Boolean);
    }

    /**
     * 按所选 item_id 汇总 `price_1`（与 `formChange` / `formChangeForNewPrice` 从 label 解析等价）
     * @param ids 选中的额外费用菜品 id
     */
    private sumExtraPricesForIds(ids: string[]): number {
        if (!ids.length) {
            return 0;
        }
        return ids.reduce((acc, id) => {
            const d = this.extraMenuData.find((x) => String(x.item_id) === String(id));
            return acc + Number(d?.price_1 ?? 0);
        }, 0);
    }

    /**
     * 进入顶区页内编辑并同步草稿
     */
    startDetailHeaderEdit() {
        const fm = this.detailFirstMenu;
        this.detailHeaderMenuNamePrimary = fm?.main_group_name || '';
        this.detailHeaderMenuNameI18nJson = fm?.main_group_name_i18n ?? '';
        this.detailHeaderMenuNameDisplayLang = 'unspecified';
        this.detailHeaderMaxLimitDraft =
            fm?.max_limit != null && Number.isFinite(Number(fm.max_limit)) ? String(fm.max_limit) : '';
        this.detailHeaderHolidayMenuEnabled =
            fm?.set_item_main === '1' &&
            !!(fm.value?.some((v) => v.week === MENU_LIBRARY_WEEK_HOLIDAY_NUM));
        if (fm?.set_item_main === '1') {
            this.detailEditPriceValueDraft = this.buildDetailEditPriceValueDraftFromFirstMenu(fm);
        } else {
            this.detailEditPriceValueDraft = null;
        }
        this.syncDetailHeaderPriceDraftsFromFirstMenu();
        this.isDetailHeaderEditMode = true;
    }

    /**
     * 取消顶区页内编辑
     */
    cancelDetailHeaderEdit() {
        this.isDetailHeaderEditMode = false;
        this.detailHeaderMenuNameDisplayLang = 'unspecified';
        this.detailHeaderMenuNamePrimary = '';
        this.detailHeaderMenuNameI18nJson = '';
        this.detailHeaderMaxLimitDraft = '';
        this.detailHeaderHolidayMenuEnabled = false;
        this.detailEditPriceValueDraft = null;
        this.editExtraNormalIds = [];
        this.editExtraChildIds = [];
        this.editExtraOtherIds = [];
        this.flushPendingDetailSecondMenuReorderNow();
        this.flushPendingDetailDishReorderNow();
    }

    setDetailHeaderMaxLimitDraft(v: string) {
        this.detailHeaderMaxLimitDraft = v;
    }

    setDetailHeaderHolidayMenuEnabled(enabled: boolean) {
        this.detailHeaderHolidayMenuEnabled = enabled;
        if (!enabled && this.selectedWeek === MENU_LIBRARY_WEEK_HOLIDAY_NUM) {
            this.selectedWeek = 1;
        }
    }

    /**
     * 按当前模块所选门店拉取 setting 153，刷新列表顶栏「节假日菜单」开关；不抛错以免拖垮 `init`。
     * @param initEpoch 与本次 `init()` 递增一致时才写入，避免快速切换门店时旧请求覆盖新门店
     */
    private async refreshStoreHolidayMenuToggleFromServer(initEpoch: number): Promise<void> {
        runInAction(() => {
            this.storeHolidayMenuSettingFetchLoading = true;
        });
        try {
            const setting153Res = await apiManage_rbp.getSettingById(153, this.resolveRbpStringIdForApi());
            if (initEpoch !== this.menuLibraryInitEpoch) {
                return;
            }
            if (_.get(setting153Res, 'error')) {
                runInAction(() => {
                    this.storeHolidayMenuOpen = false;
                });
                return;
            }
            const holidayOn = MenuLibraryManagementState.parseIsHolidaysSetting153(setting153Res);
            runInAction(() => {
                this.storeHolidayMenuOpen = holidayOn;
            });
        } catch (e) {
            console.error('refreshStoreHolidayMenuToggleFromServer', e);
            if (initEpoch === this.menuLibraryInitEpoch) {
                runInAction(() => {
                    this.storeHolidayMenuOpen = false;
                });
            }
        } finally {
            if (initEpoch === this.menuLibraryInitEpoch) {
                runInAction(() => {
                    this.storeHolidayMenuSettingFetchLoading = false;
                });
            }
        }
    }

    /** 与 RBP2 `_.get(setting153, '[0].value')` 一致解析 `is_holidays` */
    private static parseIsHolidaysSetting153(res: unknown): boolean {
        const v: unknown = _.get(res as object, '[0].value', false);
        if (typeof v === 'boolean') {
            return v;
        }
        if (typeof v === 'number') {
            return v !== 0;
        }
        if (typeof v === 'string') {
            const s = v.trim().toLowerCase();
            return s === '1' || s === 'true';
        }
        return Boolean(v);
    }

    /** 与 `getSettingById(78)` 返回体 `_[0].value` 一致，映射为 `set_item_main` */
    private static parseSetItemMainFromSetting78(res: unknown): '0' | '1' {
        const v: unknown = _.get(res as object, '[0].value');
        if (v === 0 || v === '0' || v === false) {
            return '0';
        }
        if (v === 1 || v === '1' || v === true) {
            return '1';
        }
        if (typeof v === 'string') {
            const s = v.trim().toLowerCase();
            if (s === '0' || s === 'false') {
                return '0';
            }
            if (s === '1' || s === 'true') {
                return '1';
            }
        }
        return '1';
    }

    /**
     * 切换门店「节假日菜单」设置（setting 153），与制作器 `onChangeSwitchHoliday` 相同
     */
    async toggleStoreHolidayMenu() {
        if (this.storeHolidayMenuToggleLoading || this.storeHolidayMenuSettingFetchLoading) {
            return;
        }
        const next = !this.storeHolidayMenuOpen;
        runInAction(() => {
            this.storeHolidayMenuToggleLoading = true;
            this.storeHolidayMenuOpen = next;
        });
        try {
            const res = await apiManage_rbp.patchSettingById(153, { is_holidays: Number(next) }, this.resolveRbpStringIdForApi());
            if (_.get(res, 'error')) {
                GlobalNotification.error(i18n.t('global_text_operate_failure'));
                runInAction(() => {
                    this.storeHolidayMenuOpen = !next;
                });
                return;
            }
            if (next) {
                GlobalNotification.warning(i18n.t('manger_text_holiday_menu_activated'));
            } else {
                GlobalNotification.warning(i18n.t('manger_text_holiday_menu_closed_today'));
            }
        } catch (err) {
            console.error('toggleStoreHolidayMenu', err);
            runInAction(() => {
                this.storeHolidayMenuOpen = !next;
            });
            GlobalNotification.error(i18n.t('global_text_operate_failure'));
        } finally {
            runInAction(() => {
                this.storeHolidayMenuToggleLoading = false;
            });
        }
    }

    /** 与制作器 `max_limit` 校验一致：正整数，最小 1 */
    private parseMaxLimitDraftString(draft: string): number {
        const n = Number(String(draft).trim());
        if (!Number.isFinite(n) || n < 1) {
            return 1;
        }
        return Math.floor(n);
    }

    /** 与制作器 `max_limit` 校验一致：正整数，最小 1 */
    private parseDetailHeaderMaxLimit(): number {
        return this.parseMaxLimitDraftString(this.detailHeaderMaxLimitDraft);
    }

    /**
     * 新建弹窗：未填写大类限点则为 `undefined`（请求体不带 `max_limit`）；已填写则为 ≥1 的整数。
     */
    private parseCreateDialogMaxLimitOptional(): number | undefined {
        const raw = String(this.createDialogMaxLimitDraft).trim();
        if (raw === '') {
            return undefined;
        }
        const n = Number(raw);
        if (!Number.isFinite(n) || n < 1) {
            return undefined;
        }
        return Math.floor(n);
    }

    /**
     * 从一级菜单 `value` 克隆详情顶栏按周草稿，补齐周一至周日行；保留已有节假日行
     * @param fm 当前详情一级菜单
     */
    private buildDetailEditPriceValueDraftFromFirstMenu(fm: FirstMenu): NewMenuPrice[] {
        const draft =
            fm.value && fm.value.length > 0
                ? (fm.value.map((row) => ({ ...row })) as NewMenuPrice[])
                : setEmptyNewPriceValue();
        const standardWeeks = [1, 2, 3, 4, 5, 6, 0] as const;
        const next = [...draft];
        for (const week of standardWeeks) {
            if (!next.some((row) => row.week === week)) {
                next.push({
                    week,
                    extra_child: '',
                    extra_normal: '',
                    extra_other: '',
                    normal_price: null,
                    child_price: null,
                    other_price: null,
                });
            }
        }
        return next;
    }

    /**
     * 将当前三档绑定费用写回详情顶栏按周草稿的指定星期行
     * @param week `NewMenuPrice.week` 数值
     */
    private persistDetailEditExtrasIntoWeek(week: number) {
        if (this.detailEditPriceValueDraft == null) {
            return;
        }
        const extraNormalStr = this.editExtraNormalIds.join(',');
        const extraChildStr = this.editExtraChildIds.join(',');
        const extraOtherStr = this.editExtraOtherIds.join(',');
        const np = Number(this.sumExtraPricesForIds(this.editExtraNormalIds).toFixed(2));
        const cp = Number(this.sumExtraPricesForIds(this.editExtraChildIds).toFixed(2));
        const op = Number(this.sumExtraPricesForIds(this.editExtraOtherIds).toFixed(2));
        const idx = this.detailEditPriceValueDraft.findIndex((row) => row.week === week);
        if (idx < 0) {
            this.detailEditPriceValueDraft = [
                ...this.detailEditPriceValueDraft,
                {
                    week,
                    extra_normal: extraNormalStr,
                    extra_child: extraChildStr,
                    extra_other: extraOtherStr,
                    normal_price: np,
                    child_price: cp,
                    other_price: op,
                },
            ];
            return;
        }
        const next = [...this.detailEditPriceValueDraft];
        next[idx] = {
            ...next[idx],
            extra_normal: extraNormalStr,
            extra_child: extraChildStr,
            extra_other: extraOtherStr,
            normal_price: np,
            child_price: cp,
            other_price: op,
        };
        this.detailEditPriceValueDraft = next;
    }

    /**
     * 从详情顶栏按周草稿加载指定星期行到三档绑定费用多选
     * @param week `NewMenuPrice.week` 数值
     */
    private loadDetailEditExtrasFromWeek(week: number) {
        const row = this.detailEditPriceValueDraft?.find((item) => item.week === week);
        if (!row) {
            this.editExtraNormalIds = [];
            this.editExtraChildIds = [];
            this.editExtraOtherIds = [];
            return;
        }
        this.editExtraNormalIds = this.parseExtraCsvIds(row.extra_normal);
        this.editExtraChildIds = this.parseExtraCsvIds(row.extra_child);
        this.editExtraOtherIds = this.parseExtraCsvIds(row.extra_other);
    }

    /**
     * 从当前一级菜单与选中星期刷新 extra 多选草稿（编辑态优先读按周草稿 / 否则读接口数据）
     */
    private syncDetailHeaderPriceDraftsFromFirstMenu() {
        if (this.detailEditPriceValueDraft != null) {
            this.loadDetailEditExtrasFromWeek(this.selectedWeek);
            return;
        }
        const fm = this.detailFirstMenu;
        if (!fm) {
            this.editExtraNormalIds = [];
            this.editExtraChildIds = [];
            this.editExtraOtherIds = [];
            return;
        }
        if (fm.set_item_main === '1' && fm.value?.length) {
            const row = fm.value.find((v) => v.week === this.selectedWeek);
            if (!row) {
                this.editExtraNormalIds = [];
                this.editExtraChildIds = [];
                this.editExtraOtherIds = [];
                return;
            }
            this.editExtraNormalIds = this.parseExtraCsvIds(row.extra_normal);
            this.editExtraChildIds = this.parseExtraCsvIds(row.extra_child);
            this.editExtraOtherIds = this.parseExtraCsvIds(row.extra_other);
        } else {
            this.editExtraNormalIds = this.parseExtraCsvIds(fm.extra_normal);
            this.editExtraChildIds = this.parseExtraCsvIds(fm.extra_child);
            this.editExtraOtherIds = this.parseExtraCsvIds(fm.extra_other);
        }
    }

    /**
     * 保存顶区：菜单名称 + 绑定费用（新方案：当前星期 `value` 行写入 extra_* 与汇总价；旧方案：根级 extra_* 与汇总价；与 `first-menu-detail` 一致）
     */
    async saveDetailHeaderEdit() {
        const fm = this.detailFirstMenu;
        const name = this.detailHeaderMenuNamePrimary.trim();
        const i18nNameJson = normalizeMainGroupNameI18nJson(this.detailHeaderMenuNameI18nJson);
        if (!this.isCloudMenuDetail && !fm?.main_group_id) {
            return;
        }
        if (!name) {
            GlobalNotification.error(i18n.t('menu_library_detail_menu_name_required'));
            return;
        }
        const rbp = this.resolveRbpStringIdForApi();
        const gid = fm?.main_group_id as number;
        const extraNormalStr = this.editExtraNormalIds.join(',');
        const extraChildStr = this.editExtraChildIds.join(',');
        const extraOtherStr = this.editExtraOtherIds.join(',');
        const np = Number(this.sumExtraPricesForIds(this.editExtraNormalIds).toFixed(2));
        const cp = Number(this.sumExtraPricesForIds(this.editExtraChildIds).toFixed(2));
        const op = Number(this.sumExtraPricesForIds(this.editExtraOtherIds).toFixed(2));
        const maxLimitNum = this.parseDetailHeaderMaxLimit();

        runInAction(() => {
            this.isDetailHeaderSaveLoading = true;
        });
        setLoading(true);
        try {
            if (this.isCloudMenuDetail && this.detailCloudMenuId) {
                const result = await cloudDishLibraryApi.post_cloud_dish_library_menu_update(
                    this.detailCloudMenuId,
                    {
                        main_group_name: name,
                        main_group_name_i18n: i18nNameJson,
                        max_limit: maxLimitNum,
                    },
                    rbp,
                );
                if (_.get(result, 'error')) {
                    GlobalNotification.error(i18n.t('global_text_operate_failure'));
                    return;
                }
                GlobalNotification.success(i18n.t('global_text_operate_success'));
                runInAction(() => {
                    this.isDetailHeaderEditMode = false;
                    this.detailHeaderMenuNameDisplayLang = 'unspecified';
                    this.detailHeaderMenuNamePrimary = '';
                    this.detailHeaderMenuNameI18nJson = '';
                    this.detailHeaderMaxLimitDraft = '';
                });
                await this.loadCloudMenuDetailData(this.detailCloudMenuId);
                await this.refreshCloudMenusAfterMutation();
                return;
            }
            if (!fm) {
                return;
            }
            if (fm.set_item_main === '1') {
                if (this.detailEditPriceValueDraft != null) {
                    this.persistDetailEditExtrasIntoWeek(this.selectedWeek);
                }
                let value = _.cloneDeep(
                    this.detailEditPriceValueDraft ??
                        (fm.value && fm.value.length > 0 ? fm.value : setEmptyNewPriceValue()),
                ) as NewMenuPrice[];
                if (this.detailHeaderHolidayMenuEnabled) {
                    if (!value.some((v) => v.week === MENU_LIBRARY_WEEK_HOLIDAY_NUM)) {
                        value.push({
                            week: MENU_LIBRARY_WEEK_HOLIDAY_NUM,
                            extra_normal: '',
                            extra_child: '',
                            extra_other: '',
                            normal_price: null,
                            child_price: null,
                            other_price: null,
                        });
                    }
                } else {
                    value = value.filter((v) => v.week !== MENU_LIBRARY_WEEK_HOLIDAY_NUM);
                }
                if (this.detailEditPriceValueDraft == null) {
                    let idx = value.findIndex((v) => v.week === this.selectedWeek);
                    if (idx < 0) {
                        value.push({
                            week: this.selectedWeek,
                            extra_normal: '',
                            extra_child: '',
                            extra_other: '',
                            normal_price: null,
                            child_price: null,
                            other_price: null,
                        });
                        idx = value.length - 1;
                    }
                    value[idx] = {
                        ...value[idx],
                        extra_normal: extraNormalStr,
                        extra_child: extraChildStr,
                        extra_other: extraOtherStr,
                        normal_price: np,
                        child_price: cp,
                        other_price: op,
                    };
                }
                const resName = await apiManage_rbp.patch_first_menu(
                    {
                        main_group_id: gid,
                        main_group_name: name,
                        main_group_name_i18n: i18nNameJson,
                        max_limit: maxLimitNum,
                        extra_child: fm.extra_child,
                        extra_normal: fm.extra_normal,
                        extra_other: fm.extra_other,
                    } as FirstMenu,
                    rbp,
                );
                if (_.get(resName, 'error')) {
                    GlobalNotification.error(i18n.t('global_text_operate_failure'));
                    return;
                }
                const resPrice = await apiManage_rbp.patch_first_menu_day_price(
                    {
                        ...fm,
                        main_group_id: gid,
                        main_group_name: name,
                        main_group_name_i18n: i18nNameJson,
                        value,
                    } as FirstMenu,
                    rbp,
                );
                if (_.get(resPrice, 'error')) {
                    GlobalNotification.error(i18n.t('global_text_operate_failure'));
                    return;
                }
            } else {
                const res = await apiManage_rbp.patch_first_menu(
                    {
                        main_group_id: gid,
                        main_group_name: name,
                        main_group_name_i18n: i18nNameJson,
                        max_limit: maxLimitNum,
                        extra_normal: extraNormalStr,
                        extra_child: extraChildStr,
                        extra_other: extraOtherStr,
                    } as FirstMenu,
                    rbp,
                );
                if (_.get(res, 'error')) {
                    GlobalNotification.error(i18n.t('global_text_operate_failure'));
                    return;
                }
            }
            GlobalNotification.success(i18n.t('global_text_operate_success'));
            runInAction(() => {
                this.isDetailHeaderEditMode = false;
                this.detailHeaderMenuNameDisplayLang = 'unspecified';
                this.detailHeaderMenuNamePrimary = '';
                this.detailHeaderMenuNameI18nJson = '';
                this.detailHeaderMaxLimitDraft = '';
                this.detailHeaderHolidayMenuEnabled = false;
                this.detailEditPriceValueDraft = null;
                this.editExtraNormalIds = [];
                this.editExtraChildIds = [];
                this.editExtraOtherIds = [];
            });
            await this.loadDetailData(gid);
            await this.refreshLocalMenusAfterMutation();
        } catch (e) {
            console.error(e);
            GlobalNotification.error(i18n.t('global_text_operate_failure'));
        } finally {
            setLoading(false);
            runInAction(() => {
                this.isDetailHeaderSaveLoading = false;
            });
        }
    }

    /** 打开分享菜单弹窗 */
    openShareMenuDialog() {
        this.isShareMenuDialogOpen = true;
    }

    /** 关闭分享菜单弹窗（请求进行中不可关，避免状态与接口错位） */
    closeShareMenuDialog() {
        if (
            this.shareMenuDialogMenuStoreOpeningLoading ||
            this.shareMenuDialogSelectedStoresSubmitting ||
            this.shareStoreTargetSubmitting
        ) {
            return;
        }
        this.closeShareStoreTargetDialog();
        this.isShareMenuDialogOpen = false;
    }

    /**
     * 当前详情一级菜单下是否存在至少一条菜品（汇总所有二级菜单下的 `menu_item`）。
     * @returns 有菜品为 `true`；无二级、无菜品或 `detailFirstMenu` 为空为 `false`
     */
    hasDetailMenuDishes(): boolean {
        const fm = this.detailFirstMenu;
        const slus = fm?.menu_item_slu ?? [];
        return slus.some((slu) => (slu.menu_item?.length ?? 0) > 0);
    }

    /**
     * 顶栏「分享菜单」：无菜品时提示并中止；否则打开弹窗。
     * 云端详情走独立分享 API（`import-to-local` / `from-cloud-library`），不再用写接口未就绪门禁拦截。
     */
    handleShareMenu() {
        if (!this.hasDetailMenuDishes()) {
            GlobalNotification.error(i18n.t('menu_library_share_no_dishes'));
            return;
        }
        this.openShareMenuDialog();
    }

    /**
     * 分享弹窗 / 成功弹窗子标题：当前一级菜单展示名 + `last_updated_time`（`GET v3/menus?with_dish&main_group_id`）格式化为 `DD/MM/YYYY`，与列表卡片 `MenuLibraryItem.subtitle` 一致；无更新时间则仅展示名称。
     * @returns 展示文案；无详情数据时为空字符串
     */
    getShareMenuDialogSubtitle(): string {
        const fm = this.detailFirstMenu;
        if (!fm) {
            return '';
        }
        const displayName =
            getMainGroupNameForDisplay({
                primary: fm.main_group_name ?? '',
                i18nJson: fm.main_group_name_i18n ?? '',
                lang: this.menuDisplayLang,
            }).trim() ||
            fm.main_group_name?.trim() ||
            '';
        const base = displayName || '—';
        const raw = fm.last_updated_time?.trim();
        if (!raw) {
            return base;
        }
        const formatted = moment(raw).format('DD/MM/YYYY');
        return displayName ? `${displayName} - ${formatted}` : base;
    }

    /**
     * 当前登录用户 id（与菜单商店 `created_by` 比较，口径同 `menu-store-state`）
     */
    private getShareCurrentAccountId(): string | undefined {
        accountState.getFromLocalStore();
        const id = accountState.userInfo?._id;
        return id != null ? String(id) : undefined;
    }

    /**
     * 分享至商店弹窗：可归为「我上传」的候选列表（`shareStoreTargetMyMenusRaw` 已为 **uploaded** 侧全量合并）。
     */
    getShareStoreUploadedMenuCandidates(): MenuStoreMenuListItem[] {
        const list = this.shareStoreTargetMyMenusRaw;
        const uid = this.getShareCurrentAccountId();
        return list.filter((item) => {
            if (item.status === 'delisted' || item.status === 'rejected') {
                return false;
            }
            if (!uid || !item.created_by) {
                return false;
            }
            return String(item.created_by) === uid;
        });
    }

    /**
     * 分享弹窗虚拟列表用：在**仍可分页拉取**时裁掉尾部，使「首格新增 + 已上传」总格数为 4 的倍数，避免末行缺角；
     * 已全部拉取完时不再裁切（允许最后一行不满）。
     * @returns 与 {@link getShareStoreUploadedMenuCandidates} 同序、可能更短的列表
     */
    getShareStoreUploadedMenuCandidatesForGrid(): MenuStoreMenuListItem[] {
        const full = this.getShareStoreUploadedMenuCandidates();
        return this.sliceShareStoreUploadedCandidatesForFourColumnGrid(full);
    }

    /**
     * 按 4 列栅格裁切已上传候选：总单元数 `1 + L`（含「新增菜单」）在仍有下一页数据可拉时为 4 的倍数。
     * @param full - 已过滤的「我上传」候选列表
     * @returns 裁切后的浅拷贝切片（不修改 `full`）
     */
    private sliceShareStoreUploadedCandidatesForFourColumnGrid(full: MenuStoreMenuListItem[]): MenuStoreMenuListItem[] {
        const L = full.length;
        if (L === 0) {
            return full;
        }
        /** 已合并条数达到服务端 `total` 时表示没有更多页，此时展示全部（含末行不满） */
        const noMoreFromServer =
            this.shareStoreTargetMyMenusTotal > 0 && this.shareStoreTargetMyMenusRaw.length >= this.shareStoreTargetMyMenusTotal;
        if (noMoreFromServer) {
            return full;
        }
        /** `(1 + 展示条数) % 4 === 0` 时末行满格；否则去掉末尾 `tail` 条暂不展示，等下一页拼满 */
        const tail = (1 + L) % 4;
        if (tail === 0) {
            return full;
        }
        /** `L < tail` 时（如仅 1～2 条）先不展示已上传格，仅保留「新增」直至后续页凑满一行 */
        const visibleLen = Math.max(0, L - tail);
        return visibleLen === 0 ? [] : full.slice(0, visibleLen);
    }

    /**
     * 请求分享弹窗用 uploaded 列表单页（不含 loading 位；失败返回 `null`）。
     * @param page - 页码，从 1 起
     */
    private async requestShareStoreUploadedMenusPage(page: number): Promise<MenuStoreListResponse | null> {
        const res = await apiManage.getMenuStoreMyMenusUploaded({
            pagination_page: page,
            pagination_per_page: this.shareStoreTargetMyMenusPageSize,
        });
        if (_.get(res, 'error')) {
            return null;
        }
        return parseMenuStoreListResponse(res);
    }

    /**
     * 分享弹窗：拉取 uploaded 第 1 页并替换本地列表。
     * @returns 接口成功且解析成功时为 `true`（含列表为空）；失败为 `false`
     */
    async loadShareStoreTargetUploadedFirstPage(): Promise<boolean> {
        this.shareStoreTargetMyMenusLoadingMore = false;
        this.shareStoreTargetMyMenusNextPageToLoad = 1;
        try {
            const parsed = await this.requestShareStoreUploadedMenusPage(1);
            if (!parsed) {
                runInAction(() => {
                    this.shareStoreTargetMyMenusRaw = [];
                    this.shareStoreTargetMyMenusTotal = 0;
                    this.shareStoreTargetMyMenusNextPageToLoad = 1;
                });
                return false;
            }
            runInAction(() => {
                this.shareStoreTargetMyMenusRaw = parsed.list ?? [];
                this.shareStoreTargetMyMenusTotal = parsed.total ?? 0;
                this.shareStoreTargetMyMenusNextPageToLoad = 2;
            });
            return true;
        } catch (e) {
            console.error(e);
            runInAction(() => {
                this.shareStoreTargetMyMenusRaw = [];
                this.shareStoreTargetMyMenusTotal = 0;
                this.shareStoreTargetMyMenusNextPageToLoad = 1;
            });
            return false;
        }
    }

    /**
     * 分享弹窗：触底追加 uploaded 下一页（`push` 保持 observable 数组引用）。
     */
    async loadShareStoreTargetUploadedMore() {
        if (!this.isShareStoreTargetDialogOpen) {
            return;
        }
        if (this.shareStoreTargetListLoading || this.shareStoreTargetMyMenusLoadingMore) {
            return;
        }
        if (this.shareStoreTargetMyMenusNextPageToLoad < 2) {
            return;
        }
        if (this.shareStoreTargetMyMenusRaw.length >= this.shareStoreTargetMyMenusTotal) {
            return;
        }
        this.shareStoreTargetMyMenusLoadingMore = true;
        try {
            const parsed = await this.requestShareStoreUploadedMenusPage(this.shareStoreTargetMyMenusNextPageToLoad);
            if (!parsed) {
                GlobalNotification.error(i18n.t('menu_store_msg_load_list_failed'));
                return;
            }
            const pageList = parsed.list ?? [];
            runInAction(() => {
                if (pageList.length === 0) {
                    this.shareStoreTargetMyMenusTotal = this.shareStoreTargetMyMenusRaw.length;
                    return;
                }
                const existingIds = new Set(this.shareStoreTargetMyMenusRaw.map((row) => row._id));
                for (const row of pageList) {
                    if (!existingIds.has(row._id)) {
                        this.shareStoreTargetMyMenusRaw.push(row);
                        existingIds.add(row._id);
                    }
                }
                const serverTotal = parsed.total ?? 0;
                if (serverTotal > 0) {
                    this.shareStoreTargetMyMenusTotal = serverTotal;
                }
                this.shareStoreTargetMyMenusNextPageToLoad += 1;
            });
        } finally {
            runInAction(() => {
                this.shareStoreTargetMyMenusLoadingMore = false;
            });
        }
    }

    /**
     * 分享弹窗：打开「同步至菜单商店」目标选择（新建 / 覆盖已上传）
     */
    async shareSyncToMenuStore() {
        if (
            this.shareStoreTargetSubmitting ||
            this.shareMenuDialogMenuStoreOpeningLoading ||
            this.shareMenuDialogSelectedStoresSubmitting
        ) {
            return;
        }
        if (Number(accountState.userInfo?.auth_shell_2) !== 1) {
            GlobalNotification.error(i18n.t('auth_group_no_auth'));
            return;
        }
        if (!this.hasDetailMenuDishes()) {
            GlobalNotification.error(i18n.t('menu_library_share_no_dishes'));
            return;
        }
        runInAction(() => {
            this.shareMenuDialogMenuStoreOpeningLoading = true;
        });
        this.isShareStoreTargetDialogOpen = true;
        this.shareStoreTargetSelection = 'new';
        this.shareStoreTargetListLoading = true;
        this.shareStoreTargetMyMenusRaw = [];
        this.shareStoreTargetMyMenusTotal = 0;
        this.shareStoreTargetMyMenusNextPageToLoad = 1;
        this.shareStoreTargetMyMenusLoadingMore = false;
        try {
            const ok = await this.loadShareStoreTargetUploadedFirstPage();
            if (!ok) {
                GlobalNotification.error(i18n.t('menu_store_msg_load_list_failed'));
                runInAction(() => {
                    this.isShareStoreTargetDialogOpen = false;
                });
            }
            runInAction(() => {
                this.shareStoreTargetListLoading = false;
                this.shareMenuDialogMenuStoreOpeningLoading = false;
            });
        } catch (e) {
            console.error(e);
            runInAction(() => {
                this.shareStoreTargetListLoading = false;
                this.isShareStoreTargetDialogOpen = false;
                this.shareMenuDialogMenuStoreOpeningLoading = false;
            });
            GlobalNotification.error(i18n.t('menu_store_msg_load_list_failed'));
        }
    }

    /**
     * 关闭「商店目标」子弹窗（不关闭外层分享弹窗）
     */
    closeShareStoreTargetDialog() {
        if (this.shareStoreTargetSubmitting) {
            return;
        }
        this.isShareStoreTargetDialogOpen = false;
        this.shareStoreTargetMyMenusRaw = [];
        this.shareStoreTargetMyMenusTotal = 0;
        this.shareStoreTargetMyMenusNextPageToLoad = 1;
        this.shareStoreTargetMyMenusLoadingMore = false;
    }

    /**
     * 设置商店同步目标：新建或某条已上传项的 `_id`
     */
    setShareStoreTargetSelection(value: 'new' | string) {
        this.shareStoreTargetSelection = value;
    }

    /**
     * 提交商店目标选择：本地走 `from-restaurant` POST/PATCH；云端走 `from-cloud-library` POST/PATCH。
     * 成功后关弹窗并打开成功确认。
     */
    async submitShareStoreTargetChoice() {
        if (this.shareStoreTargetSubmitting) {
            return;
        }
        if (Number(accountState.userInfo?.auth_shell_2) !== 1) {
            GlobalNotification.error(i18n.t('auth_group_no_auth'));
            return;
        }
        if (!this.hasDetailMenuDishes()) {
            GlobalNotification.error(i18n.t('menu_library_share_no_dishes'));
            return;
        }
        const fm = this.detailFirstMenu;
        if (!fm) {
            GlobalNotification.error(i18n.t('menu_store_publish_no_menu'));
            return;
        }
        const displayName = getMainGroupNameForDisplay({
            primary: fm.main_group_name ?? '',
            i18nJson: fm.main_group_name_i18n ?? '',
            lang: this.menuDisplayLang,
        }).trim();
        if (!displayName) {
            GlobalNotification.error(i18n.t('menu_library_detail_menu_name_required'));
            return;
        }
        const sel = this.shareStoreTargetSelection;
        if (sel !== 'new') {
            const allowed = new Set(this.getShareStoreUploadedMenuCandidates().map((x) => x._id));
            if (!allowed.has(sel)) {
                GlobalNotification.error(i18n.t('menu_library_share_target_invalid_overwrite'));
                return;
            }
        }
        const isCloudShare = this.detailSource === 'cloud';
        const cloudMenuId = (this.detailCloudMenuId ?? '').trim();
        if (isCloudShare && !cloudMenuId) {
            GlobalNotification.error(i18n.t('menu_library_share_missing_cloud_menu_id'));
            return;
        }
        const mainGroupId = fm.main_group_id;
        if (!isCloudShare && mainGroupId == null) {
            GlobalNotification.error(i18n.t('menu_store_publish_no_menu'));
            return;
        }
        this.shareStoreTargetSubmitting = true;
        setLoading(true);
        try {
            /** 菜单库当前餐厅 `string_id`，作为 `source_restaurant_string_id` 传给商店接口 */
            const rbpId = this.resolveRbpStringIdForApi().trim();
            if (!rbpId) {
                GlobalNotification.error(i18n.t('menu_library_share_missing_from_restaurant'));
                return;
            }
            accountState.getFromLocalStore();
            const u = accountState.userInfo;
            /** 展示用制作人名称 */
            const contactName =
                (u?.nickname && String(u.nickname).trim()) ||
                [u?.first_name, u?.last_name].filter(Boolean).join(' ').trim() ||
                '';
            /** Apifox：`POST .../from-restaurant` 与 `from-cloud-library` 的 `creator_contact` 四项均为 string */
            const phone = String(u?.phone ?? '').trim();
            const phonePrefix = String(u?.phone_prefix ?? '').trim();
            const email = String(u?.email ?? '').trim();
            const creatorForPublish = {
                name: contactName || '-',
                phone,
                phone_prefix: phonePrefix,
                email,
            };
            /** 合并区号后的电话（本地 PATCH `from-restaurant` 与云端 PATCH `from-cloud-library` example 同形） */
            const phoneMerged = [phonePrefix, phone].filter(Boolean).join(' ').trim() || phone;
            /** Apifox：`PATCH .../from-restaurant` 示例为合并电话 + `wechat` */
            const creatorForPatch = {
                name: creatorForPublish.name,
                email,
                phone: phoneMerged,
                wechat: String(_.get(u, 'wechat', '') ?? '').trim(),
            };
            /** OAS example：`PATCH .../from-cloud-library` 的 `creator_contact` 仅 name / email / phone（合并） */
            const creatorForCloudPatch = {
                name: creatorForPublish.name,
                email,
                phone: phoneMerged,
            };
            if (sel === 'new') {
                const res = isCloudShare
                    ? await apiManage.postMenuStoreMenuFromCloudLibrary({
                          name: displayName,
                          menu_uuid: uuidv4(),
                          source_restaurant_string_id: rbpId,
                          source_cloud_menu_ids: [cloudMenuId],
                          description: '',
                          creator_contact: creatorForPublish,
                      })
                    : await apiManage.postMenuStoreMenuFromRestaurant({
                          name: displayName,
                          menu_uuid: uuidv4(),
                          source_restaurant_string_id: rbpId,
                          source_main_group_ids: [mainGroupId as number],
                          creator_contact: creatorForPublish,
                      });
                if (_.get(res, 'error')) {
                    const msg = _.get(res, 'result.message') || _.get(res, 'result.details') || '';
                    GlobalNotification.error(String(msg || i18n.t('menu_store_publish_failed')));
                    return;
                }
                runInAction(() => {
                    this.shareStoreTargetSubmitting = false;
                    this.closeShareStoreTargetDialog();
                    this.closeShareMenuDialog();
                    this.openShareSuccessDialog(this.getShareMenuDialogSubtitle(), [
                        i18n.t('menu_library_share_success_line_store_new'),
                    ]);
                });
            } else {
                const overwritten =
                    this.shareStoreTargetMyMenusRaw.find((x) => x._id === sel) ??
                    this.getShareStoreUploadedMenuCandidates().find((x) => x._id === sel);
                /** 本地覆盖须带商店文档已有 `menu_uuid`；缺省时新生成（以后端校验为准） */
                const existingMenuUuid = String(overwritten?.menu_uuid ?? '').trim() || uuidv4();
                const res = isCloudShare
                    ? await apiManage.patchMenuStoreMenuFromCloudLibrary(sel, {
                          name: displayName,
                          source_restaurant_string_id: rbpId,
                          source_cloud_menu_ids: [cloudMenuId],
                          creator_contact: creatorForCloudPatch,
                      })
                    : await apiManage.patchMenuStoreMenuFromRestaurant(sel, {
                          name: displayName,
                          description: String(overwritten?.description ?? '').trim(),
                          menu_uuid: existingMenuUuid,
                          source_restaurant_string_id: rbpId,
                          source_main_group_ids: [mainGroupId as number],
                          creator_contact: creatorForPatch,
                      });
                if (_.get(res, 'error')) {
                    const msg = _.get(res, 'result.message') || _.get(res, 'result.details') || '';
                    GlobalNotification.error(String(msg || i18n.t('menu_store_publish_failed')));
                    return;
                }
                const prevName = (overwritten?.name ?? '').trim() || '—';
                runInAction(() => {
                    this.shareStoreTargetSubmitting = false;
                    this.closeShareStoreTargetDialog();
                    this.closeShareMenuDialog();
                    this.openShareSuccessDialog(this.getShareMenuDialogSubtitle(), [
                        i18n.t('menu_library_share_success_line_store_update', { name: prevName }),
                    ]);
                });
            }
        } finally {
            runInAction(() => {
                if (this.shareStoreTargetSubmitting) {
                    this.shareStoreTargetSubmitting = false;
                }
            });
            setLoading(false);
        }
    }

    /**
     * 打开分享成功确认弹窗
     * @param menuName - 当前分享的菜单展示名
     * @param lines - 已本地化说明行（顺序与菜单商店立刻使用：`line_synced` → `tunnel_fail_title` + 连续编号 `tunnel_fail_row`，含隧道失败与接口批量失败门店）
     * @param hadSuccessfulSync - 是否至少有门店同步成功（决定成功/失败图标）
     */
    openShareSuccessDialog(menuName: string, lines: string[], hadSuccessfulSync = true) {
        this.shareSuccessDialogMenuName = menuName;
        this.shareSuccessDialogLines = lines.slice();
        this.shareSuccessHadSuccessfulSync = hadSuccessfulSync;
        this.isShareSuccessDialogOpen = true;
    }

    /**
     * 关闭分享成功确认弹窗
     */
    closeShareSuccessDialog() {
        this.isShareSuccessDialogOpen = false;
        this.shareSuccessDialogMenuName = '';
        this.shareSuccessDialogLines = [];
        this.shareSuccessHadSuccessfulSync = true;
    }

    /**
     * 分享弹窗：将当前菜单同步至所选门店。
     * - 本地详情：`POST /v3/menu/share`（`main_group_ids`）；目标不含当前门店
     * - 云端详情：`POST .../menus/import-to-local`（`cloud_menu_id` + `string_ids`）；可含当前门店
     * @param selectedStoreIds - 目标门店 `string_id`
     * @param storeOptions - 归属餐厅列表，用于成功文案展示名称
     */
    async shareSyncToSelectedStores(selectedStoreIds: string[], storeOptions: { value: string; label: string }[]) {
        if (!selectedStoreIds.length) {
            GlobalNotification.error(i18n.t('menu_library_share_stores_none'));
            return;
        }
        if (!this.hasDetailMenuDishes()) {
            GlobalNotification.error(i18n.t('menu_library_share_no_dishes'));
            return;
        }
        const fm = this.detailFirstMenu;
        if (!fm) {
            GlobalNotification.error(i18n.t('menu_store_publish_no_menu'));
            return;
        }
        const isCloudShare = this.detailSource === 'cloud';
        const cloudMenuId = (this.detailCloudMenuId ?? '').trim();
        const mainGroupId = fm.main_group_id;
        if (isCloudShare) {
            if (!cloudMenuId) {
                GlobalNotification.error(i18n.t('menu_library_share_missing_cloud_menu_id'));
                return;
            }
        } else if (mainGroupId == null) {
            GlobalNotification.error(i18n.t('menu_library_share_missing_main_group_id'));
            return;
        }
        const displayName = getMainGroupNameForDisplay({
            primary: fm.main_group_name ?? '',
            i18nJson: fm.main_group_name_i18n ?? '',
            lang: this.menuDisplayLang,
        }).trim();
        if (!displayName) {
            GlobalNotification.error(i18n.t('menu_library_detail_menu_name_required'));
            return;
        }
        const sourceRestaurant = this.menuLibraryRestaurant;
        const fromId = sourceRestaurant?.string_id?.trim();
        if (!fromId) {
            GlobalNotification.error(i18n.t('menu_library_share_missing_from_restaurant'));
            return;
        }
        const groupStringId = sourceRestaurant?.belonged_group?.trim();
        if (!groupStringId) {
            GlobalNotification.error(i18n.t('menu_library_share_missing_group_string_id'));
            return;
        }
        const labelById = new Map(storeOptions.map((o) => [o.value.trim(), (o.label ?? '').trim() || o.value.trim()]));
        /** 门店名称连接符（与成功弹窗历史文案、`menu_store_use_now_result_line_synced` 拼接一致） */
        const nameSep = i18n.t('menu_library_share_success_names_sep');
        /** 去重后的所选目标门店 */
        let targetStoreIds = Array.from(new Set(selectedStoreIds.map((s) => s.trim()).filter(Boolean)));
        /**
         * 本地菜单只能同步到别的门店：提交前剔除源店 string_id（与弹窗列表排除一致，双保险）
         */
        if (!isCloudShare) {
            targetStoreIds = targetStoreIds.filter((id) => id !== fromId);
            if (!targetStoreIds.length) {
                GlobalNotification.error(i18n.t('menu_library_share_stores_none'));
                return;
            }
        }

        runInAction(() => {
            this.shareMenuDialogSelectedStoresSubmitting = true;
        });
        setLoading(true);
        try {
            /** 目标店隧道校验失败或库端版本不达标（跳过该店，继续其余店；与菜单商店「立刻使用」一致） */
            const tunnelFailRows: {
                label: string;
                tunnelRes?: unknown;
                versionBlock?: RestaurantVersionCheckBlockI18nPayload | null;
            }[] = [];
            /** 隧道通过、参与分享/导入接口的门店 */
            const tunnelOkIds: string[] = [];
            for (const storeId of targetStoreIds) {
                const tunnelRes = await apiManage_rbp.checkPermissionByRestaurantStringId(storeId);
                if (_.get(tunnelRes, 'error')) {
                    tunnelFailRows.push({
                        label: labelById.get(storeId) ?? storeId,
                        tunnelRes,
                    });
                } else {
                    const vres = await apiManage.get_restaurant_version_check(storeId);
                    if (_.get(vres, 'error')) {
                        tunnelFailRows.push({
                            label: labelById.get(storeId) ?? storeId,
                            tunnelRes: vres,
                        });
                    } else {
                        /** `meets_minimum === false` 时该店不参与 share，与隧道失败同一结果弹窗样式 */
                        const versionBlock = getRestaurantVersionCheckBlockI18n(vres);
                        if (versionBlock) {
                            tunnelFailRows.push({
                                label: labelById.get(storeId) ?? storeId,
                                versionBlock,
                            });
                        } else {
                            tunnelOkIds.push(storeId);
                        }
                    }
                }
            }

            /** 接口成功后已同步门店展示名 */
            let syncedLabels: string[] = [];
            /** 隧道通过但批量接口失败时共用原因（与隧道失败并入同一编号列表） */
            let apiBatchFailReason: string | null = null;

            if (tunnelOkIds.length > 0) {
                const res = isCloudShare
                    ? await apiManage_rbp.post_cloud_dish_library_menus_import_to_local(
                          {
                              cloud_menu_id: cloudMenuId,
                              string_ids: tunnelOkIds.slice(),
                              group_string_id: groupStringId,
                          },
                          fromId,
                      )
                    : await apiManage.postMenuShare({
                          from_restaurant_string_id: fromId,
                          to_restaurant_string_ids: tunnelOkIds.slice(),
                          group_string_id: groupStringId,
                          main_group_ids: [mainGroupId as number],
                          share_name: displayName,
                      });
                if (_.get(res, 'error')) {
                    const msg = _.get(res, 'result.message') || _.get(res, 'result.details') || '';
                    apiBatchFailReason = String(msg || i18n.t('global_text_operate_failure')).trim();
                    GlobalNotification.error(apiBatchFailReason);
                } else {
                    syncedLabels = tunnelOkIds.map((id) => labelById.get(id) ?? id);
                }
            }

            /** 汇总失败行：先隧道不通，再接口批量失败（同一标题下连续编号，与 shell2-app `menu-library-store`） */
            const syncFailRows: { label: string; reason: string }[] = [];
            tunnelFailRows.forEach((row) => {
                syncFailRows.push({
                    label: row.label,
                    reason: row.versionBlock
                        ? i18n.t(row.versionBlock.key, row.versionBlock.params)
                        : i18n.t(mapErrorToI18nKey(row.tunnelRes)),
                });
            });
            if (apiBatchFailReason && tunnelOkIds.length > 0) {
                const apiReason = apiBatchFailReason;
                tunnelOkIds.forEach((id) => {
                    syncFailRows.push({
                        label: labelById.get(id) ?? id,
                        reason: apiReason,
                    });
                });
            }

            /** 结果正文行：成功行 + 「以下门店同步失败」编号列表 */
            const lines: string[] = [];
            if (syncedLabels.length > 0) {
                lines.push(
                    i18n.t('menu_store_use_now_result_line_synced', {
                        names: syncedLabels.join(nameSep),
                    }),
                );
            }
            if (syncFailRows.length > 0) {
                lines.push(i18n.t('menu_store_use_now_result_tunnel_fail_title'));
                syncFailRows.forEach((row, idx) => {
                    lines.push(
                        i18n.t('menu_store_use_now_result_tunnel_fail_row', {
                            index: idx + 1,
                            name: row.label,
                            reason: row.reason,
                        }),
                    );
                });
            }

            const hadSuccessfulSync = syncedLabels.length > 0;

            runInAction(() => {
                this.shareMenuDialogSelectedStoresSubmitting = false;
                this.closeShareMenuDialog();
                this.openShareSuccessDialog(this.getShareMenuDialogSubtitle(), lines, hadSuccessfulSync);
            });
        } finally {
            setLoading(false);
            runInAction(() => {
                if (this.shareMenuDialogSelectedStoresSubmitting) {
                    this.shareMenuDialogSelectedStoresSubmitting = false;
                }
            });
        }
    }

    /**
     * 打印组名称（`print_class === -1` 为「无」；其它 id 查 `printGroupMap`）。
     * 映射中无对应名称时返回空串（隧道不通未拉到岗位组列表时不回退显示数字 id）。
     */
    getPrintClassLabel(printClassId?: number | null): string {
        if (printClassId === undefined || printClassId === null) {
            return '—';
        }
        if (printClassId === -1) {
            return this.printGroupMap[-1] ?? i18n.t('print_setting_page_none0');
        }
        const name = this.printGroupMap[printClassId];
        if (name != null && name !== '') {
            return name;
        }
        return '';
    }

    /**
     * 菜品类型文案
     */
    getDishTypeLabel(itemType?: number): string {
        const found = [
            { value: 0, label: i18n.t('menu_and_dishes_page_normal_dishes') },
            { value: 1, label: i18n.t('settings_page_text_condiment') },
            { value: 2, label: i18n.t('menu_and_dishes_page_dish_with_current_price') },
            { value: 3, label: i18n.t('menu_and_dishes_page_set_meal') },
            { value: 4, label: i18n.t('menu_and_dishes_page_custom') },
            { value: 5, label: i18n.t('menu_and_dishes_page_text') },
            { value: 6, label: i18n.t('menu_and_dishes_page_weight_dish') },
            { value: 7, label: i18n.t('menu_and_dishes_page_timed_dish') },
        ].find((x) => x.value === itemType);
        return found?.label || '—';
    }

    /**
     * 创建或更新一级菜单：首次为 `post_create_first_menu` 并写入子类别草稿；之后同一会话内为 PATCH（不关闭弹窗）
     * @param options.notifySuccess 是否弹出成功提示（「新增子类别」链式调用时可关闭以避免重复 Toast）
     */
    async submitCreateFirstMenu(options?: { notifySuccess?: boolean }) {
        const notifySuccess = options?.notifySuccess !== false;
        const name = this.newMenuPrimaryName.trim();
        if (!name) {
            GlobalNotification.error(i18n.t('menu_library_detail_menu_name_required'));
            return;
        }
        const i18nNameJson = normalizeMainGroupNameI18nJson(this.createDialogMainGroupNameI18nJson);
        if (this.isCloudCreateDialog) {
            if (this.createDialogPersistedCloudMenuId) {
                await this.patchCreateMenuDialogFirstMenu({ notifySuccess });
                return;
            }
            this.isSubmittingCreate = true;
            setLoading(true);
            try {
                const cloudMenuIndex = this.cloudMenuList.length || 0;
                const result = await cloudDishLibraryApi.post_cloud_dish_library_menus_create(
                    {
                        main_group_name: name,
                        main_group_name_i18n: i18nNameJson,
                        main_group_index: cloudMenuIndex,
                        exact_search_rules: 0,
                        max_limit: this.parseCreateDialogMaxLimitOptional() ?? 9999,
                        set_item_main: this.createDialogSetItemMain,
                        /** 菜单级轮次限制；与弹窗「不受轮次限制」开关对应 */
                        not_joining_global_limit: this.createMenuRoundLimitUnrestricted ? 1 : 0,
                        menu_item_slu: [],
                    },
                    this.resolveRbpStringIdForApi(),
                );
                if (_.get(result, 'error')) {
                    GlobalNotification.error(i18n.t('global_text_operate_failure'));
                    return;
                }
                // 联调真实体顶层 `id` 即为云端菜单主键
                const cloudMenuId = typeof result?.id === 'string' ? result.id.trim() : '';
                if (!cloudMenuId) {
                    throw new Error('cloud menu id not found');
                }
                runInAction(() => {
                    this.createDialogPersistedCloudMenuId = cloudMenuId;
                });
                const existingDrafts = [...this.createMenuSubcategoryDrafts];
                /** 各草稿对应的新建小类 cloud_slu_id（与 existingDrafts 中非空名行顺序一致） */
                const addedCloudSluIds: string[] = [];
                for (const draft of existingDrafts) {
                    if (!draft.name.trim()) {
                        continue;
                    }
                    const subcategoryResult = await cloudDishLibraryApi.post_cloud_dish_library_menu_update(
                        cloudMenuId,
                        {
                            menu_item_slu_add: [{
                                dmi_slu_name: draft.name.trim(),
                                dmi_slu_name_i18n: normalizeMainGroupNameI18nJson(draft.nameI18nJson),
                                ...(this.parseSubcategoryOrderLimitToNumberOrNull(draft.perPersonLimit) != null
                                    ? { category_order_limit: this.parseSubcategoryOrderLimitToNumberOrNull(draft.perPersonLimit)! }
                                    : {}),
                                ...(this.parseSubcategoryOrderLimitToNumberOrNull(draft.perTableLimit) != null
                                    ? { table_category_order_limit: this.parseSubcategoryOrderLimitToNumberOrNull(draft.perTableLimit)! }
                                    : {}),
                            }],
                        },
                        this.resolveRbpStringIdForApi(),
                    );
                    if (_.get(subcategoryResult, 'error')) {
                        throw new Error('cloud subcategory create failed');
                    }
                    const addedSluId = pickFirstAddedCloudSluId(subcategoryResult);
                    if (!addedSluId) {
                        throw new Error('cloud subcategory id not found');
                    }
                    addedCloudSluIds.push(addedSluId);
                }
                runInAction(() => {
                    let savedIndex = 0;
                    this.createMenuSubcategoryDrafts = existingDrafts.map((draft) => {
                        if (!draft.name.trim()) {
                            return draft;
                        }
                        const cloud_slu_id = addedCloudSluIds[savedIndex++];
                        return {
                            ...draft,
                            cloud_slu_id,
                        };
                    });
                    this.createDialogNextSecondIndex = savedIndex;
                    this.createDialogMaxLimitDraft = String(this.parseCreateDialogMaxLimitOptional() ?? 9999);
                });
                const menuImage = await this.resolveCreateMenuDialogMenuImageForSave(
                    this.resolveRbpStringIdForApi(),
                    cloudMenuId,
                    this.createDialogMenuImageRef.trim(),
                );
                runInAction(() => {
                    this.resetCreateDialogMenuImageStaging(menuImage);
                });
                if (notifySuccess) {
                    GlobalNotification.success(i18n.t('global_text_operate_success'));
                }
                await this.refreshCloudMenusAfterMutation();
            } catch (error) {
                console.error('submitCreateFirstMenu cloud failed:', error);
                GlobalNotification.error(i18n.t('global_text_operate_failure'));
            } finally {
                setLoading(false);
                runInAction(() => {
                    this.isSubmittingCreate = false;
                });
            }
            return;
        }
        if (this.createDialogPersistedMainGroupId != null) {
            await this.patchCreateMenuDialogFirstMenu({ notifySuccess });
            return;
        }
        this.isSubmittingCreate = true;
        setLoading(true);
        try {
            let nextIndex = 0;
            if (this.menuLibraryList.length > 0) {
                const indices = this.menuLibraryList
                    .map((it) => it.main_group_index)
                    .filter((n): n is number => n !== undefined && n !== null);
                nextIndex = indices.length > 0 ? Math.max(...indices) + 1 : this.menuLibraryList.length;
            }
            runInAction(() => {
                this.persistCreateExtrasIntoWeekKey(this.createMenuSelectedWeekKey);
            });
            const w = this.resolveCreateMenuWeekNumber(this.createMenuSelectedWeekKey);
            const row = this.createMenuPriceValueRows.find((r) => r.week === w);
            const newScheme = this.createDialogSetItemMain === '1';
            const maxLimitOpt = this.parseCreateDialogMaxLimitOptional();
            const newFirstMenu: FirstMenu = {
                main_group_name: name,
                main_group_name_i18n: i18nNameJson,
                menu_item_slu: [],
                main_group_index: nextIndex,
                set_item_main: newScheme ? '1' : '0',
                ...(maxLimitOpt !== undefined ? { max_limit: maxLimitOpt } : {}),
                ...(newScheme
                    ? { value: _.cloneDeep(this.createMenuPriceValueRows) }
                    : {
                        extra_normal: row?.extra_normal ?? '',
                        extra_child: row?.extra_child ?? '',
                        extra_other: row?.extra_other ?? '',
                        normal_price: row?.normal_price ?? null,
                        child_price: row?.child_price ?? null,
                        other_price: row?.other_price ?? null,
                    }),
            };
            const result = await apiManage_rbp.post_create_first_menu(newFirstMenu, this.resolveRbpStringIdForApi());
            if (_.get(result, 'error')) {
                GlobalNotification.error(i18n.t('global_text_operate_failure'));
                return;
            }
            const mainGroupId = result?.insertId as number | undefined;
            if (!mainGroupId) {
                GlobalNotification.error(i18n.t('global_text_operate_failure'));
                return;
            }
            const nextDrafts: CreateMenuSubcategoryDraft[] = [];
            let secondIndex = 0;
            for (const d of this.createMenuSubcategoryDrafts) {
                const trimmedPrimary = d.name.trim();
                if (!trimmedPrimary) {
                    nextDrafts.push(d);
                    continue;
                }
                const second: SecondMenu = {
                    dmi_slu_name: trimmedPrimary,
                    dmi_slu_name_i18n: normalizeMainGroupNameI18nJson(d.nameI18nJson),
                    dmi_slu_index: secondIndex,
                };
                const r2 = await apiManage_rbp.post_create_second_menu(
                    mainGroupId,
                    second,
                    this.resolveRbpStringIdForApi(),
                );
                if (_.get(r2, 'error')) {
                    GlobalNotification.error(i18n.t('global_text_operate_failure'));
                    return;
                }
                const newSluId = r2?.insertId as number | undefined;
                nextDrafts.push({
                    ...d,
                    dmi_slu_id: newSluId,
                });
                secondIndex += 1;
            }
            runInAction(() => {
                this.createDialogPersistedMainGroupId = mainGroupId;
                this.createDialogNextSecondIndex = secondIndex;
                this.createMenuSubcategoryDrafts = nextDrafts;
                this.createDialogMaxLimitDraft =
                    maxLimitOpt !== undefined ? String(maxLimitOpt) : '';
            });
            const rbpStr = this.resolveRbpStringIdForApi();
            try {
                const menuImgResolved = await this.resolveCreateMenuDialogMenuImageForSave(
                    rbpStr,
                    mainGroupId,
                    this.createDialogMenuImageRef.trim(),
                );
                runInAction(() => {
                    this.resetCreateDialogMenuImageStaging(menuImgResolved);
                });
            } catch (e) {
                GlobalNotification.error(
                    e instanceof Error ? e.message : i18n.t('global_text_operate_failure'),
                );
            }
            if (notifySuccess) {
                GlobalNotification.success(i18n.t('global_text_operate_success'));
            }
            await this.refreshLocalMenusAfterMutation();
        } catch (error) {
            console.error('submitCreateFirstMenu failed:', error);
            GlobalNotification.error(i18n.t('global_text_operate_failure'));
        } finally {
            setLoading(false);
            runInAction(() => {
                this.isSubmittingCreate = false;
            });
        }
    }
}
